import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../shared/ss-select-tooltip.directive';
import { SsSelectClearDirective } from '../shared/ss-select-clear.directive';
import { firstValueFrom } from 'rxjs';

import {
  SelfServiceDatasetService,
  WorkbookRun,
  WorkbookRunOutcome,
  WorkbookSheetOutcome,
  WorkbookUploadResponse,
} from '../shared/self-service-dataset.service';
import {
  ComputationLibraryEntry,
  SelfServiceComputationLibraryService,
} from '../shared/self-service-computation-library.service';
import { SelfServiceFormulaService } from '../shared/self-service-formula.service';
import {
  SaveEstimate,
  SaveEstimateConfig,
  estimateSave,
  formatElapsed,
  formatEstimateRange,
  formatRowCount,
} from '../shared/self-service-save-estimate.utils';
import { SelfServiceStateService } from '../shared/self-service-state.service';
import { SelfServiceUiService } from '../shared/self-service-ui.service';
import { SelfServiceChartPersistenceService } from '../shared/self-service-chart-persistence.service';
import { SelfServiceConfigService } from '../shared/self-service-config.service';
import { SelfServiceApiConfigService } from '../shared/self-service-api-config.service';
import { RuleBuilderComponent } from '../rule-builder/rule-builder.component';
import {
  BuilderLayoutConfig,
  BuilderStep,
} from '../shared/self-service-config.types';

import {
  AggregationType,
  ColumnType,
  ComputedColumn,
  CustomAggregation,
  DatasetColumn,
  DatasetFormulas,
  EntityFilterDefinition,
  EntityFormulaDefinition,
  FormulaTemplate,
  LibraryComputation,
  SupportedFileType,
  UploadedDataset,
} from '../shared/self-service-types';

/** Absolute position in the data grid: row index into dataset.rows, col index into dataset.columns. */
interface GridCellPos {
  row: number;
  col: number;
}

/** One cell's before/after value — the atomic unit batched into an undo/redo stack entry. */
interface CellChange {
  row: number;
  column: string;
  oldValue: string;
  newValue: string;
}

interface PipelineSavedDataset {
  id: string;
  name: string;
  sourceFileName: string;
  columns: string[];
  rows: Array<Record<string, any>>;
  baseColumns: string[];
  baseRows: Array<Record<string, any>>;
  file: File;
}

interface PipelineTransformerConfig {
  mapToColumn: string;
  outputEntity: string;
  selectedColumns: string[];
  lookupEntities: string[];
  lookupKeys: string[];
  lookupResultField: string;
  ruleType: string;
  delimiter: string;
  rangeStart: string;
  rangeEnd: string;
  rangeMin: string;
  rangeMax: string;
  defaultValue: string;
  decimalPrecision: string;
  regex: string;
  replacement: string;
  arrayDelimiter: string;
  literalValues: string;
  jsonAliases: string;
  outputMode: 'overwrite' | 'create';
  newColumnName: string;
}

interface PipelineTransformerRuleDefinition {
  id: string;
  label: string;
  icon: string;
  description: string;
  supportsMultipleColumns: boolean;
}

interface PipelineWorkflowTargetMetadataUpdate {
  workflows: Record<string, unknown>;
  workflowId: string;
}

type RelationshipJoinRuleType = 'left_join' | 'right_join' | 'inner_join' | 'outer_join';

interface PipelineAppliedStep {
  id: string;
  type: 'transformation' | 'relationship' | 'validation';
  label: string;
  summary: string;
  appliedAt: Date;
  datasetIds: string[];
  config?: PipelineTransformerConfig;
  active: boolean;
  supersedesStepId?: string;
}

/**
 * Data-builder stage extracted from SelfServiceVisualizationComponent.
 *
 * Owns the data-ingest / computation working state (uploaded file, selected
 * entity, entity formulas/filters, computed columns, row filter, computation
 * library). Cross-stage state — dataset, raw dataset, custom aggregations,
 * saved charts — is read from and written to SelfServiceStateService. Pure
 * formula/evaluation helpers come from SelfServiceFormulaService.
 *
 * Chart auto-defaults (mapping, series field, chart name, preview) are NOT done
 * here: ChartBuilderComponent's effect() seeds those reactively when
 * state.dataset() changes. Shell-level actions (view switching, chat, starting
 * a new chart) are raised as output() events for the shell to handle.
 */
@Component({
  selector: 'app-data-builder',
  standalone: true,
  // Default change detection (not OnPush): this component does its dataset work
  // in async FileReader/HTTP callbacks that resolve outside Angular's event flow,
  // and reads shared signal state via getters — OnPush would leave the data
  // preview stale until the next user interaction. Matches the original monolith.
  imports: [
    CommonModule,
    FormsModule,
    MatStepperModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    NgSelectModule,
    SsSelectTooltipDirective,
    SsSelectClearDirective,
    RuleBuilderComponent,
  ],
  templateUrl: './data-builder.component.html',
  styleUrl: './data-builder.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class DataBuilderComponent implements OnInit, OnDestroy {
  private static readonly HIDDEN_ENTITY_COLUMNS = [
    'uuid',
    'source_row_hash',
    'sourced_row_hash',
    '_spriced_row_hash',
    'created_at',
    'updated_date',
    'created_date',
    'updated_by',
    'created_by',
  ];

  /**
   * Hidden field embedded on every projected entity row, carrying the DB key
   * value needed to persist edits/computations. Stored ON the row itself
   * (not a parallel positional array) so it survives cloning and
   * re-projection (e.g. computeEntityPreview rebuilding the dataset from an
   * already-projected clone) without any risk of index misalignment.
   */
  private static readonly ROW_KEY_FIELD = '__rowKey';

  private static readonly DATASET_FORMULAS_STORAGE_KEY = 'dvm.selfService.datasetFormulas';
  private static readonly COMPUTATION_LIBRARY_STORAGE_KEY = 'dvm.selfService.computationLibrary';

  @ViewChild('dataStepper') dataStepper?: MatStepper;
  @ViewChild('entityFormulaEditor', { static: false })
  entityFormulaEditor?: ElementRef<HTMLTextAreaElement>;

  // ── Shared services ──────────────────────────────────────────────────────
  protected readonly state = inject(SelfServiceStateService);
  private readonly ui = inject(SelfServiceUiService);
  private readonly datasetService = inject(SelfServiceDatasetService);
  private readonly computationLibraryService = inject(SelfServiceComputationLibraryService);
  private readonly formulaService = inject(SelfServiceFormulaService);
  private readonly persistence = inject(SelfServiceChartPersistenceService);
  private readonly configService = inject(SelfServiceConfigService);
  private readonly apiConfig = inject(SelfServiceApiConfigService);

  // ── JSON-driven layout (see JSON_CONFIG_PLAN.md) ─────────────────────────
  /** Registry of step-body template ids this template exposes. */
  static readonly STEP_FIELD_IDS: ReadonlySet<string> = new Set<string>([
    'upload',
    'computations',
  ]);

  /** Built-in default layout — identical to the original 2-step stepper. Used
   *  when no `data-builder.json` is present so the builder renders unchanged. */
  private static readonly DEFAULT_LAYOUT: BuilderLayoutConfig = {
    version: '1.0',
    builder: 'data',
    stepperOrientation: 'horizontal',
    linear: false,
    steps: [
      { id: 'upload', label: 'Upload data', sections: [{ id: 'upload', fields: [{ field: 'upload' }] }] },
      { id: 'computations', label: 'Computations', sections: [{ id: 'computations', fields: [{ field: 'computations' }] }] },
    ],
  };

  /** Active layout (default until the JSON config resolves in ngOnInit). */
  layout: BuilderLayoutConfig = DataBuilderComponent.DEFAULT_LAYOUT;
  /** True until loadBuilderLayout's HTTP call settles. `layout` starts as
   *  DEFAULT_LAYOUT (no `pipelineDashboard` block), so `pipelineDashboard`
   *  is null and the template would otherwise render the LEGACY builder
   *  first, then swap to the real pipeline UI once data-builder.json
   *  arrives — a visible flash of the old design on every load. Gating the
   *  template on this flag instead shows a lightweight loading state for
   *  that one round-trip. */
  layoutLoading = true;
  pipelineCanvasNodes: any[] = [];
  draggedPipelineNodeId = '';
  private pipelineNodeDragOffset = { x: 0, y: 0 };
  private suppressNextPipelinePortClick = false;
  dragOverPipelineNodeId = '';
  pipelineCanvasDragActive = false;
  pipelineConnections: Array<{
    from: string;
    to: string;
    fromSide?: 'in' | 'out';
    toSide?: 'in' | 'out';
  }> = [];
  pendingPipelineConnection: { nodeId: string; side: 'in' | 'out' } | null = null;
  pipelineConnectionHoverTarget: { nodeId: string; side: 'in' | 'out' } | null = null;
  pipelineCanvasScale = 1;
  pipelineCanvasBounds = { width: 960, height: 360 };
  activePipelineConnectionDrag: {
    nodeId: string;
    side: 'in' | 'out';
    startPointer: { x: number; y: number };
    pointer: { x: number; y: number };
    hasMoved: boolean;
  } | null = null;
  pipelineCanvasMaximized = false;
  selectedPipelineSourceName = '';
  selectedPipelineNodeId = '';
  pipelinePendingStepNode: any | null = null;
  openPipelineSourceMenuId = '';
  private pipelineSourceDeleteInProgress = new Set<string>();
  openPipelineNodeMenuId = '';
  private readonly pipelineSourceDeleteDebugKey = 'KLITE_SOURCE_DELETE_20260727_V1';
  private hiddenPipelineSourceNames = new Set<string>();
  transformerExpanded = true;
  transformerFileMode: 'single' | 'multiple' = 'multiple';
  transformerColumnMode: 'single' | 'multiple' = 'multiple';
  readonly transformerCreateOutputAttributeValue = '__create_output_attribute__';
  transformerSelectedFiles: string[] = [];
  transformerOutputEntity = '';
  transformerCreatingOutputAttribute = false;
  transformerMapToColumn = '';
  transformerSelectedColumns: string[] = [];
  transformerRuleSearch = '';
  transformerRuleType = 'trim';
  transformerDelimiter = ' ';
  transformerRangeStart = '0';
  transformerRangeEnd = '3';
  transformerRangeMin = '1';
  transformerRangeMax = '10';
  transformerDefaultValue = 'N/A';
  transformerDecimalPrecision = '2';
  transformerRegex = '[@.#$]';
  transformerReplacement = '';
  transformerArrayDelimiter = ',';
  transformerLiteralValues = '';
  transformerJsonAliases = '';
  transformerLookupEntity = '';
  transformerLookupEntitySelections: string[] = [];
  transformerLookupEntities: string[] = [];
  transformerLookupResultFields: string[] = [];
  transformerLookupEntitiesLoading = false;
  transformerLookupEntitiesError = '';
  private transformerLookupEntitiesLoaded = false;
  private transformerLookupAttributesByEntity = new Map<string, string[]>();
  transformerLookupKeys: string[] = [];
  transformerLookupResultField = '';
  transformerOutputMode: 'overwrite' | 'create' = 'overwrite';
  transformerOutputColumn = 'same';
  transformerNewColumnName = '';
  transformerApplyMessage = '';
  activeMetadataOperationStep: 'transformation' | 'relationship' | 'validation' = 'transformation';
  private metadataOperationRuleByStep: Record<'transformation' | 'relationship' | 'validation', string> = {
    transformation: 'trim',
    relationship: 'lookup',
    validation: 'sum',
  };
  readonly validationRuleTypes = [
    { id: 'sum', label: 'Sum', icon: 'add' },
    { id: 'difference', label: 'Difference', icon: 'remove' },
    { id: 'multiply', label: 'Multiply', icon: 'close' },
  ];
  readonly relationshipOperationTypes = [
    { id: 'lookup', label: 'Lookup', icon: 'manage_search' },
    { id: 'left_join', label: 'Left Join', icon: 'join_left' },
    { id: 'right_join', label: 'Right Join', icon: 'join_right' },
    { id: 'inner_join', label: 'Inner Join', icon: 'join_inner' },
    { id: 'outer_join', label: 'Outer Join', icon: 'join_full' },
  ];
  private readonly relationshipJoinTypeByRuleType: Record<RelationshipJoinRuleType, string> = {
    left_join: 'left join',
    right_join: 'right join',
    inner_join: 'inner join',
    outer_join: 'outer join',
  };
  readonly transformerRuleTypes: PipelineTransformerRuleDefinition[] = [
    { id: 'concatenate', label: 'Concatenate', icon: 'merge_type', description: 'Combine two or more selected columns into one output value.', supportsMultipleColumns: true },
    { id: 'uppercase', label: 'Uppercase', icon: 'text_fields', description: 'Convert every character in the selected column to uppercase.', supportsMultipleColumns: false },
    { id: 'substring', label: 'Substring', icon: 'content_cut', description: 'Extract part of the selected value using start and end indexes.', supportsMultipleColumns: false },
    { id: 'validate_range', label: 'Validate Range', icon: 'rule', description: 'Keep numeric values inside a configured range and replace invalid values.', supportsMultipleColumns: false },
    { id: 'convert_to_json', label: 'Convert to JSON', icon: 'data_object', description: 'Create a JSON value from one or more selected columns.', supportsMultipleColumns: true },
    { id: 'sum', label: 'Sum', icon: 'add', description: 'Add selected numeric columns and optional fixed values.', supportsMultipleColumns: true },
    { id: 'difference', label: 'Difference', icon: 'remove', description: 'Subtract selected numeric columns and optional fixed values in order.', supportsMultipleColumns: true },
    { id: 'multiply', label: 'Multiply', icon: 'close', description: 'Multiply selected numeric columns and optional fixed values.', supportsMultipleColumns: true },
    { id: 'auto_generate', label: 'Auto Generate', icon: 'fingerprint', description: 'Generate a stable identifier from the selected source values.', supportsMultipleColumns: true },
    { id: 'trim', label: 'Trim', icon: 'format_clear', description: 'Remove surrounding whitespace and wrapping quotation marks.', supportsMultipleColumns: false },
    { id: 'regex_remove', label: 'Regex Remove', icon: 'find_replace', description: 'Remove or replace text matched by a regular expression.', supportsMultipleColumns: false },
    { id: 'to_array', label: 'To Array', icon: 'data_array', description: 'Split the selected text value into an array using a delimiter.', supportsMultipleColumns: false },
  ];
  private transformerAvailableColumnsCacheKey: string | null = null;
  private transformerAvailableColumnsCache: string[] = [];
  private transformerRelationshipSourceOptionsCacheKey: string | null = null;
  private transformerRelationshipSourceOptionsCache: string[] = [];
  private transformerOutputAttributeOptionsCacheKey: string | null = null;
  private transformerOutputAttributeOptionsCache: string[] = [];
  private transformerOutputEntityOptionsCacheKey: string | null = null;
  private transformerOutputEntityOptionsCache: string[] = [];
  private transformerOperationPreviewCacheKey = '';
  private transformerOperationPreviewCache: Record<string, any> | null = null;
  pipelinePreviewColumns: string[] = [];
  pipelinePreviewRows: Array<Record<string, any>> = [];
  private pipelineWorkingRows: Array<Record<string, any>> = [];
  pipelinePreviewTotalRows = 0;
  pipelinePreviewTitle = 'Translation preview';
  pipelineSlateClearing = false;
  pipelineTranslationPreviewOpen = false;
  pipelineTranslationPreviewError = '';
  pipelineTranslationFiles: File[] = [];
  pipelineSavedDatasets: PipelineSavedDataset[] = [];
  selectedPipelineDatasetIds: string[] = [];
  pipelineAppliedSteps: PipelineAppliedStep[] = [];
  activePipelineDetailsTab: 'node-details' | 'applied-steps' = 'node-details';
  editingPipelineStepId = '';
  private readonly pipelineStepOrder = ['translation', 'transformation', 'relationship', 'validation'];
  private pipelineCompletedSteps = new Set<string>();
  pipelinePanelSizes = {
    left: 336,
    right: 400,
    preview: 360,
  };
  private pipelinePanelResizeStart: {
    panel: 'left' | 'right' | 'preview';
    pointerX: number;
    pointerY: number;
    startSize: number;
  } | null = null;

  get pipelineDashboard(): any | null {
    return (this.layout as any)?.pipelineDashboard ?? null;
  }

  get selectedPipelineNode(): any | null {
    return this.pipelineCanvasNodes.find((node: any) => node?.id === this.selectedPipelineNodeId) ?? null;
  }

  get canRunPipelineWorkflow(): boolean {
    return !!this.datasetService.getLatestWorkflowId();
  }

  // ── Save-duration estimate ────────────────────────────────────────────────

  /** Same row source savePipelineDataset uses, so the estimate can't drift. */
  get pipelineSaveRowCount(): number {
    return this.pipelineWorkingRows.length || this.rawDataset?.rows?.length || 0;
  }

  private get saveEstimateConfig(): SaveEstimateConfig | undefined {
    return this.layout?.saveEstimate;
  }

  get saveEstimate(): SaveEstimate {
    return estimateSave(this.pipelineSaveRowCount, this.saveEstimateConfig);
  }

  get saveEstimateHint(): string {
    const estimate = this.saveEstimate;
    if (!estimate.worthShowing) return '';
    return `${formatRowCount(estimate.rows)} rows — saving usually takes ${formatEstimateRange(estimate)}.`;
  }

  savingProgressText = '';
  savingProgressPercent = 0;

  /** Set while a picked file is being read/uploaded/parsed. The dataset is
   *  cleared up front, so without this the panel just goes blank. */
  fileUploadLoading = false;
  fileUploadStatusText = '';

  workbookFile: File | null = null;
  workbookEntityPrefix = '';
  workbookAutoRun = false;
  workbookFromPipeline = false;
  workbookBusy = false;
  workbookStatusText = '';
  workbookError = '';
  workbookReport: WorkbookUploadResponse | null = null;
  workbookIngestion: WorkbookRunOutcome | null = null;

  /** Set while a translation preview is being applied to the rows. */
  translationApplyingText = '';

  /** Whatever is currently keeping the preview table empty, or '' if idle. */
  get pipelinePreviewBusyText(): string {
    return this.savingProgressText || this.fileUploadStatusText || this.translationApplyingText;
  }

  private setFileUploadStatus(text: string): void {
    this.fileUploadLoading = true;
    this.fileUploadStatusText = text;
  }

  private clearFileUploadStatus(): void {
    this.fileUploadLoading = false;
    this.fileUploadStatusText = '';
  }
  private saveTicker: ReturnType<typeof setInterval> | null = null;
  private saveStartedAt = 0;

  /** The backend's own confirmation deserves longer than the 4s default. */
  private static readonly WORKFLOW_SNACK_MS = 10000;

  private saveProgressVerb = 'Saving';

  setSaveProgressVerb(verb: string): void {
    this.saveProgressVerb = verb;
  }

  /** Button label while busy: "Preparing…" then "Saving…". */
  get saveButtonBusyLabel(): string {
    return `${this.saveProgressVerb}…`;
  }

  private beginSaveProgress(verb = 'Saving'): void {
    this.stopSaveProgress();
    const estimate = this.saveEstimate;
    this.saveProgressVerb = verb;
    this.saveStartedAt = Date.now();
    const tick = () => {
      const elapsedSeconds = (Date.now() - this.saveStartedAt) / 1000;
      const range = estimate.worthShowing ? ` of about ${formatEstimateRange(estimate)}` : '';
      this.savingProgressText = `${this.saveProgressVerb} ${formatRowCount(estimate.rows)} rows — ${formatElapsed(elapsedSeconds)} elapsed${range}.`;
      // Capped at 99 — only the response ends this, and a stuck 100% reads as a hang.
      this.savingProgressPercent = estimate.expectedSeconds > 0
        ? Math.min(99, Math.round((elapsedSeconds / estimate.expectedSeconds) * 100))
        : 0;
    };
    tick();
    this.saveTicker = setInterval(tick, 1000);
  }

  private stopSaveProgress(): void {
    if (this.saveTicker) clearInterval(this.saveTicker);
    this.saveTicker = null;
    this.savingProgressText = '';
    this.savingProgressPercent = 0;
    this.saveProgressVerb = 'Saving';
  }

  ngOnDestroy(): void {
    this.stopSaveProgress();
  }

  get canSavePipelineDataset(): boolean {
    const hasRows = this.pipelineWorkingRows.length || this.rawDataset?.rows?.length;
    const hasColumns = this.pipelinePreviewColumns.length || this.rawDataset?.columns?.length;
    return !!hasRows && !!hasColumns && !this.pipelineSlateClearing;
  }

  get pipelineWorkflowDeamResponse(): unknown | null {
    return this.datasetService.getLatestWorkflowDeamResponse();
  }

  get pipelineDetailsNode(): any | null {
    return this.selectedPipelineNode ?? this.pipelinePendingStepNode;
  }

  get transformerAvailableFiles(): string[] {
    return this.pipelineSavedDatasets.map((dataset) => dataset.name);
  }

  get transformerRelationshipSourceOptions(): string[] {
    const source = this.isRelationshipJoinOperation || !this.transformerLookupEntities.length
      ? this.transformerAvailableFiles
      : this.transformerLookupEntities;
    const cacheKey = source.join('\u0000');
    if (cacheKey === this.transformerRelationshipSourceOptionsCacheKey) {
      return this.transformerRelationshipSourceOptionsCache;
    }
    this.transformerRelationshipSourceOptionsCacheKey = cacheKey;
    this.transformerRelationshipSourceOptionsCache = [...source];
    return this.transformerRelationshipSourceOptionsCache;
  }

  get transformerOutputAttributeOptions(): string[] {
    if (this.isRelationshipJoinOperation && this.transformerSelectedColumns.length) {
      const cacheKey = `selected:${this.transformerSelectedColumns.join('\u0000')}`;
      if (cacheKey === this.transformerOutputAttributeOptionsCacheKey) {
        return this.transformerOutputAttributeOptionsCache;
      }
      this.transformerOutputAttributeOptionsCacheKey = cacheKey;
      this.transformerOutputAttributeOptionsCache = Array.from(new Set(this.transformerSelectedColumns));
      return this.transformerOutputAttributeOptionsCache;
    }

    const sourceEntities = this.transformerOutputEntity
      ? [this.transformerOutputEntity]
      : this.transformerSelectedFiles;
    const cacheKey = sourceEntities.map((entity) => `${entity}:${(this.transformerLookupAttributesByEntity.get(entity) ?? []).join('|')}`).join('\u0000');
    if (cacheKey === this.transformerOutputAttributeOptionsCacheKey) {
      return this.transformerOutputAttributeOptionsCache;
    }
    const attributes = sourceEntities.flatMap((entity) =>
      this.transformerLookupAttributesByEntity.get(entity) ?? [],
    );
    this.transformerOutputAttributeOptionsCacheKey = cacheKey;
    this.transformerOutputAttributeOptionsCache = Array.from(new Set(attributes)).sort((left, right) => left.localeCompare(right));
    return this.transformerOutputAttributeOptionsCache;
  }

  get transformerOutputAttributeSelectValue(): string {
    return this.transformerCreatingOutputAttribute
      ? this.transformerCreateOutputAttributeValue
      : this.transformerMapToColumn;
  }

  get transformerOutputEntityOptions(): string[] {
    if (this.isRelationshipJoinOperation) {
      const cacheKey = `join:${this.transformerSelectedFiles.join('\u0000')}`;
      if (cacheKey === this.transformerOutputEntityOptionsCacheKey) {
        return this.transformerOutputEntityOptionsCache;
      }
      this.transformerOutputEntityOptionsCacheKey = cacheKey;
      this.transformerOutputEntityOptionsCache = [...this.transformerSelectedFiles];
      return this.transformerOutputEntityOptionsCache;
    }

    const cacheKey = this.transformerSelectedFiles
      .map((entity) => `${entity}:${this.transformerLookupAttributesByEntity.has(entity) ? '1' : '0'}`)
      .join('\u0000');
    if (cacheKey === this.transformerOutputEntityOptionsCacheKey) {
      return this.transformerOutputEntityOptionsCache;
    }
    this.transformerOutputEntityOptionsCacheKey = cacheKey;
    this.transformerOutputEntityOptionsCache = this.transformerSelectedFiles.filter((entity) =>
      this.transformerLookupAttributesByEntity.has(entity),
    );
    return this.transformerOutputEntityOptionsCache;
  }

  get visiblePipelineAppliedSteps(): PipelineAppliedStep[] {
    const selectedIds = new Set(
      this.selectedPipelineDatasetIds.length
        ? this.selectedPipelineDatasetIds
        : this.pipelineSavedDatasets
            .filter((dataset) => dataset.sourceFileName === this.selectedPipelineSourceName)
            .map((dataset) => dataset.id),
    );
    return this.pipelineAppliedSteps
      .filter((step) => !selectedIds.size || step.datasetIds.some((id) => selectedIds.has(id)))
      .slice()
      .reverse();
  }

  get transformerAvailableColumns(): string[] {
    const joinSourceEntities = this.isRelationshipJoinOperation ? this.transformerSelectedFiles : [];
    const cacheKey = this.isRelationshipJoinOperation
      ? [
          ...joinSourceEntities,
          ...this.selectedPipelineDatasetIds,
          ...this.pipelinePreviewColumns,
        ].join('\u0000')
      : this.pipelinePreviewColumns.join('\u0000');
    if (cacheKey === this.transformerAvailableColumnsCacheKey) {
      return this.transformerAvailableColumnsCache;
    }

    const configured = this.isRelationshipJoinOperation
      ? this.selectedPipelineDatasets()
          .filter((dataset) => !joinSourceEntities.length || joinSourceEntities.includes(dataset.name))
          .flatMap((dataset) => dataset.columns)
          .concat(this.pipelinePreviewColumns)
          .map((column) => this.pipelineColumnLabel(column))
      : this.pipelinePreviewColumns.map((column) => this.pipelineColumnLabel(column));
    this.transformerAvailableColumnsCacheKey = cacheKey;
    this.transformerAvailableColumnsCache = Array.from(new Set(configured));
    return this.transformerAvailableColumnsCache;
  }

  get selectedTransformerRule(): PipelineTransformerRuleDefinition | null {
    return this.transformerRuleTypes.find((rule) => rule.id === this.transformerRuleType) ?? null;
  }

  get transformerRuleSupportsMultipleColumns(): boolean {
    return this.selectedTransformerRule?.supportsMultipleColumns ?? false;
  }

  get isRelationshipJoinOperation(): boolean {
    return this.isRelationshipJoinRuleType(this.transformerRuleType);
  }

  get relationshipPanelTitle(): string {
    return this.isRelationshipJoinOperation ? 'Relationship Joins' : 'Relationship Lookup';
  }

  get transformerOperationPreview(): Record<string, any> | null {
    const cacheKey = JSON.stringify([
      this.transformerMapToColumn,
      this.transformerOutputEntity,
      this.transformerSelectedColumns,
      this.activeMetadataOperationStep,
      this.transformerRuleType,
      this.transformerDelimiter,
      this.transformerRangeStart,
      this.transformerRangeEnd,
      this.transformerRangeMin,
      this.transformerRangeMax,
      this.transformerDefaultValue,
      this.transformerDecimalPrecision,
      this.transformerRegex,
      this.transformerReplacement,
      this.transformerArrayDelimiter,
      this.transformerLiteralValues,
      this.transformerJsonAliases,
      this.transformerLookupEntity,
      this.transformerLookupEntitySelections,
      this.transformerLookupKeys,
      this.transformerLookupResultField,
    ]);
    if (cacheKey === this.transformerOperationPreviewCacheKey) {
      return this.transformerOperationPreviewCache;
    }

    const selectedKeys = this.transformerSelectedColumns
      .map((label) => this.pipelineColumnKey(label))
      .filter(Boolean);
    this.transformerOperationPreviewCacheKey = cacheKey;
    this.transformerOperationPreviewCache =
      selectedKeys.length && this.transformerRuleType && this.transformerMapToColumn.trim()
        ? this.buildTransformerOperation(selectedKeys)
        : null;
    return this.transformerOperationPreviewCache;
  }

  get transformerMapToPreview(): string {
    return String(this.transformerOperationPreview?.['output'] || '');
  }

  pipelinePreviewCell(value: any): any {
    if (Array.isArray(value) || (value !== null && typeof value === 'object')) {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return value;
  }

  get pipelineSourceFiles(): any[] {
    return this.uploadedFiles
      .filter(({ name }) => !this.hiddenPipelineSourceNames.has(name))
      .map(({ name, file }) => ({
      id: `uploaded-${name}`,
      name,
      sheet: 'Uploaded_Source',
      rows: this.currentFileName === name ? this.dataset?.rows.length ?? 0 : 0,
      icon: 'excel',
      uploaded: true,
      file,
      }));
  }

  pipelineNodeIcon(type: string | undefined): string {
    const icons: Record<string, string> = {
      source: 'database',
      translation: 'translate',
      transformation: 'hub',
      relationship: 'account_tree',
      validation: 'verified_user',
    };
    return icons[type || ''] || 'radio_button_unchecked';
  }

  trackPipelineNode(_index: number, node: any): string {
    return node?.id ?? String(_index);
  }

  trackPipelineSourceFile(index: number, file: any): string {
    return String(file?.id || file?.name || index);
  }

  selectPipelineSourceFile(file: any): void {
    const fileName = file?.name || '';
    if (fileName !== this.selectedPipelineSourceName) {
      this.resetPipelinePreview();
    }
    this.selectedPipelineSourceName = fileName;
  }

  pipelineStepEnabled(_stepId: string): boolean {
    return true;
  }

  pipelineStepDisabledReason(stepId: string): string {
    if (this.pipelineStepEnabled(stepId)) return '';
    const stepIndex = this.pipelineStepOrder.indexOf(stepId);
    const prerequisite = this.pipelineStepOrder[Math.max(0, stepIndex - 1)];
    return `Complete ${this.pipelineStepLabel(prerequisite)} first`;
  }

  selectPipelineDataset(dataset: PipelineSavedDataset): void {
    const alreadySelected = this.selectedPipelineDatasetIds.includes(dataset.id);
    if (alreadySelected) {
      this.selectedPipelineDatasetIds = this.selectedPipelineDatasetIds.filter((id) => id !== dataset.id);
    } else {
      this.selectedPipelineDatasetIds = [...this.selectedPipelineDatasetIds, dataset.id];
    }
    this.syncPipelineDatasetSelection();
  }

  selectPipelineNode(event: Event | null, node: any): void {
    event?.stopPropagation();
    if (!node?.id) return;
    this.pipelinePendingStepNode = null;
    this.selectedPipelineNodeId = node.id;
    this.activePipelineDetailsTab = 'node-details';
    this.schedulePipelineCanvasFit();
    if (this.isMetadataOperationStep(node.type)) {
      this.prepareMetadataOperationStep(node.type);
      void this.refreshWorkflowStoreForMetadataStep(node.type);
    }
  }

  onPipelineSourceMenuPointerDown(event: PointerEvent, file: any): void {
    this.logPipelineSourceDeleteDebug('three-dot-pointerdown', {
      fileName: file?.name ?? null,
      resolvedFileKey: this.pipelineSourceMenuKey(file),
      currentOpenMenuId: this.openPipelineSourceMenuId,
      pointerType: event.pointerType,
      eventTarget: this.pipelineDebugElementSummary(event.target),
      eventCurrentTarget: this.pipelineDebugElementSummary(event.currentTarget),
    });
    this.togglePipelineSourceMenu(event, file);
  }

  suppressPipelineSourceMenuClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  togglePipelineSourceMenu(event: Event, file: any): void {
    event.preventDefault();
    event.stopPropagation();
    const fileId = this.pipelineSourceMenuKey(file);
    const previousMenuId = this.openPipelineSourceMenuId;
    this.openPipelineSourceMenuId = this.openPipelineSourceMenuId === fileId ? '' : fileId;
    this.openPipelineNodeMenuId = '';
    this.logPipelineSourceDeleteDebug('three-dot-click', {
      fileName: file?.name ?? null,
      resolvedFileKey: fileId,
      previousMenuId,
      nextMenuId: this.openPipelineSourceMenuId,
      eventTarget: this.pipelineDebugElementSummary(event.target),
      eventCurrentTarget: this.pipelineDebugElementSummary(event.currentTarget),
    });
    setTimeout(() => this.inspectPipelineSourceDeleteMenu('dom-probe-0ms', fileId), 0);
    setTimeout(() => this.inspectPipelineSourceDeleteMenu('dom-probe-100ms', fileId), 100);
  }

  pipelineSourceMenuKey(file: any): string {
    return String(file?.id || file?.name || '');
  }

  onPipelineSourceDeletePointerDown(event: PointerEvent, file: any): void {
    event.preventDefault();
    event.stopPropagation();
    const fileKey = this.pipelineSourceMenuKey(file);
    if (!fileKey || this.pipelineSourceDeleteInProgress.has(fileKey)) {
      this.logPipelineSourceDeleteDebug('delete-pointerdown-ignored', {
        fileName: file?.name ?? null,
        resolvedFileKey: fileKey,
        deleteInProgress: this.pipelineSourceDeleteInProgress.has(fileKey),
      });
      return;
    }

    this.pipelineSourceDeleteInProgress.add(fileKey);
    this.logPipelineSourceDeleteDebug('delete-pointerdown', {
      fileName: file?.name ?? null,
      resolvedFileKey: fileKey,
      pointerType: event.pointerType,
    });
    this.deletePipelineSource(event, file);
  }

  deletePipelineSource(event: Event, file: any): void {
    event.preventDefault();
    event.stopPropagation();
    const fileName = String(file?.name || '');
    const uploadedIndex = this.uploadedFiles.findIndex((entry) => entry.name === fileName);
    this.logPipelineSourceDeleteDebug('delete-click', {
      fileName,
      resolvedFileKey: this.pipelineSourceMenuKey(file),
      uploadedIndex,
      sourceCountBefore: this.pipelineSourceFiles.length,
      canvasNodeCountBefore: this.pipelineCanvasNodes.length,
      savedDatasetCountBefore: this.pipelineSavedDatasets.length,
    });

    if (fileName) {
      this.hiddenPipelineSourceNames = new Set([...this.hiddenPipelineSourceNames, fileName]);
    }
    if (uploadedIndex >= 0) {
      this.removeUploadedFile(uploadedIndex);
      this.uploadedFiles = [...this.uploadedFiles];
    }
    const removedNodeIds = new Set(
      this.pipelineCanvasNodes
        .filter((node) => node.type === 'source' && node.sourceFileName === fileName)
        .map((node) => node.id),
    );
    if (removedNodeIds.size) {
      this.pipelineCanvasNodes = this.pipelineCanvasNodes.filter((node) => !removedNodeIds.has(node.id));
      this.pipelineConnections = this.pipelineConnections.filter(
        (connection) => !removedNodeIds.has(connection.from) && !removedNodeIds.has(connection.to),
      );
      if (removedNodeIds.has(this.selectedPipelineNodeId)) {
        this.selectedPipelineNodeId = '';
      }
      this.schedulePipelineCanvasFit();
    }

    if (this.selectedPipelineSourceName === fileName) {
      this.selectedPipelineSourceName = this.pipelineSourceFiles[0]?.name || '';
    }
    const removedDatasetIds = new Set(
      this.pipelineSavedDatasets
        .filter((dataset) => dataset.sourceFileName === fileName)
        .map((dataset) => dataset.id),
    );
    this.pipelineSavedDatasets = this.pipelineSavedDatasets.filter(
      (dataset) => !removedDatasetIds.has(dataset.id),
    );
    this.selectedPipelineDatasetIds = this.selectedPipelineDatasetIds.filter(
      (id) => !removedDatasetIds.has(id),
    );
    this.pipelineAppliedSteps = this.pipelineAppliedSteps
      .map((step) => ({
        ...step,
        datasetIds: step.datasetIds.filter((id) => !removedDatasetIds.has(id)),
      }))
      .filter((step) => step.datasetIds.length > 0);
    this.syncPipelineDatasetSelection();
    this.pipelineTranslationFiles = this.pipelineTranslationFiles.filter((selectedFile) => selectedFile.name !== fileName);
    this.openPipelineSourceMenuId = '';
    this.pipelineSourceDeleteInProgress.delete(this.pipelineSourceMenuKey(file));
    this.logPipelineSourceDeleteDebug('delete-complete', {
      fileName,
      sourceCountAfter: this.pipelineSourceFiles.length,
      remainingSourceNames: this.pipelineSourceFiles.map((source) => source.name),
      canvasNodeCountAfter: this.pipelineCanvasNodes.length,
      savedDatasetCountAfter: this.pipelineSavedDatasets.length,
      selectedSourceName: this.selectedPipelineSourceName,
    });
  }

  private inspectPipelineSourceDeleteMenu(stage: string, expectedKey: string): void {
    const menus = Array.from(
      document.querySelectorAll<HTMLElement>('[data-pipeline-source-delete-menu="true"]'),
    );
    const menu = menus.find((candidate) => candidate.dataset['sourceMenuKey'] === expectedKey) ?? null;
    const rect = menu?.getBoundingClientRect();
    const style = menu ? getComputedStyle(menu) : null;
    const parent = menu?.parentElement ?? null;
    const parentStyle = parent ? getComputedStyle(parent) : null;

    this.logPipelineSourceDeleteDebug(stage, {
      expectedKey,
      openMenuState: this.openPipelineSourceMenuId,
      renderedMenuCount: menus.length,
      renderedMenuKeys: menus.map((candidate) => candidate.dataset['sourceMenuKey']),
      expectedMenuFound: !!menu,
      menuRect: rect
        ? {
            x: Math.round(rect.x),
            y: Math.round(rect.y),
            width: Math.round(rect.width),
            height: Math.round(rect.height),
          }
        : null,
      menuStyles: style
        ? {
            display: style.display,
            visibility: style.visibility,
            opacity: style.opacity,
            zIndex: style.zIndex,
            position: style.position,
          }
        : null,
      parentStyles: parentStyle
        ? {
            overflow: parentStyle.overflow,
            overflowX: parentStyle.overflowX,
            overflowY: parentStyle.overflowY,
          }
        : null,
    });
  }

  private pipelineDebugElementSummary(target: EventTarget | null): Record<string, any> | null {
    if (!(target instanceof HTMLElement)) return null;
    return {
      tag: target.tagName,
      className: target.className,
      ariaExpanded: target.getAttribute('aria-expanded'),
      sourceMenuTriggerKey: target.getAttribute('data-source-menu-trigger-key'),
    };
  }

  private logPipelineSourceDeleteDebug(stage: string, details: Record<string, any>): void {
    console.error(`[${this.pipelineSourceDeleteDebugKey}] ${stage}`, {
      timestamp: new Date().toISOString(),
      ...details,
    });
  }

  togglePipelineNodeMenu(event: Event, node: any): void {
    event.preventDefault();
    event.stopPropagation();
    const nodeId = String(node?.id || '');
    this.openPipelineNodeMenuId = this.openPipelineNodeMenuId === nodeId ? '' : nodeId;
    this.openPipelineSourceMenuId = '';
  }

  deletePipelineNode(event: Event, node: any): void {
    event.preventDefault();
    event.stopPropagation();
    const nodeId = String(node?.id || '');
    if (!nodeId) return;

    this.pipelineCanvasNodes = this.pipelineCanvasNodes.filter((candidate) => candidate.id !== nodeId);
    this.pipelineConnections = this.pipelineConnections.filter(
      (connection) => connection.from !== nodeId && connection.to !== nodeId,
    );
    if (
      this.pendingPipelineConnection?.nodeId === nodeId
      || this.activePipelineConnectionDrag?.nodeId === nodeId
    ) {
      this.cancelPipelineConnectionCreation();
    }
    if (this.selectedPipelineNodeId === nodeId) {
      this.selectedPipelineNodeId = this.pipelineCanvasNodes[0]?.id || '';
    }
    this.openPipelineNodeMenuId = '';
    const deletedStepIndex = this.pipelineStepOrder.indexOf(node?.type);
    if (deletedStepIndex >= 0) {
      this.pipelineStepOrder.slice(deletedStepIndex).forEach((step) => this.pipelineCompletedSteps.delete(step));
    }
    this.schedulePipelineCanvasFit();
  }

  onPipelineStepSelected(item: any): void {
    if (!this.pipelineStepEnabled(item?.id)) return;
    const matchingNode = this.pipelineCanvasNodes.find((node) => node.type === item?.id);
    if (matchingNode) {
      this.selectPipelineNode(null, matchingNode);
    }
    if (item?.id === 'translation') {
      this.openPipelineTranslationPreview();
      return;
    }
    if (!matchingNode && item?.id) {
      this.selectedPipelineNodeId = '';
      this.pipelinePendingStepNode = this.createPipelineNode(item, { x: 0, y: 0 });
      if (this.isMetadataOperationStep(item.id)) {
        this.prepareMetadataOperationStep(item.id);
        void this.refreshWorkflowStoreForMetadataStep(item.id);
      }
    }
  }

  private isMetadataOperationStep(
    stepType: string,
  ): stepType is 'transformation' | 'relationship' | 'validation' {
    return ['transformation', 'relationship', 'validation'].includes(stepType);
  }

  private prepareMetadataOperationStep(
    stepType: 'transformation' | 'relationship' | 'validation',
  ): void {
    this.metadataOperationRuleByStep[this.activeMetadataOperationStep] = this.transformerRuleType;
    this.activeMetadataOperationStep = stepType;
    this.transformerRuleType = this.metadataOperationRuleByStep[stepType];
    this.transformerApplyMessage = '';
    if (!this.transformerSelectedFiles.length) {
      this.transformerSelectedFiles = [...this.transformerAvailableFiles];
    }
  }

  loadTransformerLookupEntities(forceRefresh = false, showLoading = true): void {
    if (this.transformerLookupEntitiesLoading || (!forceRefresh && this.transformerLookupEntitiesLoaded)) return;

    const shouldShowLoading = showLoading || !this.transformerLookupEntitiesLoaded;
    this.transformerLookupEntitiesLoading = shouldShowLoading;
    if (forceRefresh) {
      this.transformerLookupEntitiesLoaded = false;
    }
    this.transformerLookupEntitiesError = '';
    this.datasetService.getLookupEntityMetadata().subscribe({
      next: (metadata) => {
        this.transformerLookupAttributesByEntity = new Map(
          metadata.map((item) => [item.entity, item.attributes]),
        );
        this.transformerLookupEntities = metadata.map((item) => item.entity);
        this.transformerLookupEntitiesLoaded = true;
        this.transformerLookupEntitiesLoading = false;
        this.syncTransformerLookupResultFields();
        this.syncTransformerRelationshipSelections();
        if (!this.transformerLookupEntities.length) {
          this.transformerLookupEntitiesError = 'No lookup entities are available.';
        }
      },
      error: (error) => {
        this.transformerLookupEntitiesLoading = false;
        this.transformerLookupEntitiesError = error instanceof Error && error.message
          ? `Unable to load lookup entities: ${error.message}`
          : 'Unable to load lookup entities.';
      },
    });
  }

  onTransformerLookupEntityChange(entity: string): void {
    const entityChanged = entity !== this.transformerLookupEntity;
    this.transformerLookupEntity = entity;
    this.transformerLookupEntitySelections = entity ? [entity] : [];
    this.syncTransformerLookupResultFields();
    this.transformerLookupKeys = this.transformerLookupKeys.filter((key) =>
      this.transformerLookupResultFields.includes(key),
    );
    if (entityChanged) {
      this.transformerLookupResultField = '';
    }
  }

  onTransformerLookupEntitiesChange(entities: string[]): void {
    const normalized = Array.from(new Set((entities || []).filter(Boolean)));
    const firstEntity = normalized[0] ?? '';
    const entityChanged = firstEntity !== this.transformerLookupEntity;
    this.transformerLookupEntitySelections = normalized;
    this.transformerLookupEntity = firstEntity;
    this.syncTransformerLookupResultFields();
    this.transformerLookupKeys = this.transformerLookupKeys.filter((key) =>
      this.transformerLookupResultFields.includes(key),
    );
    if (entityChanged) {
      this.transformerLookupResultField = '';
    }
  }

  onTransformerOutputEntityChange(entity: string): void {
    const entityChanged = entity !== this.transformerOutputEntity;
    this.transformerOutputEntity = entity;
    if (!this.isRelationshipJoinOperation) {
      this.loadTransformerLookupEntities(true, false);
    }
    if (entityChanged) {
      this.transformerMapToColumn = '';
      this.transformerCreatingOutputAttribute = false;
    }
    this.syncTransformerRelationshipSelections();
  }

  onTransformerOutputAttributeChange(attribute: string): void {
    if (attribute === this.transformerCreateOutputAttributeValue) {
      this.transformerCreatingOutputAttribute = true;
      this.transformerMapToColumn = '';
      return;
    }

    this.transformerCreatingOutputAttribute = false;
    this.transformerMapToColumn = attribute;
  }

  private syncTransformerLookupResultFields(): void {
    const sourceEntities = this.transformerLookupEntitySelections.length
      ? this.transformerLookupEntitySelections
      : this.transformerLookupEntity
        ? [this.transformerLookupEntity]
        : [];
    const attributes = sourceEntities.flatMap((entity) =>
      this.transformerLookupAttributesByEntity.get(entity) ?? [],
    );
    this.transformerLookupResultFields = Array.from(new Set(attributes)).sort((left, right) => left.localeCompare(right));
  }

  private syncTransformerRelationshipSelections(): void {
    if (!this.isRelationshipJoinOperation) return;

    const availableColumns = new Set(this.transformerAvailableColumns);
    this.transformerSelectedColumns = this.transformerSelectedColumns.filter((column) =>
      availableColumns.has(column),
    );

    const outputAttributes = new Set(this.transformerOutputAttributeOptions);
    if (
      this.transformerMapToColumn
      && !this.transformerCreatingOutputAttribute
      && !outputAttributes.has(this.transformerMapToColumn)
    ) {
      this.transformerMapToColumn = '';
    }
  }

  private async refreshWorkflowStoreForMetadataStep(
    stepType: 'transformation' | 'relationship' | 'validation',
  ): Promise<void> {
    const workflowId = this.datasetService.getLatestWorkflowId();
    const stepLabel = this.pipelineStepLabel(stepType);
    if (!workflowId) {
      this.transformerApplyMessage = `Add a source file before opening ${stepLabel}.`;
      return;
    }

    try {
      await firstValueFrom(this.datasetService.refreshLatestWorkflowStore());
    } catch (error) {
      this.transformerApplyMessage = error instanceof Error && error.message
        ? `Unable to load workflow ${workflowId}: ${error.message}`
        : `Unable to load workflow ${workflowId}.`;
    }
  }

  setTransformerFileMode(mode: 'single' | 'multiple'): void {
    this.transformerFileMode = mode;
    if (mode === 'single' && this.transformerSelectedFiles.length > 1) {
      this.transformerSelectedFiles = this.transformerSelectedFiles.slice(0, 1);
      const selectedName = this.transformerSelectedFiles[0];
      this.selectedPipelineDatasetIds = this.pipelineSavedDatasets
        .filter((dataset) => dataset.name === selectedName)
        .map((dataset) => dataset.id);
    }
  }

  addTransformerFile(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const fileName = select.value;
    select.value = '';
    if (!fileName) return;
    this.transformerSelectedFiles = this.transformerFileMode === 'single'
      ? [fileName]
      : Array.from(new Set([...this.transformerSelectedFiles, fileName]));
    const selectedNames = new Set(this.transformerSelectedFiles);
    const matchingDatasetIds = this.pipelineSavedDatasets
      .filter((dataset) => selectedNames.has(dataset.name))
      .map((dataset) => dataset.id);
    if (matchingDatasetIds.length) {
      this.selectedPipelineDatasetIds = matchingDatasetIds;
      this.syncPipelineDatasetSelection();
    }
  }

  onTransformerSelectedFilesChange(fileNames: string[]): void {
    this.transformerSelectedFiles = Array.from(new Set((fileNames || []).filter(Boolean)));
    if (!this.isRelationshipJoinOperation) {
      this.loadTransformerLookupEntities(true, false);
    }
    const selectedNames = new Set(this.transformerSelectedFiles);
    const matchingDatasetIds = this.pipelineSavedDatasets
      .filter((dataset) => selectedNames.has(dataset.name))
      .map((dataset) => dataset.id);
    if (matchingDatasetIds.length) {
      this.selectedPipelineDatasetIds = matchingDatasetIds;
      this.syncPipelineDatasetSelection();
    }
    if (this.transformerOutputEntity && !this.transformerSelectedFiles.includes(this.transformerOutputEntity)) {
      this.transformerOutputEntity = '';
      this.transformerMapToColumn = '';
      this.transformerCreatingOutputAttribute = false;
    }
    this.syncTransformerRelationshipSelections();
  }

  onTransformerFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    const supportedFiles = files.filter((file) => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      return Boolean(this.getSupportedFileType(extension));
    });
    if (!supportedFiles.length) {
      this.onFileSelected(event);
      return;
    }

    const selectedNames = supportedFiles.map((file) => file.name);
    this.transformerSelectedFiles = this.transformerFileMode === 'single'
      ? selectedNames.slice(0, 1)
      : Array.from(new Set([...this.transformerSelectedFiles, ...selectedNames]));

    // Reuse the existing source upload, parsing, preview, and backend enrichment flow.
    this.onFileSelected(event, true);
  }

  removeTransformerFile(fileName: string): void {
    this.transformerSelectedFiles = this.transformerSelectedFiles.filter((name) => name !== fileName);
    const selectedNames = new Set(this.transformerSelectedFiles);
    this.selectedPipelineDatasetIds = this.pipelineSavedDatasets
      .filter((dataset) => selectedNames.has(dataset.name))
      .map((dataset) => dataset.id);
  }

  setTransformerColumnMode(mode: 'single' | 'multiple'): void {
    this.transformerColumnMode = mode;
    if (mode === 'single' && this.transformerSelectedColumns.length > 1) {
      this.transformerSelectedColumns = this.transformerSelectedColumns.slice(0, 1);
    }
  }

  addTransformerColumn(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const columnName = select.value;
    select.value = '';
    if (!columnName) return;
    this.transformerSelectedColumns = this.transformerColumnMode === 'single'
      ? [columnName]
      : Array.from(new Set([...this.transformerSelectedColumns, columnName]));
  }

  removeTransformerColumn(columnName: string): void {
    this.transformerSelectedColumns = this.transformerSelectedColumns.filter((name) => name !== columnName);
  }

  selectTransformerRule(ruleId: string): void {
    this.transformerRuleType = ruleId;
    this.metadataOperationRuleByStep[this.activeMetadataOperationStep] = ruleId;
    if (this.isRelationshipJoinOperation) {
      this.syncTransformerRelationshipSelections();
      this.updateRelationshipDummyJoinTypeSelection();
    } else if (this.activeMetadataOperationStep === 'relationship') {
      this.loadTransformerLookupEntities(true, false);
    }
    if (this.activeMetadataOperationStep === 'transformation' && !this.transformerRuleSupportsMultipleColumns) {
      this.transformerColumnMode = 'single';
      this.transformerSelectedColumns = this.transformerSelectedColumns.slice(0, 1);
    }
    this.transformerApplyMessage = '';
  }

  resetTransformerOperations(): void {
    this.transformerFileMode = 'multiple';
    this.transformerColumnMode = 'multiple';
    this.transformerSelectedFiles = [...this.transformerAvailableFiles];
    this.transformerOutputEntity = '';
    this.transformerCreatingOutputAttribute = false;
    this.transformerMapToColumn = '';
    this.transformerSelectedColumns = [];
    this.transformerRuleSearch = '';
    this.transformerRuleType = this.activeMetadataOperationStep === 'relationship'
      ? 'lookup'
      : this.activeMetadataOperationStep === 'validation'
        ? 'sum'
        : 'trim';
    this.metadataOperationRuleByStep[this.activeMetadataOperationStep] = this.transformerRuleType;
    this.transformerDelimiter = ' ';
    this.transformerRangeStart = '0';
    this.transformerRangeEnd = '3';
    this.transformerRangeMin = '1';
    this.transformerRangeMax = '10';
    this.transformerDefaultValue = 'N/A';
    this.transformerDecimalPrecision = '2';
    this.transformerRegex = '[@.#$]';
    this.transformerReplacement = '';
    this.transformerArrayDelimiter = ',';
    this.transformerLiteralValues = '';
    this.transformerJsonAliases = '';
    this.transformerLookupEntity = '';
    this.transformerLookupEntitySelections = [];
    this.transformerLookupKeys = [];
    this.transformerLookupResultField = '';
    this.transformerOutputMode = 'overwrite';
    this.transformerOutputColumn = 'same';
    this.transformerNewColumnName = '';
    this.transformerApplyMessage = '';
    this.resetPipelinePreview();
  }

  async applyTransformerPreview(): Promise<boolean> {
    if (!this.selectedPipelineDatasetIds.length) {
      this.transformerApplyMessage = 'Select at least one saved dataset from the left panel.';
      return false;
    }
    if (!this.transformerMapToColumn.trim()) {
      this.transformerApplyMessage = this.isRelationshipJoinOperation
        ? 'Select the Output Attribute first.'
        : 'Select the Map To column first.';
      return false;
    }
    if (!this.transformerSelectedColumns.length) {
      this.transformerApplyMessage = 'Select at least one operation column.';
      return false;
    }

    const selectedDatasets = this.selectedPipelineDatasets();
    if (!selectedDatasets.some((dataset) => dataset.rows.length)) {
      this.transformerApplyMessage =
        'Analyze and preview the Translation configuration before applying a transformation.';
      return false;
    }
    const config = this.captureTransformerConfig();
    const availableColumns = Array.from(new Set(selectedDatasets.flatMap((dataset) => dataset.columns)));
    const selectedKeys = config.selectedColumns
      .map((label) => this.pipelineColumnKey(label))
      .filter((key) => availableColumns.includes(key));
    if (!selectedKeys.length) {
      this.transformerApplyMessage = 'The selected columns are not available in the current preview.';
      return false;
    }
    const validationError = this.validateTransformerConfig(config, selectedKeys);
    if (validationError) {
      this.transformerApplyMessage = validationError;
      return false;
    }

    const operationConfig = this.buildTransformerOperation(selectedKeys);
    const stepType = this.activeMetadataOperationStep;
    const label = [...this.transformerRuleTypes, ...this.validationRuleTypes, ...this.relationshipOperationTypes]
      .find((rule) => rule.id === config.ruleType)?.label || this.pipelineStepLabel(stepType);
    const existingStep = this.pipelineAppliedSteps.find((step) => step.id === this.editingPipelineStepId);
    if (existingStep?.type === stepType) {
      existingStep.active = false;
      this.pipelineAppliedSteps = [...this.pipelineAppliedSteps];
    }
    this.pipelineAppliedSteps = [
      ...this.pipelineAppliedSteps,
      {
        id: `step-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type: stepType,
        label,
        summary: this.transformationStepSummary(config),
        appliedAt: new Date(),
        datasetIds: [...this.selectedPipelineDatasetIds],
        config,
        active: true,
        supersedesStepId: existingStep?.type === stepType ? existingStep.id : undefined,
      },
    ];
    this.editingPipelineStepId = '';
    this.rebuildSelectedPipelineDatasets();

    this.ensurePipelineActionNode(stepType, {
      meta: `${this.transformerSelectedColumns.length} Column${this.transformerSelectedColumns.length === 1 ? '' : 's'} -> ${this.transformerMapToColumn}`,
      summary: label,
      operationConfig,
    });
    this.markPipelineStepComplete(stepType);
    this.transformerApplyMessage = `${label} applied to the data preview.`;
    return true;
  }

  async finalizeTransformerChanges(): Promise<void> {
    if (this.activeMetadataOperationStep === 'relationship' && this.isRelationshipJoinOperation) {
      this.updateRelationshipDummyJoinTypeSelection();
      await this.saveRelationshipWorkflowJson();
      return;
    }

    if (this.activeMetadataOperationStep === 'relationship' && this.transformerRuleType === 'lookup') {
      await this.saveRelationshipWorkflowJson();
      return;
    }

    let previewApplied = false;
    const hasPreviewRows = this.selectedPipelineDatasets().some((dataset) => dataset.rows.length);

    if (this.activeMetadataOperationStep !== 'relationship' && hasPreviewRows) {
      previewApplied = await this.applyTransformerPreview();
      if (!previewApplied) return;
    }

    await this.saveTransformerWorkflowJson(!previewApplied);
  }

  async saveTransformerWorkflowJson(recordAppliedStep = true): Promise<void> {
    if (!this.transformerMapToColumn.trim()) {
      this.transformerApplyMessage = this.isRelationshipJoinOperation
        ? 'Select the Output Attribute before saving workflow JSON.'
        : 'Select the Map To column before saving workflow JSON.';
      return;
    }
    if (!this.transformerSelectedColumns.length) {
      this.transformerApplyMessage = 'Select at least one operation column before saving workflow JSON.';
      return;
    }

    const config = this.captureTransformerConfig();
    const availableColumns = Array.from(new Set([
      ...this.selectedPipelineDatasets().flatMap((dataset) => dataset.columns),
      ...this.pipelinePreviewColumns,
      ...this.transformerAvailableColumns.map((column) => this.pipelineColumnKey(column)),
    ]));
    const selectedKeys = config.selectedColumns
      .map((label) => this.pipelineColumnKey(label))
      .filter((key) => !availableColumns.length || availableColumns.includes(key));
    if (!selectedKeys.length) {
      this.transformerApplyMessage = 'The selected columns are not available in the current workflow metadata.';
      return;
    }

    const validationError = this.validateTransformerConfig(config, selectedKeys);
    if (validationError) {
      this.transformerApplyMessage = validationError;
      return;
    }

    try {
      const operationConfig = this.buildTransformerOperation(selectedKeys);
      const operationLabel = this.transformerRuleLabel(this.transformerRuleType);
      this.transformerApplyMessage = 'Sending updated workflow JSON...';
      await this.persistTransformerWorkflowTargetMetadata(config, operationConfig);
      this.transformerApplyMessage = 'Updated workflow JSON sent successfully.';

      this.ensurePipelineActionNode(this.activeMetadataOperationStep, {
        meta: `${this.transformerSelectedColumns.length} Column${this.transformerSelectedColumns.length === 1 ? '' : 's'} -> ${this.transformerMapToColumn}`,
        summary: operationLabel,
        operationConfig,
      });
      this.markPipelineStepComplete(this.activeMetadataOperationStep);

      if (this.activeMetadataOperationStep !== 'transformation' && recordAppliedStep) {
        this.recordPipelineAppliedStep(
          this.activeMetadataOperationStep,
          operationLabel,
          `${this.transformerSelectedColumns.join(', ')} -> ${this.transformerMapToColumn}`,
        );
      }
    } catch (error) {
      this.transformerApplyMessage = error instanceof Error && error.message
        ? error.message
        : 'Unable to send updated workflow JSON.';
    }
  }

  applyPipelineRelationship(): void {
    if (this.selectedPipelineDatasetIds.length < 2) return;
    const selectedDatasets = this.selectedPipelineDatasets();
    this.recordPipelineAppliedStep(
      'relationship',
      'Left Join',
      `${selectedDatasets.map((dataset) => dataset.name).join(' + ')} joined using configured keys`,
    );
    this.ensurePipelineActionNode('relationship', {
      meta: selectedDatasets.map((dataset) => dataset.name).join(' + '),
      summary: 'Left Join',
    });
    this.markPipelineStepComplete('relationship');
  }

  applyPipelineValidation(): void {
    if (!this.selectedPipelineDatasetIds.length) return;
    this.recordPipelineAppliedStep(
      'validation',
      'Validation',
      `${this.selectedPipelineDatasets().length} dataset(s) validated with the configured rules`,
    );
    this.ensurePipelineActionNode('validation', {
      meta: 'Validation',
      summary: 'Rules Applied',
    });
    this.markPipelineStepComplete('validation');
  }

  setPipelineDetailsTab(tab: string): void {
    this.activePipelineDetailsTab = tab === 'applied-steps' ? 'applied-steps' : 'node-details';
    if (this.activePipelineDetailsTab === 'node-details') {
      this.editingPipelineStepId = '';
      this.transformerApplyMessage = '';
    }
  }

  editPipelineAppliedStep(step: PipelineAppliedStep): void {
    this.editingPipelineStepId = step.id;
    this.selectedPipelineDatasetIds = [...step.datasetIds];
    this.syncPipelineDatasetSelection();
    const matchingNode = this.pipelineCanvasNodes.find((node) => node.type === step.type);
    if (matchingNode) this.selectedPipelineNodeId = matchingNode.id;
    this.activePipelineDetailsTab = 'node-details';
    if (step.type === 'transformation' && step.config) {
      this.restoreTransformerConfig(step.config);
      this.transformerApplyMessage = `Editing ${step.label}. Apply to update the current dataset.`;
    }
  }

  pipelineAppliedDatasetNames(step: PipelineAppliedStep): string {
    return this.pipelineSavedDatasets
      .filter((dataset) => step.datasetIds.includes(dataset.id))
      .map((dataset) => dataset.name)
      .join(', ');
  }

  startPipelinePanelResize(event: PointerEvent, panel: 'left' | 'right' | 'preview'): void {
    event.preventDefault();
    event.stopPropagation();
    this.pipelinePanelResizeStart = {
      panel,
      pointerX: event.clientX,
      pointerY: event.clientY,
      startSize: this.pipelinePanelSizes[panel],
    };
    document.body.classList.add('pipeline-panel-resizing');
  }

  onPipelinePanelResizeMove(event: PointerEvent): void {
    if (!this.pipelinePanelResizeStart) return;

    const { panel, pointerX, pointerY, startSize } = this.pipelinePanelResizeStart;
    if (panel === 'left') {
      this.pipelinePanelSizes.left = this.clampNumber(startSize + event.clientX - pointerX, 260, 520);
      this.schedulePipelineCanvasFit();
      return;
    }

    if (panel === 'right') {
      this.pipelinePanelSizes.right = this.clampNumber(startSize + pointerX - event.clientX, 320, 480);
      this.schedulePipelineCanvasFit();
      return;
    }

    const maxPreviewHeight = Math.max(260, Math.round(window.innerHeight * 0.62));
    this.pipelinePanelSizes.preview = this.clampNumber(startSize + pointerY - event.clientY, 220, maxPreviewHeight);
    this.schedulePipelineCanvasFit();
  }

  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  stopPipelinePointerActions(event?: PointerEvent): void {
    if (this.activePipelineConnectionDrag && event) {
      this.finishPipelineConnectionDrag(event);
    }
    if (!this.pipelinePanelResizeStart) return;
    this.pipelinePanelResizeStart = null;
    document.body.classList.remove('pipeline-panel-resizing');
  }

  @HostListener('window:resize')
  onPipelineWindowResize(): void {
    this.schedulePipelineCanvasFit();
  }

  @HostListener('document:keydown', ['$event'])
  onPipelineDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;

    if (this.openPipelineSourceMenuId || this.openPipelineNodeMenuId) {
      event.preventDefault();
      this.openPipelineSourceMenuId = '';
      this.openPipelineNodeMenuId = '';
      return;
    }

    if (this.activePipelineConnectionDrag || this.pendingPipelineConnection) {
      event.preventDefault();
      this.cancelPipelineConnectionCreation();
      return;
    }

    if (this.pipelineCanvasMaximized) {
      event.preventDefault();
      this.togglePipelineCanvasMaximize(false);
      return;
    }

    if (this.pipelineTranslationPreviewOpen) {
      event.preventDefault();
      this.closePipelineTranslationPreview();
      return;
    }

  }

  @HostListener('document:click', ['$event'])
  closePipelineContextMenus(event: MouseEvent): void {
    if (this.openPipelineSourceMenuId) {
      this.logPipelineSourceDeleteDebug('document-click-close', {
        closingMenuId: this.openPipelineSourceMenuId,
        eventTarget: this.pipelineDebugElementSummary(event.target),
      });
    }
    this.openPipelineSourceMenuId = '';
    this.openPipelineNodeMenuId = '';
  }

  closePipelineTranslationPreview(): void {
    this.pipelineTranslationPreviewOpen = false;
    this.pipelineTranslationFiles = [];
    this.pipelineTranslationPreviewError = '';
  }

  async applyPipelineTranslationPreview(event: {
    columns: Array<{
      sourceColumn: string;
      sourceHeader: string;
      columnName: string;
      dataType: string;
    }>;
  }): Promise<void> {
    const configuredColumns = event?.columns ?? [];
    // Read before closing — the close clears pipelineTranslationFiles.
    const translatedSourceName = this.pipelineTranslationFiles[0]?.name
      || this.selectedPipelineSourceName
      || this.currentFileName;

    // Close first, then show the loader on the preview table underneath: the
    // row mapping + xlsx build below block for seconds on a big dataset, and
    // that gap used to pass with the modal still up and nothing happening.
    this.closePipelineTranslationPreview();
    const rowCount = this.rawDataset?.rows?.length ?? 0;
    this.translationApplyingText = `Applying translation to ${formatRowCount(rowCount)} rows…`;
    await this.delay(0);

    try {
      this.applyPipelineTranslationColumns(configuredColumns);
      this.ensurePipelineActionNode('translation', {
        meta: 'Map Values',
        summary: `${configuredColumns.length} Column${configuredColumns.length === 1 ? '' : 's'}`,
      });
      this.markPipelineStepComplete('translation');
      await this.savePipelineDataset(translatedSourceName, false);
    } catch (error) {
      this.errorMessage = error instanceof Error && error.message
        ? `Unable to apply the translation: ${error.message}`
        : 'Unable to apply the translation.';
    } finally {
      this.translationApplyingText = '';
    }
  }

  private applyPipelineTranslationColumns(configuredColumns: Array<{
    sourceColumn: string;
    sourceHeader: string;
    columnName: string;
    dataType: string;
  }>): void {
    const sourceRows = this.rawDataset?.rows ?? [];
    this.pipelinePreviewColumns = configuredColumns.map((column) => column.columnName);
    this.pipelinePreviewTotalRows = sourceRows.length;
    this.pipelinePreviewTitle = `Preview of translated data - First ${Math.min(10, sourceRows.length)} Rows`;

    const sourceKeys = sourceRows.length ? Object.keys(sourceRows[0]) : [];
    const resolveSourceKey = (column: typeof configuredColumns[number]): string | undefined => {
      const candidates = [column.sourceHeader, column.sourceColumn, column.columnName]
        .map((value) => this.pipelineColumnKey(value));
      return sourceKeys.find((key) => candidates.includes(this.pipelineColumnKey(key)));
    };

    this.pipelineWorkingRows = sourceRows.map((sourceRow) => {
      const previewRow: Record<string, any> = {};
      configuredColumns.forEach((column) => {
        const sourceKey = resolveSourceKey(column);
        previewRow[column.columnName] = this.coercePipelinePreviewValue(
          sourceKey ? sourceRow[sourceKey] : null,
          column.dataType,
        );
      });
      return previewRow;
    });
    this.pipelinePreviewRows = this.pipelineWorkingRows.slice(0, 10).map((row) => ({ ...row }));
  }

  completePipelineTranslationAction(event: 'export' | 'save' | {
    action: 'export' | 'save';
    columns?: Array<{
      sourceColumn: string;
      sourceHeader: string;
      columnName: string;
      dataType: string;
    }>;
    changeCount?: number;
  }): void {
    const action = typeof event === 'string' ? event : event.action;
    const configuredColumns = typeof event === 'string' ? [] : event.columns ?? [];
    if (configuredColumns.length) {
      this.applyPipelineTranslationColumns(configuredColumns);
    }
    const changeCount = typeof event === 'string' ? 0 : event.changeCount ?? 0;
    const fallbackColumnCount = configuredColumns.length || this.pipelinePreviewColumns.length || this.rawDataset?.columns?.length || 0;
    this.ensurePipelineActionNode('translation', {
      meta: 'Map Values',
      summary: changeCount > 0
        ? `${changeCount} Change${changeCount === 1 ? '' : 's'}`
        : `${fallbackColumnCount} Column${fallbackColumnCount === 1 ? '' : 's'}`,
    });
    this.markPipelineStepComplete('translation');
    if (action === 'save') {
      const translatedSourceName = this.pipelineTranslationFiles[0]?.name
        || this.selectedPipelineSourceName
        || this.currentFileName;
      void this.savePipelineDataset(translatedSourceName, false);
    }
    this.closePipelineTranslationPreview();
  }

  async savePipelineDataset(sourceFileNameOverride = '', preferSelectedDataset = true): Promise<boolean> {
    const sourceRows = this.pipelineWorkingRows.length
      ? this.pipelineWorkingRows
      : (this.rawDataset?.rows ?? []).map((row) => ({ ...row }));
    const columns = this.pipelinePreviewColumns.length
      ? [...this.pipelinePreviewColumns]
      : (this.rawDataset?.columns ?? []).map((column) => column.name);
    if (!sourceRows.length || !columns.length) return false;

    const selectedDataset = preferSelectedDataset ? this.selectedPipelineDatasets().at(-1) : undefined;
    const sourceFileName = sourceFileNameOverride
      || selectedDataset?.sourceFileName
      || this.selectedPipelineSourceName
      || this.pipelineTranslationFiles[0]?.name
      || this.currentFileName
      || 'dataset.xlsx';
    const baseName = sourceFileName.replace(/\.[^.]+$/, '') || 'dataset';
    const existingDataset = selectedDataset || this.pipelineSavedDatasets.find(
      (dataset) => dataset.sourceFileName === sourceFileName,
    );
    if (existingDataset) {
      existingDataset.columns = [...columns];
      existingDataset.rows = sourceRows.map((row) => ({ ...row }));
      const hasTransformations = this.pipelineAppliedSteps.some(
        (step) => step.type === 'transformation' && step.datasetIds.includes(existingDataset.id),
      );
      if (!hasTransformations) {
        existingDataset.baseColumns = [...columns];
        existingDataset.baseRows = sourceRows.map((row) => ({ ...row }));
      }
      await this.refreshPipelineDatasetFile(existingDataset);
      this.pipelineSavedDatasets = [...this.pipelineSavedDatasets];
      this.selectedPipelineDatasetIds = [existingDataset.id];
      this.syncPipelineDatasetSelection();
      this.markPipelineStepComplete('translation');
      return true;
    }

    const name = `${baseName}_dataset.xlsx`;
    const file = await this.createPipelineDatasetFile(name, columns, sourceRows);
    const dataset: PipelineSavedDataset = {
      id: `dataset-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name,
      sourceFileName,
      columns,
      rows: sourceRows.map((row) => ({ ...row })),
      baseColumns: [...columns],
      baseRows: sourceRows.map((row) => ({ ...row })),
      file,
    };
    this.pipelineSavedDatasets = [...this.pipelineSavedDatasets, dataset];
    this.selectedPipelineDatasetIds = [dataset.id];
    this.syncPipelineDatasetSelection();
    this.markPipelineStepComplete('translation');
    return true;
  }

  async savePipelineDatasetFromPreview(): Promise<void> {
    // Loader FIRST — building the .xlsx below blocks for seconds on a big file,
    // so anything after that point leaves the click with no feedback at all.
    this.beginSaveProgress('Preparing');
    // Let the spinner paint before the synchronous xlsx work starts.
    await this.delay(0);

    try {
      const workbookOrder = this.workbookReport?.provisioningOrder ?? [];
      const workflowId = this.datasetService.getLatestWorkflowId();
      if (!workflowId && !workbookOrder.length) {
        this.errorMessage = 'Add a source file before saving the dataset.';
        this.stopSaveProgress();
        return;
      }

      let saved: boolean;
      try {
        saved = await this.savePipelineDataset();
      } catch (error) {
        if (!workflowId) {
          throw error;
        }
        console.warn('Local dataset copy could not be built; running the workflow on the uploaded file.', error);
        saved = true;
      }
      if (!saved) {
        this.errorMessage = 'Analyze and preview a dataset before saving.';
        this.stopSaveProgress();
        return;
      }

      if (workbookOrder.length) {
        this.saveMessage = `Saving dataset and ingesting ${workbookOrder.length} sheet(s)`
          + ` in order: ${workbookOrder.join(' → ')}`;
        this.setSaveProgressVerb('Ingesting');
        const outcome = await firstValueFrom(this.datasetService.runWorkbook(workbookOrder));
        this.workbookIngestion = outcome;
        const failed = outcome?.failedCount ?? 0;
        const workbookMessage = failed
          ? `Dataset saved. ${workbookOrder.length - failed} of ${workbookOrder.length} sheet(s) ingested;`
            + ` ${failed} failed — see the per-sheet result above.`
          : `Dataset saved. All ${workbookOrder.length} sheet(s) ingested.`;
        if (failed) {
          this.errorMessage = workbookMessage;
        } else {
          this.ui.showSuccess(workbookMessage, DataBuilderComponent.WORKFLOW_SNACK_MS);
        }
        this._saveMessage = workbookMessage;
        this.stopSaveProgress();
        return;
      }

      const estimate = this.saveEstimate;
      this.saveMessage = estimate.worthShowing
        ? `Saving ${formatRowCount(estimate.rows)} rows — this usually takes ${formatEstimateRange(estimate)}. Workflow ${workflowId}.`
        : `Saving dataset and running workflow ${workflowId}...`;
      this.setSaveProgressVerb('Saving');
      const response = await firstValueFrom(this.datasetService.runWorkflow(workflowId));
      // Prefer the backend's own wording (e.g. "Workflow '…' triggered
      // successfully. Check logs/status for progress.") over a generic line.
      const message = this.extractWorkflowMessage(response)
        || `Dataset saved successfully. Workflow ${workflowId} started.`;
      this.ui.showSuccess(message, DataBuilderComponent.WORKFLOW_SNACK_MS);
      this._saveMessage = message;
      await this.clearPipelineWorkspaceAfterDatasetSave();
    } catch (error) {
      this.errorMessage = error instanceof Error && error.message
        ? `Unable to save dataset: ${error.message}`
        : 'Unable to save dataset.';
    } finally {
      this.stopSaveProgress();
      this.pipelineSlateClearing = false;
    }
  }

  /** The run-workflow body is plain text, but tolerate a JSON envelope too. */
  private extractWorkflowMessage(response: unknown): string {
    if (typeof response === 'string') {
      const text = response.trim();
      if (!text) return '';
      if (!text.startsWith('{') && !text.startsWith('[')) return text;
      try {
        return this.extractWorkflowMessage(JSON.parse(text));
      } catch {
        return text;
      }
    }
    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      for (const key of ['message', 'status', 'detail', 'description']) {
        const value = record[key];
        if (typeof value === 'string' && value.trim()) return value.trim();
      }
    }
    return '';
  }

  private async clearPipelineWorkspaceAfterDatasetSave(): Promise<void> {
    this.pipelineSlateClearing = true;
    await this.delay(520);

    this.uploadedFiles = [];
    this.hiddenPipelineSourceNames = new Set<string>();
    this.selectedPipelineSourceName = '';
    this.currentFileName = '';
    this.uploadedFileName = '';
    this.pipelineTranslationFiles = [];
    this.openPipelineSourceMenuId = '';
    this.pipelineSourceDeleteInProgress.clear();
    this.datasetService.clearLatestWorkflow();
    this.state.setRawDataset(undefined);
    this.state.setDataset(undefined);
    this.state.setCustomAggregations([]);

    this.pipelineCanvasNodes = [];
    this.pipelineConnections = [];
    this.selectedPipelineNodeId = '';
    this.pipelinePendingStepNode = null;
    this.pipelineSavedDatasets = [];
    this.selectedPipelineDatasetIds = [];
    this.pipelineAppliedSteps = [];
    this.editingPipelineStepId = '';
    this.activePipelineDetailsTab = 'node-details';
    this.pipelineCompletedSteps.clear();
    this.resetTransformerOperations();
    this.resetPipelinePreview();
    this.schedulePipelineCanvasFit();
  }

  private delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  async runPipelineWorkflow(): Promise<void> {
    const workflowId = this.datasetService.getLatestWorkflowId();
    if (!workflowId) {
      this.saveMessage = 'Add a source file before saving the dataset.';
      return;
    }

    try {
      await firstValueFrom(this.datasetService.runWorkflow(workflowId));
      this.saveMessage = `Workflow ${workflowId} started.`;
    } catch (error) {
      this.saveMessage = error instanceof Error && error.message
        ? `Unable to run workflow ${workflowId}: ${error.message}`
        : `Unable to run workflow ${workflowId}.`;
    }
  }

  private async createPipelineDatasetFile(
    name: string,
    columns: string[],
    rows: Array<Record<string, any>>,
  ): Promise<File> {
    const XLSX = await import('xlsx');
    const worksheetRows = rows.map((row) =>
      Object.fromEntries(columns.map((column) => [column, row[column]])),
    );
    const worksheet = XLSX.utils.json_to_sheet(worksheetRows, { header: columns });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
    const workbookBytes = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new File(
      [workbookBytes],
      name,
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
    );
  }

  private async refreshPipelineDatasetFile(dataset: PipelineSavedDataset): Promise<void> {
    dataset.file = await this.createPipelineDatasetFile(dataset.name, dataset.columns, dataset.rows);
  }

  openPipelineTranslationPreview(): void {
    const selectedFiles = this.pipelineSelectedUploadFiles();
    this.pipelineTranslationPreviewOpen = true;
    this.pipelineTranslationPreviewError = '';
    this.pipelineTranslationFiles = selectedFiles;

    if (!selectedFiles.length) {
      this.pipelineTranslationPreviewError = 'Select an uploaded source file before running Translation.';
    }
  }

  togglePipelineCanvasMaximize(force?: boolean): void {
    this.pipelineCanvasMaximized = typeof force === 'boolean' ? force : !this.pipelineCanvasMaximized;
    this.schedulePipelineCanvasFit();
  }

  onPipelineLibraryDragStart(event: DragEvent, item: any): void {
    if (!this.pipelineStepEnabled(item?.id)) {
      event.preventDefault();
      return;
    }
    event.dataTransfer?.setData('application/x-pipeline-step', JSON.stringify(item));
    event.dataTransfer?.setData('text/plain', item?.id || '');
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copy';
  }

  onPipelineNodeDragStart(event: DragEvent, node: any): void {
    if (!node) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('.pipeline-menu-trigger, .pipeline-context-menu')) {
      event.preventDefault();
      return;
    }
    this.draggedPipelineNodeId = node.id;
    const pointer = this.pipelinePointerPositionFromEvent(event);
    this.pipelineNodeDragOffset = {
      x: pointer.x - this.pipelineNodeX(node),
      y: pointer.y - this.pipelineNodeY(node),
    };
    this.selectedPipelineNodeId = node.id;
    event.dataTransfer?.setData('application/x-pipeline-node', node.id);
    event.dataTransfer?.setData('text/plain', node.id);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  }

  onPipelineDragOver(event: DragEvent, targetNodeId = ''): void {
    event.preventDefault();
    this.pipelineCanvasDragActive = true;
    this.dragOverPipelineNodeId = targetNodeId;
    if (this.draggedPipelineNodeId) {
      this.movePipelineNode(
        this.draggedPipelineNodeId,
        this.pipelineDraggedNodePositionFromEvent(event, false),
        false,
      );
    }
    if (event.dataTransfer) event.dataTransfer.dropEffect = this.draggedPipelineNodeId ? 'move' : 'copy';
  }

  onPipelineDragLeave(targetNodeId = ''): void {
    if (!targetNodeId || this.dragOverPipelineNodeId === targetNodeId) {
      this.dragOverPipelineNodeId = '';
    }
  }

  onPipelineCanvasDrop(event: DragEvent): void {
    event.preventDefault();
    this.dropPipelinePayload(event);
  }

  onPipelineDragEnd(): void {
    this.draggedPipelineNodeId = '';
    this.pipelineNodeDragOffset = { x: 0, y: 0 };
    this.dragOverPipelineNodeId = '';
    this.pipelineCanvasDragActive = false;
    this.schedulePipelineCanvasFit();
  }

  addPipelineStepFromPalette(item: any): void {
    const position = this.nextPipelineSlot();
    this.insertPipelineLibraryItem(item, position);
  }

  pipelineNodeX(node: any): number {
    return Number.isFinite(node?.position?.x) ? node.position.x : 0;
  }

  pipelineNodeY(node: any): number {
    return Number.isFinite(node?.position?.y) ? node.position.y : 0;
  }

  handlePipelinePortPointerDown(event: PointerEvent, node: any, side: 'in' | 'out'): void {
    event.preventDefault();
    event.stopPropagation();
    if (!node?.id) return;
    const pointer = this.pipelinePointerPositionFromEvent(event);
    this.activePipelineConnectionDrag = {
      nodeId: node.id,
      side,
      startPointer: pointer,
      pointer,
      hasMoved: false,
    };
    document.body.classList.add('pipeline-connection-dragging');
  }

  handlePipelinePortPointerUp(event: PointerEvent): void {
    if (!this.activePipelineConnectionDrag) return;
    event.preventDefault();
    event.stopPropagation();
    this.finishPipelineConnectionDrag(event);
  }

  @HostListener('document:pointermove', ['$event'])
  onPipelinePointerMove(event: PointerEvent): void {
    this.onPipelinePanelResizeMove(event);
    if (!this.activePipelineConnectionDrag) return;
    const pointer = this.pipelinePointerPositionFromEvent(event);
    this.pipelineConnectionHoverTarget = this.pipelineConnectionTargetFromPoint(event.clientX, event.clientY);
    const distance = Math.hypot(
      pointer.x - this.activePipelineConnectionDrag.startPointer.x,
      pointer.y - this.activePipelineConnectionDrag.startPointer.y,
    );
    this.activePipelineConnectionDrag = {
      ...this.activePipelineConnectionDrag,
      pointer,
      hasMoved: this.activePipelineConnectionDrag.hasMoved || distance > 6,
    };
  }

  handlePipelinePortClick(event: MouseEvent, node: any, side: 'in' | 'out'): void {
    event.stopPropagation();
    if (this.suppressNextPipelinePortClick) {
      this.suppressNextPipelinePortClick = false;
      return;
    }
    if (!node?.id || this.activePipelineConnectionDrag) return;
    this.selectPipelineConnectionPort(node.id, side);
  }

  pipelinePortIsConnecting(node: any, side: 'in' | 'out'): boolean {
    return (this.pendingPipelineConnection?.nodeId === node?.id && this.pendingPipelineConnection?.side === side)
      || (this.activePipelineConnectionDrag?.nodeId === node?.id && this.activePipelineConnectionDrag?.side === side)
      || (this.pipelineConnectionHoverTarget?.nodeId === node?.id && this.pipelineConnectionHoverTarget?.side === side);
  }

  pipelineConnectionLine(connection: {
    from: string;
    to: string;
    fromSide?: 'in' | 'out';
    toSide?: 'in' | 'out';
  }): { x1: number; y1: number; x2: number; y2: number } {
    const fromNode = this.pipelineCanvasNodes.find((node) => node.id === connection.from);
    const toNode = this.pipelineCanvasNodes.find((node) => node.id === connection.to);
    const fromPoint = this.pipelinePortPoint(fromNode, connection.fromSide ?? 'out');
    const toPoint = this.pipelinePortPoint(toNode, connection.toSide ?? 'in');
    return {
      x1: fromPoint.x,
      y1: fromPoint.y,
      x2: toPoint.x,
      y2: toPoint.y,
    };
  }

  pipelinePreviewConnectionLine(): { x1: number; y1: number; x2: number; y2: number } | null {
    const drag = this.activePipelineConnectionDrag;
    if (!drag) return null;
    const node = this.pipelineCanvasNodes.find((candidate) => candidate.id === drag.nodeId);
    const startPoint = this.pipelinePortPoint(node, drag.side);
    const hoverTarget = this.pipelineConnectionHoverTarget;
    const hoverNode = hoverTarget
      ? this.pipelineCanvasNodes.find((candidate) => candidate.id === hoverTarget.nodeId)
      : null;
    const endPoint = hoverNode && hoverTarget && hoverTarget.nodeId !== drag.nodeId
      ? this.pipelinePortPoint(hoverNode, hoverTarget.side)
      : drag.pointer;
    return {
      x1: startPoint.x,
      y1: startPoint.y,
      x2: endPoint.x,
      y2: endPoint.y,
    };
  }

  private dropPipelinePayload(event: DragEvent): void {
    const nodeId = event.dataTransfer?.getData('application/x-pipeline-node') || this.draggedPipelineNodeId;
    const itemPayload = event.dataTransfer?.getData('application/x-pipeline-step') || '';

    if (nodeId) {
      this.movePipelineNode(nodeId, this.pipelineDraggedNodePositionFromEvent(event, true));
    } else if (itemPayload) {
      try {
        const item = JSON.parse(itemPayload);
        if (this.pipelineStepEnabled(item?.id)) {
          this.insertPipelineLibraryItem(item, this.pipelineDropPositionFromEvent(event));
        }
      } catch {
        // Ignore malformed drag payloads from outside the builder.
      }
    }

    this.onPipelineDragEnd();
  }

  private movePipelineNode(nodeId: string, position: { x: number; y: number }, fitCanvas = true): void {
    const currentIndex = this.pipelineCanvasNodes.findIndex((node) => node.id === nodeId);
    if (currentIndex < 0) return;
    const nodes = [...this.pipelineCanvasNodes];
    nodes[currentIndex] = {
      ...nodes[currentIndex],
      position,
    };
    this.pipelineCanvasNodes = nodes;
    if (fitCanvas) this.schedulePipelineCanvasFit();
  }

  private insertPipelineLibraryItem(item: any, position: { x: number; y: number }): void {
    const node = this.createPipelineNode(item, position);
    const nodes = [...this.pipelineCanvasNodes];
    nodes.push(node);
    this.pipelineCanvasNodes = nodes;
    this.schedulePipelineCanvasFit();
  }

  private ensurePipelineActionNode(type: string, updates: Record<string, any> = {}): any {
    let node = this.selectedPipelineNode?.type === type ? this.selectedPipelineNode : null;
    if (node) {
      const nodeIndex = this.pipelineCanvasNodes.findIndex((candidate) => candidate.id === node.id);
      const nodes = [...this.pipelineCanvasNodes];
      nodes[nodeIndex] = { ...node, ...updates };
      this.pipelineCanvasNodes = nodes;
      node = nodes[nodeIndex];
    } else {
      const paletteItem = this.pipelineDashboard?.sourcesPanel?.stepLibrary?.find((item: any) => item.id === type);
      node = {
        ...this.createPipelineNode(
          paletteItem ?? this.pipelinePendingStepNode ?? { id: type, label: type },
          this.nextPipelineSlot(),
        ),
        ...updates,
      };
      this.pipelineCanvasNodes = [...this.pipelineCanvasNodes, node];
      const predecessor = this.pipelineActionPredecessor(type, node.id);
      if (predecessor) {
        this.addPipelineConnection(predecessor.id, 'out', node.id, 'in');
      }
    }
    this.pipelinePendingStepNode = null;
    this.selectedPipelineNodeId = node.id;
    this.schedulePipelineCanvasFit();
    return node;
  }

  private pipelineActionPredecessor(type: string, nodeId: string): any | null {
    const candidates = this.pipelineCanvasNodes.filter((node) => node.id !== nodeId);
    const latestOfType = (...types: string[]) =>
      [...candidates].reverse().find((node) => types.includes(node.type));

    if (type === 'translation') {
      return candidates.find(
        (node) => node.type === 'source' && node.sourceFileName === this.selectedPipelineSourceName,
      ) ?? latestOfType('source') ?? null;
    }
    if (type === 'transformation') return latestOfType('translation', 'source') ?? null;
    if (type === 'relationship') return latestOfType('transformation', 'translation', 'source') ?? null;
    if (type === 'validation') {
      return latestOfType('transformation', 'relationship', 'translation', 'source') ?? null;
    }
    return candidates[candidates.length - 1] ?? null;
  }

  private ensurePipelineSourceNode(fileName: string, rows: number): void {
    const existingIndex = this.pipelineCanvasNodes.findIndex(
      (node) => node.type === 'source' && node.sourceFileName === fileName,
    );
    const summary = `${rows.toLocaleString()} Rows`;
    if (existingIndex >= 0) {
      const nodes = [...this.pipelineCanvasNodes];
      nodes[existingIndex] = { ...nodes[existingIndex], label: fileName, summary };
      this.pipelineCanvasNodes = nodes;
      return;
    }

    const node = {
      id: `source-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type: 'source',
      label: fileName,
      meta: 'Source',
      summary,
      status: 'complete',
      accent: '#2563eb',
      sourceFileName: fileName,
      position: this.nextPipelineSlot(),
    };
    this.pipelineCanvasNodes = [...this.pipelineCanvasNodes, node];
    this.selectedPipelineNodeId = node.id;
    this.pipelinePendingStepNode = null;
    this.schedulePipelineCanvasFit();
  }

  private createPipelineNode(item: any, position: { x: number; y: number }): any {
    const type = item?.id || item?.type || 'step';
    const relationshipTemplate = this.pipelineCanvasNodes.find((node) => node.type === 'relationship')
      ?? this.pipelineDashboard?.designer?.nodes?.find((node: any) => node.type === 'relationship');
    const summaries: Record<string, string> = {
      translation: '2 Dictionaries',
      transformation: '4 Rules',
      relationship: 'Left Join',
      validation: '3 Rules',
    };
    const meta: Record<string, string> = {
      translation: 'Map Values',
      transformation: '4 Rules',
      relationship: 'Sales + Customer',
      validation: '3 Rules',
    };
    return {
      id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type,
      label: item?.label || type,
      meta: meta[type] || item?.description || 'Step',
      summary: summaries[type] || '',
      status: 'complete',
      accent: item?.accent || '#2563eb',
      position,
      popover: type === 'relationship' ? relationshipTemplate?.popover : undefined,
    };
  }

  private pipelineDropPositionFromEvent(event: DragEvent): { x: number; y: number } {
    const flow = this.pipelineFlowElement(event.target as HTMLElement | null) ?? document.querySelector<HTMLElement>('.pipeline-flow');
    const stage = flow?.querySelector<HTMLElement>('.pipeline-canvas-stage');
    const rect = flow?.getBoundingClientRect();
    if (!rect) return this.nextPipelineSlot();

    const nodeWidth = this.pipelineNodeWidth();
    const nodeHeight = 112;
    const grid = 24;
    const stageRect = stage?.getBoundingClientRect();
    const originX = stageRect?.left ?? rect.left;
    const originY = stageRect?.top ?? rect.top;
    const rawX = (event.clientX - originX) / this.pipelineCanvasScale - nodeWidth / 2;
    const rawY = (event.clientY - originY) / this.pipelineCanvasScale - nodeHeight / 2;
    const snappedX = Math.round(rawX / grid) * grid;
    const snappedY = Math.round(rawY / grid) * grid;
    const visibleWidth = Math.max(nodeWidth + 16, (rect.width - 36) / this.pipelineCanvasScale);
    const visibleHeight = Math.max(nodeHeight + 56, (rect.height - 58) / this.pipelineCanvasScale);
    return {
      x: Math.max(8, Math.min(snappedX, Math.max(8, visibleWidth - nodeWidth - 8))),
      y: Math.max(48, Math.min(snappedY, Math.max(48, visibleHeight - nodeHeight - 8))),
    };
  }

  private pipelineDraggedNodePositionFromEvent(event: DragEvent, snapToGrid: boolean): { x: number; y: number } {
    const flow = this.pipelineFlowElement(event.target as HTMLElement | null)
      ?? document.querySelector<HTMLElement>('.pipeline-flow');
    const rect = flow?.getBoundingClientRect();
    if (!rect) return this.nextPipelineSlot();

    const pointer = this.pipelinePointerPositionFromEvent(event);
    const nodeWidth = this.pipelineNodeWidth();
    const nodeHeight = this.pipelineNodeHeight();
    const grid = 24;
    const rawX = pointer.x - this.pipelineNodeDragOffset.x;
    const rawY = pointer.y - this.pipelineNodeDragOffset.y;
    const x = snapToGrid ? Math.round(rawX / grid) * grid : rawX;
    const y = snapToGrid ? Math.round(rawY / grid) * grid : rawY;
    const visibleWidth = Math.max(nodeWidth + 16, (rect.width - 36) / this.pipelineCanvasScale);
    const visibleHeight = Math.max(nodeHeight + 56, (rect.height - 58) / this.pipelineCanvasScale);

    return {
      x: Math.max(8, Math.min(x, Math.max(8, visibleWidth - nodeWidth - 8))),
      y: Math.max(48, Math.min(y, Math.max(48, visibleHeight - nodeHeight - 8))),
    };
  }

  private pipelineFlowElement(element: HTMLElement | null): HTMLElement | null {
    return element?.closest?.('.pipeline-flow') as HTMLElement | null;
  }

  private nextPipelineSlot(): { x: number; y: number } {
    const flow = document.querySelector<HTMLElement>('.pipeline-flow');
    const width = flow?.clientWidth || 760;
    const nodeWidth = this.pipelineNodeWidth();
    const gap = 24;
    const index = this.pipelineCanvasNodes.length;
    const columns = Math.max(1, Math.floor((width - 16) / (nodeWidth + gap)));
    return {
      x: 8 + (index % columns) * (nodeWidth + gap),
      y: 56 + Math.floor(index / columns) * 136,
    };
  }

  private pipelineNodeWidth(): number {
    return 156;
  }

  private pipelineNodeHeight(): number {
    return 106;
  }

  private pipelinePortPoint(node: any, side: 'in' | 'out'): { x: number; y: number } {
    const width = this.pipelineNodeWidth();
    const height = this.pipelineNodeHeight();
    return {
      x: this.pipelineNodeX(node) + (side === 'out' ? width : 0),
      y: this.pipelineNodeY(node) + height / 2,
    };
  }

  private pipelinePointerPositionFromEvent(event: { clientX: number; clientY: number }): { x: number; y: number } {
    const stage = document.querySelector<HTMLElement>('.pipeline-canvas-stage');
    const rect = stage?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: (event.clientX - rect.left) / this.pipelineCanvasScale,
      y: (event.clientY - rect.top) / this.pipelineCanvasScale,
    };
  }

  private finishPipelineConnectionDrag(event: PointerEvent): void {
    const drag = this.activePipelineConnectionDrag;
    this.activePipelineConnectionDrag = null;
    const target = this.pipelineConnectionTargetFromPoint(event.clientX, event.clientY);
    this.pipelineConnectionHoverTarget = null;
    document.body.classList.remove('pipeline-connection-dragging');
    if (!drag?.hasMoved) {
      if (drag) {
        this.selectPipelineConnectionPort(drag.nodeId, drag.side);
        this.suppressNextPipelinePortClick = true;
      }
      return;
    }
    this.suppressNextPipelinePortClick = true;

    if (!target || target.nodeId === drag.nodeId) return;
    this.addPipelineConnection(drag.nodeId, drag.side, target.nodeId, target.side);
  }

  private selectPipelineConnectionPort(nodeId: string, side: 'in' | 'out'): void {
    if (!this.pendingPipelineConnection) {
      this.pendingPipelineConnection = { nodeId, side };
      return;
    }

    const pending = this.pendingPipelineConnection;
    this.pendingPipelineConnection = null;
    if (pending.nodeId === nodeId) return;

    this.addPipelineConnection(pending.nodeId, pending.side, nodeId, side);
  }

  private cancelPipelineConnectionCreation(): void {
    this.activePipelineConnectionDrag = null;
    this.pendingPipelineConnection = null;
    this.pipelineConnectionHoverTarget = null;
    document.body.classList.remove('pipeline-connection-dragging');
  }

  private addPipelineConnection(
    from: string,
    fromSide: 'in' | 'out',
    to: string,
    toSide: 'in' | 'out',
  ): void {
    const exists = this.pipelineConnections.some((connection) =>
      connection.from === from
      && connection.to === to
      && (connection.fromSide ?? 'out') === fromSide
      && (connection.toSide ?? 'in') === toSide);
    if (exists) return;
    this.pipelineConnections = [...this.pipelineConnections, { from, to, fromSide, toSide }];
  }

  private pipelineConnectionTargetFromPoint(
    clientX: number,
    clientY: number,
  ): { nodeId: string; side: 'in' | 'out' } | null {
    const elements = document.elementsFromPoint(clientX, clientY);
    const exactPort = elements.find(
      (element) => element instanceof HTMLElement && element.classList.contains('pipeline-port'),
    ) as HTMLElement | undefined;
    if (exactPort?.dataset?.['nodeId']) {
      return {
        nodeId: exactPort.dataset['nodeId'],
        side: exactPort.dataset['side'] === 'out' ? 'out' : 'in',
      };
    }

    const hoveredNode = elements.find(
      (element) => element instanceof HTMLElement && element.classList.contains('pipeline-node'),
    ) as HTMLElement | undefined;
    if (hoveredNode?.dataset?.['nodeId']) {
      const rect = hoveredNode.getBoundingClientRect();
      return {
        nodeId: hoveredNode.dataset['nodeId'],
        side: Math.abs(clientX - rect.left) <= Math.abs(clientX - rect.right) ? 'in' : 'out',
      };
    }

    const nearbyTargets: Array<{ nodeId: string; side: 'in' | 'out'; distance: number }> = [];
    document.querySelectorAll<HTMLElement>('.pipeline-node[data-node-id]').forEach((nodeElement) => {
      const rect = nodeElement.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      const candidates: Array<{ side: 'in' | 'out'; x: number }> = [
        { side: 'in', x: rect.left },
        { side: 'out', x: rect.right },
      ];
      candidates.forEach((candidate) => {
        const distance = Math.hypot(clientX - candidate.x, clientY - centerY);
        if (distance <= 44) {
          nearbyTargets.push({
            nodeId: nodeElement.dataset['nodeId'] || '',
            side: candidate.side,
            distance,
          });
        }
      });
    });
    const nearest = nearbyTargets.sort((left, right) => left.distance - right.distance)[0];
    return nearest ? { nodeId: nearest.nodeId, side: nearest.side } : null;
  }

  private schedulePipelineCanvasFit(): void {
    window.requestAnimationFrame(() => this.refreshPipelineCanvasFit());
  }

  private refreshPipelineCanvasFit(): void {
    const flow = document.querySelector<HTMLElement>('.pipeline-flow');
    if (!flow) return;
    const bounds = this.calculatePipelineCanvasBounds();
    const availableWidth = Math.max(280, flow.clientWidth - 36);
    const availableHeight = Math.max(160, flow.clientHeight - 58);
    const scale = Math.min(1, availableWidth / bounds.width, availableHeight / bounds.height);
    this.pipelineCanvasBounds = bounds;
    this.pipelineCanvasScale = Math.max(0.5, Math.min(1, scale));
  }

  private calculatePipelineCanvasBounds(): { width: number; height: number } {
    const nodeWidth = this.pipelineNodeWidth();
    const nodeHeight = this.pipelineNodeHeight();
    const maxX = this.pipelineCanvasNodes.reduce((max, node) => Math.max(max, this.pipelineNodeX(node) + nodeWidth + 32), 960);
    const maxY = this.pipelineCanvasNodes.reduce((max, node) => Math.max(max, this.pipelineNodeY(node) + nodeHeight + 32), 360);
    return {
      width: Math.max(420, maxX),
      height: Math.max(220, maxY),
    };
  }

  private clampNumber(value: number, min: number, max: number): number {
    return Math.round(Math.min(max, Math.max(min, value)));
  }

  private pipelineSelectedUploadFiles(): File[] {
    if (this.selectedPipelineSourceName) {
      const selected = this.uploadedFiles.find((entry) => entry.name === this.selectedPipelineSourceName)?.file;
      if (selected) return [selected];
    }
    return this.uploadedFiles.map((entry) => entry.file);
  }

  private pipelineColumnLabel(column: string): string {
    return String(column || '')
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  private pipelineColumnKey(label: string): string {
    return String(label || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  private resetPipelinePreview(): void {
    this.pipelinePreviewColumns = [];
    this.pipelinePreviewRows = [];
    this.pipelineWorkingRows = [];
    this.pipelinePreviewTotalRows = 0;
    this.pipelinePreviewTitle = 'Translation preview';
  }

  private coercePipelinePreviewValue(value: any, dataType: string): any {
    if (value === null || value === undefined || value === '') return value;
    const normalizedType = String(dataType || '').toUpperCase();
    if (normalizedType === 'TEXT') return String(value);
    if (normalizedType === 'INTEGER') {
      const parsed = Number.parseInt(String(value).replace(/[^0-9+-]/g, ''), 10);
      return Number.isFinite(parsed) ? parsed : value;
    }
    if (normalizedType === 'NUMERIC') {
      const parsed = Number(String(value).replace(/[^0-9.+-]/g, ''));
      return Number.isFinite(parsed) ? parsed : value;
    }
    if (normalizedType === 'BOOLEAN') {
      return ['true', '1', 'yes', 'y'].includes(String(value).trim().toLowerCase());
    }
    return value;
  }

  private transformPipelineValue(value: any, config = this.captureTransformerConfig()): any {
    if (value === null || value === undefined) return value;
    const text = String(value);

    if (config.ruleType === 'uppercase') {
      return text.toUpperCase();
    }
    if (config.ruleType === 'substring') {
      const start = Math.max(0, Number.parseInt(config.rangeStart, 10) || 0);
      const configuredEnd = Number.parseInt(config.rangeEnd, 10);
      const end = Number.isFinite(configuredEnd) ? Math.max(start, configuredEnd) : undefined;
      return text.slice(start, end);
    }
    if (config.ruleType === 'validate_range') {
      const numericValue = this.transformerNumber(value);
      const min = this.transformerNumber(config.rangeMin);
      const max = this.transformerNumber(config.rangeMax);
      if (numericValue === null || min === null || max === null || numericValue < min || numericValue > max) {
        return config.defaultValue;
      }
      const precision = Math.min(20, Math.max(0, Number.parseInt(config.decimalPrecision, 10) || 0));
      return Number(numericValue.toFixed(precision));
    }
    if (config.ruleType === 'regex_remove') {
      try {
        return text.replace(new RegExp(config.regex, 'g'), config.replacement);
      } catch {
        return value;
      }
    }
    if (config.ruleType === 'to_array') {
      const delimiter = config.arrayDelimiter || ',';
      return text.split(delimiter).map((entry) => entry.trim()).filter(Boolean);
    }
    if (config.ruleType !== 'trim') return value;

    const transformed = text.trim().replace(/^(["'])(.*)\1$/, '$2').trim();
    return transformed || config.defaultValue;
  }

  private transformPipelineAggregate(
    row: Record<string, any>,
    selectedKeys: string[],
    config = this.captureTransformerConfig(),
  ): any {
    const values = selectedKeys.map((key) => row[key]);
    if (config.ruleType === 'concatenate') {
      return values.filter((value) => value !== null && value !== undefined).join(config.delimiter);
    }
    if (config.ruleType === 'convert_to_json') {
      const aliases = config.jsonAliases
        .split(/[\n,]+/)
        .map((mapping) => {
          const separatorIndex = mapping.indexOf(':');
          return separatorIndex < 0
            ? []
            : [mapping.slice(0, separatorIndex).trim(), mapping.slice(separatorIndex + 1).trim()];
        })
        .filter((mapping) => mapping.length === 2 && mapping[0] && mapping[1]);
      const jsonValue = aliases.length
        ? aliases.reduce<Record<string, any>>((result, [alias, source]) => {
            result[alias] = this.transformerRowValue(row, source);
            return result;
          }, {})
        : selectedKeys.reduce<Record<string, any>>((result, key) => {
            result[key] = row[key];
            return result;
          }, {});
      return JSON.stringify(jsonValue);
    }
    if (config.ruleType === 'auto_generate') {
      const input = values.map((value) => String(value ?? '')).join('|');
      let hash = 2166136261;
      for (let index = 0; index < input.length; index += 1) {
        hash ^= input.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }
      return `AUTO_${(hash >>> 0).toString(16).toUpperCase().padStart(8, '0')}`;
    }

    const literalValues = config.literalValues
      .split(',')
      .map((value) => this.transformerNumber(value.trim()))
      .filter((value): value is number => value !== null);
    const numbers = [
      ...values.map((value) => this.transformerNumber(value)).filter((value): value is number => value !== null),
      ...literalValues,
    ];
    if (!numbers.length) return config.defaultValue;
    if (config.ruleType === 'sum') return numbers.reduce((total, value) => total + value, 0);
    if (config.ruleType === 'difference') return numbers.slice(1).reduce((total, value) => total - value, numbers[0]);
    if (config.ruleType === 'multiply') return numbers.reduce((total, value) => total * value, 1);
    return values[0];
  }

  private captureTransformerConfig(): PipelineTransformerConfig {
    return {
      mapToColumn: this.transformerMapToColumn,
      outputEntity: this.transformerOutputEntity,
      selectedColumns: [...this.transformerSelectedColumns],
      lookupEntities: [...this.transformerLookupEntitySelections],
      lookupKeys: [...this.transformerLookupKeys],
      lookupResultField: this.transformerLookupResultField,
      ruleType: this.transformerRuleType,
      delimiter: this.transformerDelimiter,
      rangeStart: this.transformerRangeStart,
      rangeEnd: this.transformerRangeEnd,
      rangeMin: this.transformerRangeMin,
      rangeMax: this.transformerRangeMax,
      defaultValue: this.transformerDefaultValue,
      decimalPrecision: this.transformerDecimalPrecision,
      regex: this.transformerRegex,
      replacement: this.transformerReplacement,
      arrayDelimiter: this.transformerArrayDelimiter,
      literalValues: this.transformerLiteralValues,
      jsonAliases: this.transformerJsonAliases,
      outputMode: this.transformerOutputMode,
      newColumnName: this.transformerNewColumnName,
    };
  }

  private restoreTransformerConfig(config: PipelineTransformerConfig): void {
    this.transformerOutputEntity = '';
    this.transformerMapToColumn = config.mapToColumn || '';
    this.transformerOutputEntity = config.outputEntity || '';
    this.transformerSelectedColumns = [...config.selectedColumns];
    this.transformerLookupEntitySelections = [...(config.lookupEntities || [])];
    this.transformerLookupEntity = this.transformerLookupEntitySelections[0] ?? '';
    this.transformerRuleType = config.ruleType;
    this.transformerDelimiter = config.delimiter;
    this.transformerRangeStart = config.rangeStart;
    this.transformerRangeEnd = config.rangeEnd;
    this.transformerRangeMin = config.rangeMin;
    this.transformerRangeMax = config.rangeMax;
    this.transformerDefaultValue = config.defaultValue;
    this.transformerDecimalPrecision = config.decimalPrecision;
    this.transformerRegex = config.regex;
    this.transformerReplacement = config.replacement;
    this.transformerArrayDelimiter = config.arrayDelimiter;
    this.transformerLiteralValues = config.literalValues;
    this.transformerJsonAliases = config.jsonAliases;
    this.transformerOutputMode = config.outputMode;
    this.transformerNewColumnName = config.newColumnName;
  }

  private transformationStepSummary(config: PipelineTransformerConfig): string {
    const columns = config.selectedColumns.join(', ');
    const output = config.mapToColumn || config.newColumnName.trim() || (config.outputMode === 'overwrite' ? 'selected columns' : 'new columns');
    return `${columns} -> ${output}`;
  }

  private applyTransformerConfig(
    rows: Array<Record<string, any>>,
    columns: string[],
    config: PipelineTransformerConfig,
  ): { rows: Array<Record<string, any>>; columns: string[] } {
    const nextColumns = [...columns];
    const selectedKeys = config.selectedColumns
      .map((label) => this.pipelineColumnKey(label))
      .filter((key) => nextColumns.includes(key));
    if (!selectedKeys.length) return { rows: rows.map((row) => ({ ...row })), columns: nextColumns };
    const aggregateRules = new Set(['concatenate', 'convert_to_json', 'sum', 'difference', 'multiply', 'auto_generate']);
    const nextRows = rows.map((row) => {
      const nextRow = { ...row };
      if (aggregateRules.has(config.ruleType)) {
        const configuredName = config.mapToColumn.trim() || config.newColumnName.trim();
        const outputKey = configuredName
          ? this.pipelineColumnKey(configuredName)
          : config.outputMode === 'create'
            ? this.pipelineColumnKey(`${selectedKeys[0]}_${config.ruleType}`)
            : selectedKeys[0];
        nextRow[outputKey] = this.transformPipelineAggregate(row, selectedKeys, config);
        if (config.outputMode === 'overwrite' && outputKey !== selectedKeys[0]) {
          delete nextRow[selectedKeys[0]];
          const originalIndex = nextColumns.indexOf(selectedKeys[0]);
          if (originalIndex >= 0) nextColumns.splice(originalIndex, 1);
        }
        if (!nextColumns.includes(outputKey)) nextColumns.push(outputKey);
      } else {
        selectedKeys.forEach((key) => {
          const configuredName = config.mapToColumn.trim() || config.newColumnName.trim();
          const outputKey = configuredName
            ? this.pipelineColumnKey(selectedKeys.length === 1 ? configuredName : `${configuredName}_${key}`)
            : config.outputMode === 'create'
              ? `${key}_${config.ruleType}`
              : key;
          nextRow[outputKey] = this.transformPipelineValue(row[key], config);
          if (config.outputMode === 'overwrite' && outputKey !== key) {
            delete nextRow[key];
            const originalIndex = nextColumns.indexOf(key);
            if (originalIndex >= 0) nextColumns.splice(originalIndex, 1);
          }
          if (!nextColumns.includes(outputKey)) nextColumns.push(outputKey);
        });
      }
      return nextRow;
    });
    return { rows: nextRows, columns: nextColumns };
  }

  private rebuildSelectedPipelineDatasets(): void {
    this.selectedPipelineDatasetIds.forEach((datasetId) => {
      const dataset = this.pipelineSavedDatasets.find((candidate) => candidate.id === datasetId);
      if (!dataset) return;
      let rows = dataset.baseRows.map((row) => ({ ...row }));
      let columns = [...dataset.baseColumns];
      this.pipelineAppliedSteps
        .filter((step) =>
          step.active
          && step.type === 'transformation'
          && step.datasetIds.includes(datasetId)
          && step.config,
        )
        .forEach((step) => {
          const transformed = this.applyTransformerConfig(rows, columns, step.config!);
          rows = transformed.rows;
          columns = transformed.columns;
        });
      dataset.rows = rows;
      dataset.columns = columns;
      void this.refreshPipelineDatasetFile(dataset);
    });
    this.pipelineSavedDatasets = [...this.pipelineSavedDatasets];
    this.syncPipelineDatasetSelection();
  }

  private recordPipelineAppliedStep(
    type: 'relationship' | 'validation',
    label: string,
    summary: string,
  ): void {
    const existing = this.pipelineAppliedSteps.find((step) => step.id === this.editingPipelineStepId);
    if (existing?.type === type) {
      existing.active = false;
      this.pipelineAppliedSteps = [...this.pipelineAppliedSteps];
    }
    this.pipelineAppliedSteps = [
      ...this.pipelineAppliedSteps,
      {
        id: `step-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type,
        label,
        summary,
        appliedAt: new Date(),
        datasetIds: [...this.selectedPipelineDatasetIds],
        active: true,
        supersedesStepId: existing?.type === type ? existing.id : undefined,
      },
    ];
    this.editingPipelineStepId = '';
  }

  private transformerNumber(value: any): number | null {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    const source = String(value).trim();
    const isParenthesizedNegative = /^\(.*\)$/.test(source);
    const normalized = source
      .replace(/^\((.*)\)$/, '$1')
      .replace(/[$€£¥₹,\s]/g, '')
      .replace(/%$/, '');
    if (!/^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i.test(normalized)) return null;
    const numericValue = Number(normalized) * (isParenthesizedNegative ? -1 : 1);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private transformerRowValue(row: Record<string, any>, source: string): any {
    const directKey = this.pipelineColumnKey(source);
    if (Object.prototype.hasOwnProperty.call(row, directKey)) return row[directKey];
    if (Object.prototype.hasOwnProperty.call(row, source)) return row[source];

    const [root, ...path] = source.split('.').map((part) => part.trim()).filter(Boolean);
    if (!root) return undefined;
    let current = row[this.pipelineColumnKey(root)] ?? row[root];
    if (typeof current === 'string' && path.length) {
      try {
        current = JSON.parse(current);
      } catch {
        return undefined;
      }
    }
    for (const segment of path) {
      if (current === null || current === undefined || typeof current !== 'object') return undefined;
      current = current[segment] ?? current[this.pipelineColumnKey(segment)];
    }
    return current;
  }

  private validateTransformerConfig(config: PipelineTransformerConfig, selectedKeys: string[]): string {
    const validRule = this.activeMetadataOperationStep === 'relationship'
      ? this.relationshipOperationTypes.some((rule) => rule.id === config.ruleType)
      : this.activeMetadataOperationStep === 'validation'
        ? this.validationRuleTypes.some((rule) => rule.id === config.ruleType)
        : this.transformerRuleTypes.some((rule) => rule.id === config.ruleType);
    if (!validRule) {
      return `Select a valid ${this.pipelineStepLabel(this.activeMetadataOperationStep).toLowerCase()} rule.`;
    }
    if (config.ruleType === 'concatenate' && selectedKeys.length < 2) {
      return 'Concatenate requires at least two selected columns.';
    }
    const selectedRule = this.transformerRuleTypes.find((rule) => rule.id === config.ruleType);
    if (
      this.activeMetadataOperationStep === 'transformation'
      && selectedRule
      && !selectedRule.supportsMultipleColumns
      && selectedKeys.length !== 1
    ) {
      return `${selectedRule.label} requires exactly one selected column.`;
    }
    if (config.ruleType === 'substring') {
      const start = Number(config.rangeStart);
      const end = Number(config.rangeEnd);
      if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end < start) {
        return 'Substring requires whole-number indexes with the end index greater than or equal to the start index.';
      }
    }
    if (config.ruleType === 'validate_range') {
      const min = this.transformerNumber(config.rangeMin);
      const max = this.transformerNumber(config.rangeMax);
      const precision = Number(config.decimalPrecision);
      if (min === null || max === null || min > max) {
        return 'Validate Range requires a valid minimum that is not greater than the maximum.';
      }
      if (!Number.isInteger(precision) || precision < 0 || precision > 20) {
        return 'Decimal precision must be a whole number from 0 to 20.';
      }
    }
    if (config.ruleType === 'convert_to_json' && config.jsonAliases.trim()) {
      const mappings = config.jsonAliases.split(/[\n,]+/).map((mapping) => mapping.trim()).filter(Boolean);
      if (mappings.some((mapping) => {
        const separatorIndex = mapping.indexOf(':');
        return separatorIndex <= 0 || !mapping.slice(separatorIndex + 1).trim();
      })) {
        return 'JSON aliases must use the format "Output key: Source column".';
      }
    }
    if (['sum', 'difference', 'multiply'].includes(config.ruleType)) {
      const invalidLiteral = config.literalValues
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
        .some((value) => this.transformerNumber(value) === null);
      if (invalidLiteral) return 'Fixed numeric values must contain only valid numbers separated by commas.';
    }
    if (config.ruleType === 'regex_remove') {
      if (!config.regex) return 'Regex Remove requires a regular expression.';
      try {
        void new RegExp(config.regex, 'g');
      } catch {
        return 'Enter a valid regular expression.';
      }
    }
    if (config.ruleType === 'lookup') {
      const lookupEntities = this.transformerLookupEntitySelections.length
        ? this.transformerLookupEntitySelections
        : this.transformerLookupEntity
          ? [this.transformerLookupEntity]
          : [];
      if (!lookupEntities.length) return 'Lookup Entity is required.';
      if (!this.transformerLookupResultField.trim()) return 'Lookup Result Field is required.';
      if (!this.transformerLookupKeys.length) return 'Select at least one Lookup Key.';
    }
    if (this.isRelationshipOperation(config.ruleType) && config.ruleType !== 'lookup' && !config.outputEntity.trim()) {
      return 'Output Entity is required.';
    }
    return '';
  }

  private buildTransformerOperation(selectedKeys: string[]): Record<string, any> {
    const mapToColumn = this.pipelineColumnKey(this.transformerMapToColumn);
    const sourceAttributes = selectedKeys.map((key) => {
      const selectedColumn = this.transformerSelectedColumns.find(
        (column) => this.pipelineColumnKey(column) === key,
      );
      return selectedColumn ? this.pipelineColumnLabel(selectedColumn) : this.pipelineColumnLabel(key);
    });
    const operation: Record<string, any> = {
      step: '1',
      operation: this.isRelationshipOperation(this.transformerRuleType)
        ? this.transformerRuleLabel(this.transformerRuleType).toUpperCase()
        : this.transformerRuleType.toUpperCase(),
      attributes: sourceAttributes,
      output: mapToColumn,
    };

    if (this.transformerRuleType === 'concatenate') operation['operator'] = this.transformerDelimiter;
    if (this.transformerRuleType === 'substring') operation['range'] = [this.transformerRangeStart, this.transformerRangeEnd];
    if (this.transformerRuleType === 'validate_range') {
      operation['range'] = [this.transformerRangeMin, this.transformerRangeMax];
      operation['defaultValue'] = this.transformerDefaultValue;
      operation['decimalPrecision'] = this.transformerDecimalPrecision;
    }
    if (this.transformerRuleType === 'trim') operation['defaultValue'] = this.transformerDefaultValue;
    if (this.transformerRuleType === 'regex_remove') {
      operation['regex'] = this.transformerRegex;
      operation['replacement'] = this.transformerReplacement;
    }
    if (this.transformerRuleType === 'to_array') operation['delimiter'] = this.transformerArrayDelimiter || ',';
    if (['sum', 'difference', 'multiply'].includes(this.transformerRuleType)) {
      const literals = this.transformerLiteralValues.split(',').map((value) => value.trim()).filter(Boolean);
      operation['attributes'] = [...sourceAttributes, ...literals];
    }
    if (this.transformerRuleType === 'convert_to_json' && this.transformerJsonAliases.trim()) {
      operation['attributes'] = this.transformerJsonAliases
        .split(/[\n,]+/)
        .map((mapping) => mapping.trim())
        .filter(Boolean);
    }
    if (this.isRelationshipOperation(this.transformerRuleType)) {
      delete operation['step'];
      operation['attributes'] = this.transformerSelectedColumns.map((column) => this.pipelineColumnLabel(column));
    }
    if (this.transformerRuleType !== 'lookup' && this.isRelationshipOperation(this.transformerRuleType)) {
      operation['outputEntity'] = this.transformerOutputEntity.trim();
      operation['joinType'] = this.relationshipJoinTypeValue(this.transformerRuleType);
    }
    if (this.transformerRuleType === 'lookup') {
      const lookupEntities = this.transformerLookupEntitySelections.length
        ? [...this.transformerLookupEntitySelections]
        : this.transformerLookupEntity.trim()
          ? [this.transformerLookupEntity.trim()]
          : [];
      operation['lookupEntity'] = lookupEntities;
      operation['lookupEntities'] = lookupEntities;
      operation['lookupKeys'] = [...this.transformerLookupKeys];
      operation['lookupResultField'] = this.transformerLookupResultField.trim();
      operation['defaultValue'] = this.transformerDefaultValue;
    }
    return operation;
  }

  private isRelationshipOperation(ruleType: string): boolean {
    return this.relationshipOperationTypes.some((rule) => rule.id === ruleType);
  }

  private isRelationshipJoinRuleType(ruleType: string): ruleType is RelationshipJoinRuleType {
    return Object.prototype.hasOwnProperty.call(this.relationshipJoinTypeByRuleType, ruleType);
  }

  private relationshipJoinTypeValue(ruleType: string): string {
    return this.isRelationshipJoinRuleType(ruleType)
      ? this.relationshipJoinTypeByRuleType[ruleType]
      : '';
  }

  private updateRelationshipDummyJoinTypeSelection(): void {
    const joinType = this.relationshipJoinTypeValue(this.transformerRuleType);
    if (!joinType) return;

    this.updateRelationshipJoinTypes(this.layout, joinType);

    const joinLabel = this.transformerRuleLabel(this.transformerRuleType);
    const detailsNode = this.pipelineDashboard?.detailsPanel?.node;
    if (detailsNode && typeof detailsNode === 'object' && !Array.isArray(detailsNode)) {
      detailsNode['joinType'] = joinLabel;
    }

    const relationshipNode = this.pipelineCanvasNodes.find((node: any) => node?.type === 'relationship');
    if (relationshipNode && typeof relationshipNode === 'object' && !Array.isArray(relationshipNode)) {
      relationshipNode['joinType'] = joinLabel;
      relationshipNode['summary'] = joinLabel;
    }

    try {
      window.localStorage.setItem('dvm.selfService.relationshipDummy.joinType', joinType);
    } catch {
      // Best-effort UI-only state; the browser cannot write public asset files directly.
    }
  }

  private transformerRuleLabel(ruleType: string): string {
    return [...this.transformerRuleTypes, ...this.validationRuleTypes, ...this.relationshipOperationTypes]
      .find((rule) => rule.id === ruleType)?.label
      || ruleType.toUpperCase();
  }

  private async persistTransformerWorkflowTargetMetadata(
    config: PipelineTransformerConfig,
    operationConfig: Record<string, any>,
  ): Promise<void> {
    const latestWorkflowId = this.datasetService.getLatestWorkflowId();
    if (!latestWorkflowId) {
      throw new Error('Add a source file before applying Transformation.');
    }

    if (!this.datasetService.getLatestWorkflowStoreResponse()) {
      await firstValueFrom(this.datasetService.refreshLatestWorkflowStore());
    }

    const update = this.buildTransformerWorkflowTargetMetadataUpdate(config, operationConfig);
    if (!update) {
      throw new Error(`Unable to find workflow target metadata for ${latestWorkflowId}.`);
    }

    await firstValueFrom(this.datasetService.updateWorkflowStore({
      requestData: {
        workflows: update.workflows,
        workflow_id: update.workflowId,
        businessKey: ['workflow_id'],
        updated_by: 'spriced-platform',
        updated_date: this.formatLocalIsoDateTime(new Date()),
      },
      operation: 'update',
      entity: 'workflow_store',
      batchId: 'test',
      workflowId: update.workflowId,
    }));
  }

  private async saveRelationshipWorkflowJson(): Promise<void> {
    const config = this.captureTransformerConfig();
    const selectedKeys = config.selectedColumns
      .map((label) => this.pipelineColumnKey(label))
      .filter(Boolean);
    const validationError = this.validateTransformerConfig(config, selectedKeys);
    if (validationError) {
      this.transformerApplyMessage = validationError;
      return;
    }
    if (!config.mapToColumn.trim()) {
      this.transformerApplyMessage = 'Output / MapTo is required.';
      return;
    }

    try {
      const workflow = this.buildRelationshipWorkflow(config, selectedKeys);
      const operationLabel = this.transformerRuleLabel(this.transformerRuleType);
      this.transformerApplyMessage = 'Sending relationship workflow JSON...';
      await firstValueFrom(this.datasetService.updateWorkflowStore({
        requestData: {
          workflows: workflow,
          is_enabled: true,
          workflow_id: '',
        },
        operation: 'insert',
        responseList: [],
        entity: 'workflow_store',
        batchId: 'test',
        workflowId: 'ABC-123',
        endpoints: [],
        version: '1.0',
      }));
      this.transformerApplyMessage = 'Relationship workflow JSON sent successfully.';
      this.ensurePipelineActionNode('relationship', {
        meta: `${this.relationshipAnchorEntityName(config)} + ${this.relationshipReadModelEntityName(config)}`,
        summary: operationLabel,
        operationConfig: workflow,
      });
      this.markPipelineStepComplete('relationship');
      this.recordPipelineAppliedStep(
        'relationship',
        operationLabel,
        `${config.selectedColumns.join(', ')} -> ${config.mapToColumn}`,
      );
    } catch (error) {
      this.transformerApplyMessage = error instanceof Error && error.message
        ? error.message
        : 'Unable to send relationship workflow JSON.';
    }
  }

  private buildRelationshipWorkflow(
    config: PipelineTransformerConfig,
    selectedKeys: string[],
  ): Record<string, unknown> {
    const anchorTable = this.relationshipAnchorEntityName(config);
    const lookupEntity = this.relationshipReadModelEntityName(config);
    const joinType = this.relationshipJoinTypeValue(config.ruleType) || 'left join';
    const selectedAttributes = Array.from(new Set([
      ...config.selectedColumns.map((column) => this.pipelineColumnKey(column)).filter(Boolean),
      this.pipelineColumnKey(config.lookupResultField || config.mapToColumn),
    ].filter(Boolean)));
    const lookupKeys = config.lookupKeys.length
      ? config.lookupKeys
      : selectedKeys;
    const anchorAttributes = this.relationshipAnchorAttributes(anchorTable);
    const readModelAttributes = Array.from(new Set([
      ...lookupKeys.map((key) => this.pipelineColumnKey(key)).filter(Boolean),
      ...selectedAttributes,
    ]));

    return {
      id: 'PriceProductJoinToEntity',
      updated_date: this.formatLocalIsoDateTime(new Date()),
      workflowName: 'PriceProductJoinToEntity',
      subWorkflowExecution: [
        {
          ttm: {},
          crud: {
            operation: 'insert',
          },
          sinkType: 'GRPC_SINK',
          entityName: this.relationshipSinkEntityName(config),
          sourceType: 'DB_SOURCE',
          serviceName: [],
          SourceConfig: {
            entityName: anchorTable,
            parameters: {
              anchorTable,
              joinObjects: [
                {
                  joinType,
                  readModel: {
                    entityName: lookupEntity,
                    filterList: [
                      {
                        operator: '>',
                        attributeName: 'id',
                        attributeValue: 0,
                        logicalOperator: '',
                      },
                    ],
                    attributesList: readModelAttributes,
                  },
                  readFilter: lookupKeys.map((key) => ({
                    operator: '=',
                    attributeName: this.pipelineColumnKey(key),
                    attributeValue: this.pipelineColumnKey(key),
                  })),
                  selectedAttributes,
                },
              ],
              attributesList: anchorAttributes,
              anchorTableFilters: [
                {
                  operator: '>',
                  attributeName: 'id',
                  attributeValue: 0,
                  logicalOperator: '',
                },
              ],
            },
          },
          referenceEntity: [],
          customServiceName: [],
        },
      ],
    };
  }

  private relationshipAnchorEntityName(config?: PipelineTransformerConfig): string {
    const selectedDataset = this.selectedPipelineDatasets()
      .find((dataset) => !config?.outputEntity || dataset.name !== config.outputEntity)
      ?? this.selectedPipelineDatasets()[0];
    if (selectedDataset?.sourceFileName) {
      return this.pipelineColumnKey(selectedDataset.sourceFileName.replace(/\.[^.]+$/, ''));
    }
    const latestWorkflowId = this.datasetService.getLatestWorkflowId();
    if (latestWorkflowId) return latestWorkflowId;
    return 'price';
  }

  private relationshipReadModelEntityName(config: PipelineTransformerConfig): string {
    const selectedOutputEntity = config.outputEntity.trim();
    const lookupEntity = config.lookupEntities[0] || this.transformerLookupEntity.trim();
    const selectedDataset = this.selectedPipelineDatasets()
      .find((dataset) => dataset.name === selectedOutputEntity)
      ?? this.selectedPipelineDatasets()
        .find((dataset) => dataset.name !== selectedOutputEntity);
    const sourceEntity = selectedDataset?.sourceFileName
      ? this.pipelineColumnKey(selectedDataset.sourceFileName.replace(/\.[^.]+$/, ''))
      : '';
    return this.pipelineColumnKey(selectedOutputEntity.replace(/\.[^.]+$/, ''))
      || lookupEntity
      || sourceEntity
      || 'product';
  }

  private relationshipSinkEntityName(config: PipelineTransformerConfig): string {
    const outputEntity = config.outputEntity.trim();
    return this.isRelationshipJoinRuleType(config.ruleType) && outputEntity
      ? this.pipelineColumnKey(outputEntity.replace(/\.[^.]+$/, ''))
      : this.pipelineColumnKey(config.mapToColumn);
  }

  private relationshipAnchorAttributes(anchorTable: string): string[] {
    const selectedDataset = this.selectedPipelineDatasets()[0];
    const columns = selectedDataset?.columns?.length
      ? selectedDataset.columns
      : this.pipelinePreviewColumns;
    const attributes = columns
      .map((column) => this.pipelineColumnKey(column))
      .filter((column) => column && column !== DataBuilderComponent.ROW_KEY_FIELD);
    return attributes.length ? Array.from(new Set(attributes)) : [anchorTable];
  }

  private buildTransformerWorkflowTargetMetadataUpdate(
    config: PipelineTransformerConfig,
    operationConfig: Record<string, any>,
  ): PipelineWorkflowTargetMetadataUpdate | null {
    const workflowStoreRow = this.resolveWorkflowStoreRow(this.datasetService.getLatestWorkflowStoreResponse());
    const workflows = this.parseWorkflowStoreWorkflows(workflowStoreRow?.['workflows']);
    if (!workflows) return null;

    const clonedWorkflows = this.cloneUnknown(workflows);
    const targetMetadata = this.findWorkflowTargetMetadata(clonedWorkflows);
    const relationshipJoinType = this.relationshipJoinTypeValue(config.ruleType);
    const updatedJoinObjectCount = relationshipJoinType
      ? this.updateRelationshipJoinTypes(clonedWorkflows, relationshipJoinType)
      : 0;
    if (!targetMetadata) {
      return updatedJoinObjectCount
        ? {
            workflows: clonedWorkflows,
            workflowId: String(clonedWorkflows['workflowId'] || clonedWorkflows['id'] || this.datasetService.getLatestWorkflowId()),
          }
        : null;
    }

    const output = String(operationConfig['output'] || '').trim();
    const selectedKeys = (operationConfig['attributes'] as unknown[])
      .map((attribute) => String(attribute || '').trim())
      .filter(Boolean);
    const sourceAttributes = config.selectedColumns.map((column) => this.pipelineColumnLabel(column));
    const dataType = this.transformerOutputDataType(config.ruleType, selectedKeys, targetMetadata);
    const operation = this.cloneUnknown(operationConfig);

    const targetIndexes = this.findTargetMetadataIndexes(targetMetadata, output);
    if (config.outputMode === 'create' || !targetIndexes.length) {
      targetMetadata.push({
        mapTo: [output],
        dataType,
        attributes: sourceAttributes,
        transformations: {
          operations: [operation],
        },
      });
    } else {
      targetIndexes.slice(0, 1).forEach((index) => {
        const target = targetMetadata[index];
        if (!target || typeof target !== 'object' || Array.isArray(target)) return;

        const targetRecord = target as Record<string, unknown>;
        targetRecord['mapTo'] = [output];
        targetRecord['dataType'] = this.transformerOutputDataType(config.ruleType, [output], targetMetadata);
        targetRecord['attributes'] = sourceAttributes;
        targetRecord['transformations'] = {
          operations: [operation],
        };
      });
    }

    return {
      workflows: clonedWorkflows,
      workflowId: String(clonedWorkflows['workflowId'] || clonedWorkflows['id'] || this.datasetService.getLatestWorkflowId()),
    };
  }

  private updateRelationshipJoinTypes(value: unknown, joinType: string): number {
    if (!value || typeof value !== 'object') return 0;

    if (Array.isArray(value)) {
      return value.reduce((total, item) => total + this.updateRelationshipJoinTypes(item, joinType), 0);
    }

    const record = value as Record<string, unknown>;
    let updatedCount = 0;
    const joinObjects = record['joinObjects'];
    if (Array.isArray(joinObjects)) {
      joinObjects.forEach((joinObject) => {
        if (!joinObject || typeof joinObject !== 'object' || Array.isArray(joinObject)) return;
        (joinObject as Record<string, unknown>)['joinType'] = joinType;
        updatedCount += 1;
      });
    }

    for (const childValue of Object.values(record)) {
      if (childValue === joinObjects) continue;
      updatedCount += this.updateRelationshipJoinTypes(childValue, joinType);
    }
    return updatedCount;
  }

  private resolveWorkflowStoreRow(response: unknown): Record<string, unknown> | null {
    if (!response) return null;
    if (Array.isArray(response)) {
      for (const item of response) {
        const match = this.resolveWorkflowStoreRow(item);
        if (match) return match;
      }
      return null;
    }
    if (typeof response !== 'object') return null;

    const record = response as Record<string, unknown>;
    if (this.parseWorkflowStoreWorkflows(record['workflows'])) return record;
    for (const key of ['data', 'result', 'response', 'body', 'items']) {
      const match = this.resolveWorkflowStoreRow(record[key]);
      if (match) return match;
    }
    return null;
  }

  private parseWorkflowStoreWorkflows(value: unknown): Record<string, unknown> | null {
    if (!value) return null;
    if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
    if (typeof value !== 'string') return null;

    try {
      const parsed = JSON.parse(value) as unknown;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed as Record<string, unknown>
        : null;
    } catch {
      return null;
    }
  }

  private findWorkflowTargetMetadata(value: unknown): Array<Record<string, unknown>> | null {
    if (!value) return null;
    if (Array.isArray(value)) {
      for (const item of value) {
        const match = this.findWorkflowTargetMetadata(item);
        if (match) return match;
      }
      return null;
    }
    if (typeof value !== 'object') return null;

    const record = value as Record<string, unknown>;
    if (Array.isArray(record['targetMetadata'])) {
      return record['targetMetadata'] as Array<Record<string, unknown>>;
    }
    for (const childValue of Object.values(record)) {
      const match = this.findWorkflowTargetMetadata(childValue);
      if (match) return match;
    }
    return null;
  }

  private findTargetMetadataIndexes(
    targetMetadata: Array<Record<string, unknown>>,
    mapToColumn: string,
  ): number[] {
    const mapToKey = this.pipelineColumnKey(mapToColumn);
    return targetMetadata
      .map((target, index) => ({ target, index }))
      .filter(({ target }) => {
        const mapTo = Array.isArray(target['mapTo']) ? target['mapTo'] : [];
        return mapTo.some((value) => this.pipelineColumnKey(value) === mapToKey);
      })
      .map(({ index }) => index);
  }

  private transformerOutputDataType(
    ruleType: string,
    selectedKeys: string[],
    targetMetadata: Array<Record<string, unknown>>,
  ): string {
    if (ruleType === 'convert_to_json') return 'json';
    if (ruleType === 'to_array') return 'array';
    if (['sum', 'difference', 'multiply', 'validate_range'].includes(ruleType)) return 'double';

    const selectedKeySet = new Set(selectedKeys.map((key) => this.pipelineColumnKey(key)));
    const matchingTarget = targetMetadata.find((target) => {
      const mapTo = Array.isArray(target['mapTo']) ? target['mapTo'] : [];
      return mapTo.some((value) => selectedKeySet.has(this.pipelineColumnKey(value)));
    });
    return String(matchingTarget?.['dataType'] || 'string');
  }

  private formatLocalIsoDateTime(date: Date): string {
    const pad = (value: number, length = 2): string => String(value).padStart(length, '0');
    const offsetMinutes = -date.getTimezoneOffset();
    const offsetSign = offsetMinutes >= 0 ? '+' : '-';
    const absoluteOffsetMinutes = Math.abs(offsetMinutes);
    const offsetHours = Math.floor(absoluteOffsetMinutes / 60);
    const offsetRemainderMinutes = absoluteOffsetMinutes % 60;

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
      + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
      + `.${pad(date.getMilliseconds(), 3)}`
      + `${offsetSign}${pad(offsetHours)}:${pad(offsetRemainderMinutes)}`;
  }

  private cloneUnknown<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
  }

  private initializePipelineCanvas(layout: BuilderLayoutConfig): void {
    this.pipelineCanvasNodes = [];
    this.pipelineConnections = [];
    this.selectedPipelineNodeId = '';
    this.pipelinePendingStepNode = null;
    this.pipelineSavedDatasets = [];
    this.selectedPipelineDatasetIds = [];
    this.pipelineAppliedSteps = [];
    this.editingPipelineStepId = '';
    this.activePipelineDetailsTab = 'node-details';
    this.pipelineCompletedSteps.clear();
    this.resetPipelinePreview();
    this.schedulePipelineCanvasFit();
  }

  private markPipelineStepComplete(stepId: string): void {
    const stepIndex = this.pipelineStepOrder.indexOf(stepId);
    if (stepIndex < 0) return;
    const prerequisitesComplete = this.pipelineStepOrder
      .slice(0, stepIndex)
      .every((step) => this.pipelineCompletedSteps.has(step));
    if (prerequisitesComplete) this.pipelineCompletedSteps.add(stepId);
  }

  private pipelineStepLabel(stepId: string): string {
    return this.pipelineDashboard?.sourcesPanel?.stepLibrary
      ?.find((item: any) => item.id === stepId)?.label
      || stepId;
  }

  private selectedPipelineDatasets(): PipelineSavedDataset[] {
    return this.pipelineSavedDatasets.filter((dataset) =>
      this.selectedPipelineDatasetIds.includes(dataset.id),
    );
  }

  private syncPipelineDatasetSelection(): void {
    const selected = this.selectedPipelineDatasets();
    this.transformerSelectedFiles = selected.map((dataset) => dataset.name);
    const activeDataset = selected[selected.length - 1];
    if (!activeDataset) return;
    this.pipelinePreviewColumns = [...activeDataset.columns];
    this.pipelineWorkingRows = activeDataset.rows.map((row) => ({ ...row }));
    this.pipelinePreviewRows = activeDataset.rows.slice(0, 10).map((row) => ({ ...row }));
    this.pipelinePreviewTotalRows = activeDataset.rows.length;
    this.pipelinePreviewTitle = `Preview of ${activeDataset.name}`;
    this.transformerSelectedColumns = activeDataset.columns.map((column) =>
      this.pipelineColumnLabel(column),
    );
  }

  private initialPipelineNodePosition(index: number, configuredPosition: any): { x: number; y: number } {
    const fallback = {
      x: 32 + index * 180,
      y: 142,
    };
    if (!configuredPosition) return fallback;
    return {
      x: Math.max(8, Math.round(Number(configuredPosition.x ?? fallback.x) / 24) * 24),
      y: Math.max(48, Math.round(Number(configuredPosition.y ?? fallback.y) / 24) * 24),
    };
  }

  // ── Events the shell handles (cross-stage actions the data-builder requests
  //    but does not own). ────────────────────────────────────────────────────
  /** Ask the shell to switch the active view (e.g. to 'charts'). */
  readonly viewModeChange = output<'data' | 'charts' | 'dashboards'>();
  /** Ask the shell to open the self-service chat. */
  readonly openChat = output<void>();
  /** Ask the shell to start a fresh chart (clears chart-builder state too). */
  readonly startNewChartRequest = output<void>();
  /** A saved chart changed — ask the shell to re-render the dashboard grid. */
  readonly dashboardNeedsRefresh = output<void>();

  // ── Template-facing accessors for shared signal state ───────────────────────
  // Reads/writes of the dataset + raw dataset delegate to the shared state
  // service so the chart-builder reacts via effect()/computed().
  get dataset(): UploadedDataset | undefined { return this.state.dataset(); }
  private get rawDataset(): UploadedDataset | undefined { return this.state.rawDataset(); }

  // Shared with the chart-builder (aggregation picker reads it). No local field.
  get customAggregations(): CustomAggregation[] { return this.state.customAggregations(); }

  // Keep local message fields because the moved template bound `validationMessage`
  // directly; mirror error/success side-effects through the shared UI service.
  validationMessage = '';

  private _errorMessage = '';

  // UI state: show/hide the functions help panel
  showFunctionHelp = false;

  toggleFunctionHelp(): void {
    this.showFunctionHelp = !this.showFunctionHelp;
  }

  // ── Functions reference ──────────────────────────────────────────────────
  // Clickable snippets grouped by category. Clicking a snippet inserts it into
  // the formula editor and selects its first parameter placeholder so the user
  // can immediately fill it (by clicking an available column) or type over it.
  readonly functionReference: { label: string; snippets: string[] }[] = [
    {
      label: 'Aggregates',
      snippets: ['SUM(array)', 'SUM([col])', 'AVERAGE(array)', 'COUNT(array)', 'COUNTIF(array, ">100")', 'weightedAverage(vals, weights)'],
    },
    {
      label: 'Stats',
      snippets: ['min(array)', 'max(array)', 'median(array)', 'percentile(array, 50)', 'VARP(array)', 'VARS(array)', 'STDEVP(array)', 'STDEVS(array)'],
    },
    {
      label: 'Strings',
      snippets: ['concat(a, b)', 'CONCATENATE(...)', 'upper(s)', 'lower(s)', 'trim(s)', 'substr(s, 0, 5)', 'replace(s, "a", "b")', 'contains(s, sub)'],
    },
    {
      label: 'Conditionals',
      snippets: ['IF(cond, trueVal, falseVal)', 'IFERROR(expr, fallback)'],
    },
    {
      label: 'Date parts',
      snippets: ['year(d)', 'month(d)', 'day(d)', 'dayOfWeek(d)'],
    },
    {
      label: 'Math',
      snippets: ['abs(x)', 'round(x)', 'ceil(x)', 'floor(x)', 'sqrt(x)', 'log(x)', 'exp(x)', 'pow(x, y)'],
    },
  ];

  // Parameter placeholders used inside the reference snippets above. Filling one
  // (via a column click or typing) auto-advances the selection to the next.
  private static readonly PLACEHOLDER_TOKENS = new Set([
    'array', 'col', 'vals', 'weights', 's', 'a', 'b', 'sub',
    'x', 'y', 'd', 'cond', 'trueVal', 'falseVal', 'expr', 'fallback',
  ]);
  get errorMessage(): string { return this._errorMessage; }
  set errorMessage(val: string) {
    this._errorMessage = val;
    this.ui.showError(val);
  }

  private _saveMessage = '';
  get saveMessage(): string { return this._saveMessage; }
  set saveMessage(val: string) {
    this._saveMessage = val;
    this.ui.showSuccess(val);
  }

  // JSON-configurable (defaults.previewRows / defaults.maxRows); fall back to
  // the original constants when the config omits them.
  private static readonly MAX_PREVIEW_ROWS_DEFAULT = 5;
  private static readonly MAX_ROWS_FOR_POC_DEFAULT = 10000;
  get maxPreviewRows(): number {
    return this.layout.defaults?.previewRows ?? DataBuilderComponent.MAX_PREVIEW_ROWS_DEFAULT;
  }
  get maxRowsForPoc(): number {
    return this.layout.defaults?.maxRows ?? DataBuilderComponent.MAX_ROWS_FOR_POC_DEFAULT;
  }

  selectedAggregation: AggregationType = 'sum';

  computedColumns: ComputedColumn[] = [];
  rowFilter = '';
  entityFormulas: EntityFormulaDefinition[] = [];
  entityFilters: EntityFilterDefinition[] = [];
  entityFormulaName = '';
  entityFormulaExpression = '';
  entityFormulaError = '';
  editingEntityFormulaIndex: number | null = null;
  entityFilterColumn = '';
  entityFilterOperator = '=';
  entityFilterValue = '';
  entityFilterLogicalOperator = 'AND';
  entityFilterError = '';
  editingEntityFilterIndex: number | null = null;
  entityExecuteMessage = '';
  entityExecuteStatus: 'idle' | 'pending' | 'running' | 'success' | 'error' = 'idle';
  entityExecuting = false;
  entityLastExecutedRows = 0;
  entityLastExecutedColumns = 0;
  // Client compute preview state
  computedPreviewAvailable = false;
  computedPreviewColumns: string[] = [];
  newColumnName = '';
  newColumnExpression = '';
  newColumnError = '';
  rowFilterError = '';
  newAggregationLabel = '';
  newAggregationExpression = '';
  newAggregationError = '';
  computationLibrary: LibraryComputation[] = [];
  showLibraryPanel = false;
  editingComputedColumn: string | null = null;
  editingComputedColumnName = '';
  editingComputedColumnExpression = '';
  editingComputedColumnError = '';
  editingAggregation: string | null = null;
  editingAggregationLabel = '';
  editingAggregationExpression = '';
  editingAggregationError = '';
  viewingVersionsId: string | null = null;
  deamSyncMessage = '';

  private readonly formulaTemplates_DEFAULT: FormulaTemplate[] = [
    { id: 'sum', label: 'Sum', kind: 'aggregation', category: 'Sum', description: 'Sum of a column within each group', defaultName: 'Total', expressionTemplate: 'sum([Column])' },
    { id: 'avg', label: 'Average', kind: 'aggregation', category: 'Average', description: 'Average of a column within each group', defaultName: 'Average', expressionTemplate: 'avg([Column])' },
    { id: 'count', label: 'Count', kind: 'aggregation', category: 'Count', description: 'Row count within each group', defaultName: 'Count', expressionTemplate: 'count([Column])' },
    { id: 'distinct-count', label: 'Distinct Count', kind: 'aggregation', category: 'Count', description: 'Distinct row count within each group', defaultName: 'Distinct count', expressionTemplate: 'distinctCount([Column])' },
    { id: 'pct-of-total', label: 'Percentage', kind: 'aggregation', category: 'Percentage', description: 'Group share as percent', defaultName: 'Share %', expressionTemplate: '(sum([Column]) / sum([TotalColumn])) * 100' },
    { id: 'ratio', label: 'Ratio', kind: 'aggregation', category: 'Ratio', description: 'Ratio of two sums', defaultName: 'Ratio', expressionTemplate: 'sum([Numerator]) / sum([Denominator])' },
    { id: 'derived-margin', label: 'Margin', kind: 'column', category: 'Derived', description: 'Row-level margin %', defaultName: 'Margin', expressionTemplate: '([Profit] / [Revenue]) * 100' },
    { id: 'derived-diff', label: 'Diff', kind: 'column', category: 'Derived', description: 'Row-level difference', defaultName: 'Diff', expressionTemplate: '[ColumnA] - [ColumnB]' },
    { id: 'derived-pct', label: 'Percent of', kind: 'column', category: 'Percentage', description: 'Row-level percentage of two columns', defaultName: 'Percent', expressionTemplate: '([Part] / [Whole]) * 100' },
    { id: 'derived-concat', label: 'Concat', kind: 'column', category: 'Derived', description: 'Concatenate two text columns', defaultName: 'Concat', expressionTemplate: 'concat([First], " - ", [Second])' },
  ];
  private readonly entityFilterOperators_DEFAULT = ['=', '!=', '>', '<', '>=', '<=', 'IN', 'NOT IN', 'LIKE', 'IS NULL', 'IS NOT NULL', 'BETWEEN'];
  private readonly entityLogicalOperators_DEFAULT = ['AND', 'OR'];

  // ── JSON-configurable option lists (fallback to *_DEFAULT) ───────────────
  get formulaTemplates(): FormulaTemplate[] {
    return (this.layout.options?.formulaTemplates as FormulaTemplate[] | undefined) ?? this.formulaTemplates_DEFAULT;
  }
  get entityFilterOperators(): string[] {
    return this.layout.options?.filterOperators ?? this.entityFilterOperators_DEFAULT;
  }
  /** Logical operators (AND/OR) for entity filters (JSON-configurable). */
  get entityLogicalOperators(): string[] {
    return this.layout.options?.logicalOperators ?? this.entityLogicalOperators_DEFAULT;
  }
  /** Columns hidden from the entity preview/dataset (JSON-configurable). */
  get hiddenEntityColumns(): string[] {
    return this.layout.options?.hiddenColumns ?? DataBuilderComponent.HIDDEN_ENTITY_COLUMNS;
  }
  /** Accept attribute for the file-upload input (JSON-configurable). */
  private static readonly ACCEPTED_FILE_TYPES_DEFAULT =
    '.csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';
  get acceptedFileTypes(): string {
    return this.layout.options?.acceptedFileTypes ?? DataBuilderComponent.ACCEPTED_FILE_TYPES_DEFAULT;
  }

  private currentFileName = '';
  /** Name of the uploaded file, shown next to the file input (the native input
   *  text resets to "No file chosen" because we clear input.value to allow
   *  re-uploading the same file). */
  uploadedFileName = '';
  /** Tracks files currently shown as chips in the upload area. */
  uploadedFiles: Array<{ name: string; file: File }> = [];
  entityOptions: string[] = [];
  selectedEntity: string | null = null;
  entitiesLoading = false;
  entitiesErrorMessage = '';

  // ── Schema / Workflow selectors (top-left of the pipeline panel) ─────────
  schemaOptions: string[] = [];
  selectedSchema: string | null = null;
  schemasLoading = false;
  schemasErrorMessage = '';

  /** 'existing' → pick from schemaOptions; 'new' → type a schema name to
   *  create, and the uploaded source file(s) get filed under it. */
  schemaMode: 'existing' | 'new' = 'existing';
  newSchemaName = '';
  /** Surfaced when +Add Source is clicked without a schema resolved yet
   *  (no existing schema picked, or no new schema name typed). */
  schemaModeErrorMessage = '';

  /** The schema name that will be sent with the next source-file upload —
   *  the picked existing schema, or the typed new one. */
  get effectiveSchemaName(): string {
    return (this.schemaMode === 'new' ? this.newSchemaName : this.selectedSchema ?? '').trim();
  }

  setSchemaMode(mode: 'existing' | 'new'): void {
    this.schemaMode = mode;
    this.schemaModeErrorMessage = '';
  }

  /** True once the CURRENT `newSchemaName` has been successfully POSTed to
   *  the backend — reset by onNewSchemaNameChange whenever the user edits the
   *  name, so a changed name gets its own create call rather than silently
   *  reusing the previous one's "already created" state. */
  private newSchemaCreated = false;
  schemaCreating = false;

  onNewSchemaNameChange(): void {
    this.newSchemaCreated = false;
  }

  /** Gate opening the source-file picker on a schema actually being resolved
   *  — either an existing one picked, or a new one named — so a source file
   *  is never uploaded with no schema to file it under. In "new" mode, the
   *  schema is created on the backend first (POST) before the file dialog
   *  opens, per the "Use existing schema OR create + upload into it" flow. */
  openSourceFilePicker(input: HTMLInputElement): void {
    if (this.schemaCreating) return;
    // Schema gate DISABLED (on request): the Schema / Workflow pickers are
    // commented out in the template, so there is no way for the user to resolve
    // a schema and this guard would block the file dialog outright. Restore it
    // together with those pickers.
    // if (!this.effectiveSchemaName) {
    //   this.schemaModeErrorMessage = this.schemaMode === 'new'
    //     ? 'Enter a name for the new schema first.'
    //     : 'Select a schema first.';
    //   return;
    // }
    this.schemaModeErrorMessage = '';

    if (this.schemaMode === 'new' && !this.newSchemaCreated) {
      const projectName = this.newSchemaName.trim();
      this.schemaCreating = true;
      this.datasetService.createSchema(projectName).subscribe({
        next: () => {
          this.schemaCreating = false;
          this.newSchemaCreated = true;
          if (!this.schemaOptions.includes(projectName)) {
            this.schemaOptions = [...this.schemaOptions, projectName];
          }
          input.click();
        },
        error: () => {
          this.schemaCreating = false;
          this.schemaModeErrorMessage = 'Unable to create the new schema. Please try again.';
        },
      });
      return;
    }

    input.click();
  }

  workflowOptions: string[] = [];
  selectedWorkflow: string | null = null;
  workflowsLoading = false;
  workflowsErrorMessage = '';
  entityTableRows: Array<Record<string, unknown>> = [];
  entityTableColumns: string[] = [];
  private originalEntityColumns: DatasetColumn[] = [];
  entityDataLoading = false;
  entityDataErrorMessage = '';
  isEntityPreviewOpen = false;

  // ── Inline data editing (entity mode) ─────────────────────────────────────
  /**
   * DB key column used to identify rows for UPDATE ('uuid' preferred, 'id'
   * fallback). Set only when positively detected from raw rows carrying the
   * field verbatim — never downgraded/nulled by a later call that runs on
   * already-projected rows (see createDatasetFromEntityRows).
   */
  private entityEditKeyColumn: string | null = null;
  /** Dirty cells keyed by the row object itself → { column: newValue }. Keying
   *  by reference (not index) means edits stay correctly attached to their
   *  row even if the dataset is re-sorted/re-filtered/re-projected. */
  private pendingCellEdits = new Map<Record<string, string>, Record<string, string>>();
  savingCellEdits = false;
  cellEditStatusMessage = '';
  cellEditStatus: 'idle' | 'success' | 'error' = 'idle';

  private entityOptionsLoadTimer: ReturnType<typeof setTimeout> | null = null;

  // ── Rich grid editing: selection, keyboard nav, copy/paste, fill, undo ────
  /** Per-column editor kind, computed once per dataset load. */
  columnEditorKind = new Map<string, { kind: 'number' | 'select' | 'text'; options: string[] }>();

  activeCell: GridCellPos | null = null;
  private selectionAnchor: GridCellPos | null = null;
  private selectionFocus: GridCellPos | null = null;
  private isDraggingSelection = false;
  private isDraggingFillHandle = false;
  private fillDragEndRow: number | null = null;

  private undoStack: CellChange[][] = [];
  private redoStack: CellChange[][] = [];

  showFindReplace = false;
  findReplaceColumn = '';
  findReplaceFind = '';
  findReplaceReplace = '';
  findReplaceMessage = '';

  rowActionMessage = '';
  rowActionBusy = false;

  // ── Preview pagination ──────────────────────────────────────────────────────
  /** Renders only one page of rows so 20k+ row datasets never bloat the DOM. */
  previewPage = 0;
  previewPageSize = 50;
  readonly previewPageSizeOptions = [25, 50, 100, 250];

  readonly defaultPreviewColumnWidth = 180;
  readonly minPreviewColumnWidth = 96;
  readonly maxPreviewColumnWidth = 520;
  readonly columnWidths: Record<string, number> = {};
  resizingColumnName: string | null = null;
  private resizeStartX = 0;
  private resizeStartWidth = 0;

  get previewTotalRows(): number {
    return this.dataset?.rows.length ?? 0;
  }

  get previewTotalPages(): number {
    return Math.max(1, Math.ceil(this.previewTotalRows / this.previewPageSize));
  }

  get previewRangeStart(): number {
    return this.previewTotalRows === 0 ? 0 : this.previewPage * this.previewPageSize + 1;
  }

  get previewRangeEnd(): number {
    return Math.min(this.previewTotalRows, (this.previewPage + 1) * this.previewPageSize);
  }

  goToPreviewPage(page: number): void {
    this.previewPage = Math.min(Math.max(0, page), this.previewTotalPages - 1);
  }

  onPreviewPageSizeChange(size: number | string): void {
    const parsed = Number(size);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return;
    }
    // Keep the first visible row in view when the page size changes.
    const firstVisibleRow = this.previewPage * this.previewPageSize;
    this.previewPageSize = parsed;
    this.previewPage = Math.floor(firstVisibleRow / parsed);
  }

  getColumnWidth(columnName: string): number {
    return this.columnWidths[columnName] ?? this.defaultPreviewColumnWidth;
  }

  /** Presentation-only label: `order_total` / `orderTotal` -> `Order Total`. */
  formatColumnLabel(columnName: string): string {
    return columnName
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  onPreviewDropdownOpen(rowIndex: number, colIndex: number, columnName: string, value: string): void {
    const cellId = this.previewDropdownDebugId(rowIndex, colIndex);
    console.info('[DB_PREVIEW_DROPDOWN_V1] OPEN', {
      cellId,
      rowIndex,
      colIndex,
      columnName,
      value,
      optionCount: this.getColumnEditorOptions(columnName).length,
    });

    // ng-select creates its overlay after the open event; capture its final
    // placement on the next task so any layout issue is visible in one log.
    setTimeout(() => {
      const control = document.getElementById(`grid-cell-${rowIndex}-${colIndex}`);
      const panels = document.querySelectorAll('.ng-dropdown-panel');
      const panel = panels.length ? panels.item(panels.length - 1) as HTMLElement : null;
      console.info('[DB_PREVIEW_DROPDOWN_V1] OVERLAY', {
        cellId,
        controlRect: control?.getBoundingClientRect() ?? null,
        panelRect: panel?.getBoundingClientRect() ?? null,
        panelFound: !!panel,
        panelOptionCount: panel?.querySelectorAll('.ng-option').length ?? 0,
      });
    });
  }

  onPreviewDropdownClose(rowIndex: number, colIndex: number, columnName: string): void {
    console.info('[DB_PREVIEW_DROPDOWN_V1] CLOSE', {
      cellId: this.previewDropdownDebugId(rowIndex, colIndex),
      rowIndex,
      colIndex,
      columnName,
    });
  }

  onPreviewDropdownChange(rowIndex: number, colIndex: number, columnName: string, value: string): void {
    console.info('[DB_PREVIEW_DROPDOWN_V1] CHANGE', {
      cellId: this.previewDropdownDebugId(rowIndex, colIndex),
      rowIndex,
      colIndex,
      columnName,
      value,
    });
    this.onPreviewCellEdit(rowIndex, columnName, value);
  }

  private previewDropdownDebugId(rowIndex: number, colIndex: number): string {
    return `db-preview-r${rowIndex}-c${colIndex}`;
  }

  startColumnResize(event: MouseEvent, columnName: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.resizingColumnName = columnName;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.getColumnWidth(columnName);
    document.body.classList.add('preview-column-resizing');
  }

  @HostListener('document:mousemove', ['$event'])
  onColumnResizeMove(event: MouseEvent): void {
    if (!this.resizingColumnName) return;
    const nextWidth = this.resizeStartWidth + event.clientX - this.resizeStartX;
    this.columnWidths[this.resizingColumnName] = this.clampColumnWidth(nextWidth);
  }

  autoFitColumn(event: MouseEvent, columnName: string): void {
    event.preventDefault();
    event.stopPropagation();
    const values = this.previewRows.map((row) => String(row[columnName] ?? ''));
    const longestLength = Math.max(this.formatColumnLabel(columnName).length + 7, ...values.map((value) => value.length));
    this.columnWidths[columnName] = this.clampColumnWidth(longestLength * 7.4 + 32);
  }

  onColumnResizeKeydown(event: KeyboardEvent, columnName: string): void {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    event.stopPropagation();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const step = event.shiftKey ? 24 : 8;
    this.columnWidths[columnName] = this.clampColumnWidth(this.getColumnWidth(columnName) + direction * step);
  }

  private clampColumnWidth(width: number): number {
    return Math.round(Math.min(this.maxPreviewColumnWidth, Math.max(this.minPreviewColumnWidth, width)));
  }

  // ── Template-facing getters ─────────────────────────────────────────────────
  get previewRows(): Record<string, string>[] {
    const rows = this.dataset?.rows || [];
    // Clamp in case the dataset shrank (e.g. after a filter re-execution).
    if (this.previewPage >= this.previewTotalPages) {
      this.previewPage = this.previewTotalPages - 1;
    }
    const start = this.previewPage * this.previewPageSize;
    return rows.slice(start, start + this.previewPageSize);
  }

  get hasDataset(): boolean {
    return !!this.dataset?.rows.length && !!this.dataset?.columns.length;
  }

  get showDataPreview(): boolean {
    if (!this.hasDataset) return false;
    if (this.selectedEntity) {
      return !!this.entityTableRows.length && !!this.entityTableColumns.length;
    }
    if (this.dataset?.sourceType === 'entity') return false;
    return !!this.uploadedFileName;
  }

  get isEntityDatasetMode(): boolean {
    return !!this.selectedEntity;
  }

  get entityFilterRequiresValue(): boolean {
    return !this.formulaService.isEntityNoValueOperator(this.entityFilterOperator);
  }

  get entityAvailableColumns(): DatasetColumn[] {
    const sourceColumns = this.originalEntityColumns.length
      ? this.originalEntityColumns
      : (this.dataset?.columns ?? []);
    const generatedNames = new Set([
      ...this.entityFormulas.map((formula) => formula.name.toLowerCase()),
      ...this.computedColumns.map((column) => column.name.toLowerCase()),
      ...this.customAggregations.flatMap((aggregation) => [aggregation.key, aggregation.label]).map((name) => name.toLowerCase()),
    ]);

    return sourceColumns.filter((column) => {
      const name = column.name.trim();
      if (!name) return false;
      if (column.type !== 'number' && column.type !== 'string') return false;
      const normalized = name.toLowerCase();
      if (generatedNames.has(normalized)) return false;
      if (/\bas\s+[A-Za-z_][A-Za-z0-9_]*$/i.test(name)) return false;
      return !/[+\-*/()]/.test(name);
    });
  }

  get entityFilterValuePlaceholder(): string {
    switch (this.entityFilterOperator) {
      case 'IN':
      case 'NOT IN':
        return 'Value (comma-separated, e.g., Asia, Europe)';
      case 'BETWEEN':
        return 'Two values (comma-separated, e.g., 2024, 2025)';
      case 'LIKE':
        return 'Value (e.g., Asia%)';
      case 'IS NULL':
      case 'IS NOT NULL':
        return 'No value required for this operator';
      default:
        return 'Value';
    }
  }

  // ── Shell-action shims (template calls these names) ──────────────────────────
  setViewMode(mode: 'data' | 'charts' | 'dashboards'): void {
    this.viewModeChange.emit(mode);
  }

  openSelfServiceChat(): void {
    this.openChat.emit();
  }

  startNewChart(): void {
    this.startNewChartRequest.emit();
  }

  ngOnInit(): void {
    this.logPipelineSourceDeleteDebug('component-loaded', {
      location: window.location.href,
      userAgent: navigator.userAgent,
    });
    // Load the JSON layout first; falls back to DEFAULT_LAYOUT on any failure.
    // Entities are loaded INSIDE the subscribe so the config-driven `entityGroup`
    // (options.entityGroup) is in effect before the getAllEntities call fires.
    this.configService
      .loadBuilderLayout('data', DataBuilderComponent.DEFAULT_LAYOUT)
      .subscribe({
        next: (layout) => {
          const validation = this.configService.validateLayout(
            layout,
            DataBuilderComponent.STEP_FIELD_IDS,
          );
          if (validation.unknownFields.length) {
            console.warn(
              '[data-builder] config references unknown field ids; they will be skipped:',
              validation.unknownFields,
            );
          }
          this.layout = layout;
          this.layoutLoading = false;
          this.initializePipelineCanvas(layout);
          this.apiConfig.applyFromLayout(layout);
          this.loadEntityOptions();
          this.loadSchemaOptions();
          // loadWorkflowOptions() disabled — the backend has no workflow-list endpoint yet.
        },
        // If the config request itself errors out (loadBuilderLayout already
        // swallows HTTP errors, but guard anyway), still populate entities with
        // the default group so the dropdown is never left empty.
        error: () => {
          this.layoutLoading = false;
          this.loadEntityOptions();
          this.loadSchemaOptions();
        },
      });
  }

  // ── Layout helpers (template-facing) ─────────────────────────────────────
  // The template keeps each step's body inline (direct <mat-step> content —
  // the reliable Material projection path) and asks these helpers, per known
  // step id, whether to render it and what label to use. Order is fixed in the
  // template; the JSON layout drives visibility + labels (and could be extended
  // to drive order by reordering the template's @if blocks if ever needed).

  /** Find the configured step entry for a given field id. */
  private stepFor(fieldId: string): BuilderStep | undefined {
    return (this.layout.steps ?? []).find((step) =>
      (step.sections ?? []).some((section) =>
        (section.fields ?? []).some((field) => field.field === fieldId),
      ),
    );
  }

  /** Whether the step carrying `fieldId` should render (present in config,
   *  not statically hidden, and its visibleWhen predicate passes). Absent from
   *  config → hidden. */
  isStepVisible(fieldId: string): boolean {
    const step = this.stepFor(fieldId);
    if (!step) return false;
    return step.visible !== false && this.resolveVisibility(step.visibleWhen);
  }

  /** Label for the step carrying `fieldId`, from config (falls back to the id). */
  stepLabel(fieldId: string): string {
    return this.stepFor(fieldId)?.label ?? fieldId;
  }

  /** Resolve a `visibleWhen` predicate key against builder state; unknown/absent
   *  keys default to visible. */
  private resolveVisibility(key: string | undefined): boolean {
    if (!key) return true;
    const predicates: Record<string, () => boolean> = {
      hasDataset: () => this.hasDataset,
    };
    const predicate = predicates[key];
    return predicate ? predicate() : true;
  }

  /** Entity group passed to the backend getAllEntities call (JSON-configurable). */
  private get entityGroup(): string {
    return this.layout.options?.entityGroup ?? 'NontimeSeriesEntities';
  }

  loadEntityOptions(): void {
    this.entitiesLoading = true;
    this.entitiesErrorMessage = '';
    this.datasetService.getEntities(this.entityGroup).subscribe({
      next: (entities) => {
        this.entityOptions = entities;
        if (this.selectedEntity && !entities.includes(this.selectedEntity)) {
          this.selectedEntity = null;
        }
        this.entitiesLoading = false;
      },
      error: () => {
        this.entityOptions = [];
        this.selectedEntity = null;
        this.entitiesLoading = false;
        this.entitiesErrorMessage = 'Unable to load entities.';
      },
    });
  }

  /** Populates the top-left "Schema" dropdown from the backend. */
  loadSchemaOptions(): void {
    this.schemasLoading = true;
    this.schemasErrorMessage = '';
    this.datasetService.getSchemas().subscribe({
      next: (schemas) => {
        this.schemaOptions = schemas;
        if (this.selectedSchema && !schemas.includes(this.selectedSchema)) {
          this.selectedSchema = null;
        }
        this.schemasLoading = false;
      },
      error: () => {
        this.schemaOptions = [];
        this.selectedSchema = null;
        this.schemasLoading = false;
        this.schemasErrorMessage = 'Unable to load schemas.';
      },
    });
  }

  /** Populates the top-left "Workflow" dropdown from the backend. */
  loadWorkflowOptions(): void {
    this.workflowsLoading = true;
    this.workflowsErrorMessage = '';
    this.datasetService.getWorkflows().subscribe({
      next: (workflows) => {
        this.workflowOptions = workflows;
        if (this.selectedWorkflow && !workflows.includes(this.selectedWorkflow)) {
          this.selectedWorkflow = null;
        }
        this.workflowsLoading = false;
      },
      error: () => {
        this.workflowOptions = [];
        this.selectedWorkflow = null;
        this.workflowsLoading = false;
        this.workflowsErrorMessage = 'Unable to load workflows.';
      },
    });
  }

  onSchemaSelectionChange(schemaName: string | null): void {
    this.selectedSchema = schemaName;
  }

  onWorkflowSelectionChange(workflowName: string | null): void {
    this.selectedWorkflow = workflowName;
  }

  private scheduleEntityOptionsLoad(): void {
    if (this.entityOptionsLoadTimer) {
      clearTimeout(this.entityOptionsLoadTimer);
    }

    this.entitiesLoading = true;
    this.entityOptionsLoadTimer = setTimeout(() => {
      this.entityOptionsLoadTimer = null;
      this.loadEntityOptions();
    }, 2000);
  }

  onEntitySelectionChange(entityName: string | null): void {
    this.selectedEntity = entityName;
    this.entityDataErrorMessage = '';
    this.entityExecuteMessage = '';
    this.isEntityPreviewOpen = false;
    this.resetEntityComputationState();
    // Clear the resolved key column so it's freshly (re-)detected from this
    // entity's raw rows below — never carry a stale column name (e.g. 'uuid'
    // from a previously-selected entity) into a different entity's data.
    this.entityEditKeyColumn = null;

    if (!entityName) {
      this.entityTableRows = [];
      this.entityTableColumns = [];
      this.originalEntityColumns = [];
      this.entityDataLoading = false;
      this.state.setRawDataset(undefined);
      this.state.setDataset(undefined);
      this.currentFileName = '';
      return;
    }

    this.entityDataLoading = true;
    this.datasetService.getEntityRows(entityName).subscribe({
      next: (rows) => {
        this.entityTableRows = rows;
        this.entityTableColumns = this.extractVisibleEntityColumns(rows);
        if (rows.length && this.entityTableColumns.length) {
          try {
            // createDatasetFromEntityRows throws when no usable rows remain
            // after projection. Without this try/catch the throw escaped the
            // RxJS next handler, so this.dataset was never set AND no error was
            // shown — the Computations step then wrongly said "upload a file".
            const parsedDataset = this.createDatasetFromEntityRows(rows, entityName);
            this.originalEntityColumns = [...parsedDataset.columns];
            this.state.setRawDataset(parsedDataset);
            this.currentFileName = `entity:${entityName}`;
            this.state.setDataset(this.cloneDataset(parsedDataset));
          } catch (error) {
            this.originalEntityColumns = [];
            this.state.setRawDataset(undefined);
            this.state.setDataset(undefined);
            this.currentFileName = '';
            this.entityDataErrorMessage =
              error instanceof Error ? error.message : 'Unable to load entity data.';
          }
        } else {
          this.originalEntityColumns = [];
          this.state.setRawDataset(undefined);
          this.state.setDataset(undefined);
          this.currentFileName = '';
          this.entityDataErrorMessage = 'The selected entity returned no usable columns or rows.';
        }
        this.entityDataLoading = false;
      },
      error: () => {
        this.entityTableRows = [];
        this.entityTableColumns = [];
        this.originalEntityColumns = [];
        this.entityDataLoading = false;
        this.entityDataErrorMessage = 'Unable to load entity data.';
        this.state.setRawDataset(undefined);
        this.state.setDataset(undefined);
        this.currentFileName = '';
      },
    });
  }

  insertEntityColumn(columnName: string): void {
    const editor = this.entityFormulaEditor?.nativeElement;
    if (!editor) {
      const spacer = this.entityFormulaExpression && !/\s$/.test(this.entityFormulaExpression) ? ' ' : '';
      this.entityFormulaExpression = `${this.entityFormulaExpression}${spacer}${columnName}`;
      return;
    }

    // Names with spaces/operators need bracket syntax so the parser reads them
    // as a single column reference (mirrors the [Column Name] guidance).
    const reference = /[^A-Za-z0-9_]/.test(columnName) ? `[${columnName}]` : columnName;

    const selectionStart = editor.selectionStart ?? this.entityFormulaExpression.length;
    const selectionEnd = editor.selectionEnd ?? selectionStart;
    const prefix = this.entityFormulaExpression.slice(0, selectionStart);
    const suffix = this.entityFormulaExpression.slice(selectionEnd);
    const needsLeadingSpace = !!prefix && !/[\s([*/+\-,]$/.test(prefix);
    const needsTrailingSpace = !!suffix && !/^[\s)\],]/.test(suffix);
    const insertion = `${needsLeadingSpace ? ' ' : ''}${reference}${needsTrailingSpace ? ' ' : ''}`;

    this.entityFormulaExpression = `${prefix}${insertion}${suffix}`;

    // After filling a parameter, jump to the next placeholder (if any) so the
    // user can fill multi-argument snippets like weightedAverage(vals, weights)
    // with successive column clicks.
    const cursor = prefix.length + insertion.length;
    setTimeout(() => {
      editor.focus();
      if (!this.selectPlaceholderFrom(editor, cursor)) {
        editor.setSelectionRange(cursor, cursor);
      }
    });
  }

  // Insert a reference snippet (e.g. "SUM(array)") at the cursor and select its
  // first parameter placeholder, ready to be replaced by a column click.
  insertFunctionSnippet(snippet: string): void {
    const editor = this.entityFormulaEditor?.nativeElement;
    const selectionStart = editor?.selectionStart ?? this.entityFormulaExpression.length;
    const selectionEnd = editor?.selectionEnd ?? selectionStart;
    const prefix = this.entityFormulaExpression.slice(0, selectionStart);
    const suffix = this.entityFormulaExpression.slice(selectionEnd);
    const needsLeadingSpace = !!prefix && !/[\s([*/+\-,]$/.test(prefix);
    const insertion = `${needsLeadingSpace ? ' ' : ''}${snippet}`;

    this.entityFormulaExpression = `${prefix}${insertion}${suffix}`;

    if (!editor) return;
    const snippetStart = prefix.length + insertion.length - snippet.length;
    setTimeout(() => {
      editor.focus();
      if (!this.selectPlaceholderFrom(editor, snippetStart)) {
        const end = snippetStart + snippet.length;
        editor.setSelectionRange(end, end);
      }
    });
  }

  // Find the first known parameter placeholder at/after `from` and select it in
  // the textarea. Returns false when none remain.
  private selectPlaceholderFrom(editor: HTMLTextAreaElement, from: number): boolean {
    const text = this.entityFormulaExpression;
    const tokenRe = /[A-Za-z_][A-Za-z0-9_]*/g;
    tokenRe.lastIndex = Math.max(0, from);
    let match: RegExpExecArray | null;
    while ((match = tokenRe.exec(text))) {
      if (DataBuilderComponent.PLACEHOLDER_TOKENS.has(match[0])) {
        editor.setSelectionRange(match.index, match.index + match[0].length);
        return true;
      }
    }
    return false;
  }

  addEntityFormula(): void {
    const name = this.entityFormulaName.trim();
    const expression = this.entityFormulaExpression.trim();
    this.entityFormulaError = '';

    const validationError = this.validateEntityFormula(name, expression, this.editingEntityFormulaIndex);
    if (validationError) {
      this.entityFormulaError = validationError;
      return;
    }

    const next = { name, expression };
    if (this.editingEntityFormulaIndex === null) {
      this.entityFormulas = [...this.entityFormulas, next];
    } else {
      this.entityFormulas = this.entityFormulas.map((formula, index) => (
        index === this.editingEntityFormulaIndex ? next : formula
      ));
    }

    this.cancelEntityFormulaEdit();
    this.markEntityExecutionPending();
  }

  editEntityFormula(index: number): void {
    const formula = this.entityFormulas[index];
    if (!formula) {
      return;
    }
    this.editingEntityFormulaIndex = index;
    this.entityFormulaName = formula.name;
    this.entityFormulaExpression = formula.expression;
    this.entityFormulaError = '';
  }

  deleteEntityFormula(index: number): void {
    this.entityFormulas = this.entityFormulas.filter((_, formulaIndex) => formulaIndex !== index);
    if (this.editingEntityFormulaIndex === index) {
      this.cancelEntityFormulaEdit();
    } else if (this.editingEntityFormulaIndex !== null && this.editingEntityFormulaIndex > index) {
      this.editingEntityFormulaIndex -= 1;
    }
    this.markEntityExecutionPending();
  }

  cancelEntityFormulaEdit(): void {
    this.editingEntityFormulaIndex = null;
    this.entityFormulaName = '';
    this.entityFormulaExpression = '';
    this.entityFormulaError = '';
  }

  addEntityFilter(): void {
    const attributeName = this.entityFilterColumn.trim();
    const operator = this.entityFilterOperator.trim();
    this.entityFilterError = '';

    const attributeValue = this.formulaService.parseEntityFilterValues(operator, this.entityFilterValue);
    const validationError = this.validateEntityFilter(attributeName, operator, attributeValue);
    if (validationError) {
      this.entityFilterError = validationError;
      return;
    }

    const next: EntityFilterDefinition = {
      attributeName,
      operator,
      attributeValue,
      logicalOperator: this.entityFilterLogicalOperator,
    };

    if (this.editingEntityFilterIndex === null) {
      this.entityFilters = [...this.entityFilters, next];
    } else {
      this.entityFilters = this.entityFilters.map((filter, index) => (
        index === this.editingEntityFilterIndex ? next : filter
      ));
    }

    this.cancelEntityFilterEdit();
    this.markEntityExecutionPending();
  }

  editEntityFilter(index: number): void {
    const filter = this.entityFilters[index];
    if (!filter) {
      return;
    }
    this.editingEntityFilterIndex = index;
    this.entityFilterColumn = filter.attributeName;
    this.entityFilterOperator = filter.operator;
    this.entityFilterValue = filter.attributeValue != null ? filter.attributeValue.join(', ') : '';
    this.entityFilterLogicalOperator = filter.logicalOperator || 'AND';
    this.entityFilterError = '';
  }

  deleteEntityFilter(index: number): void {
    this.entityFilters = this.entityFilters.filter((_, filterIndex) => filterIndex !== index);
    if (this.editingEntityFilterIndex === index) {
      this.cancelEntityFilterEdit();
    } else if (this.editingEntityFilterIndex !== null && this.editingEntityFilterIndex > index) {
      this.editingEntityFilterIndex -= 1;
    }
    this.markEntityExecutionPending();
  }

  cancelEntityFilterEdit(): void {
    this.editingEntityFilterIndex = null;
    this.entityFilterColumn = '';
    this.entityFilterOperator = '=';
    this.entityFilterValue = '';
    this.entityFilterLogicalOperator = 'AND';
    this.entityFilterError = '';
  }

  onEntityFilterOperatorChange(operator: string): void {
    this.entityFilterOperator = operator;
    if (this.formulaService.isEntityNoValueOperator(operator)) {
      this.entityFilterValue = '';
    }
  }

  private markEntityExecutionPending(): void {
    this.entityExecuteStatus = 'pending';
    this.entityExecuteMessage = 'Saved changes are ready. Execute to refresh the dataset.';
    this.entityDataErrorMessage = '';
  }

  async executeEntityComputations(): Promise<void> {
    if (!this.selectedEntity) {
      this.entityDataErrorMessage = 'Select an entity before executing formulas.';
      this.entityExecuteStatus = 'error';
      this.entityExecuteMessage = '';
      return;
    }

    this.entityDataErrorMessage = '';
    this.entityExecuteMessage = 'Executing formulas and row filters...';
    this.entityExecuteStatus = 'running';
    this.entityExecuting = true;
    this.entityDataLoading = true;

    try {
      const computedRows = await firstValueFrom(this.datasetService.executeEntityQuery({
        entityName: this.selectedEntity,
        attributesList: this.buildEntityAttributesList(),
        filterList: this.buildEntityFilterPayload(),
      }));

      // The backend computes ONLY the formula columns (its `attributesList`
      // contract). Sending base columns there makes it 500, so we merge the
      // existing entity rows with the computed columns here instead — otherwise
      // the table would show just the new computed column.
      const rows = this.mergeComputedColumns(computedRows);

      const visibleColumns = this.extractVisibleEntityColumns(rows);

      if (rows.length && visibleColumns.length) {
        // createDatasetFromEntityRows can throw if every row is blank after
        // projecting to the visible columns — catch it so we surface a clear
        // message and KEEP the existing dataset instead of wiping it.
        const parsedDataset = this.createDatasetFromEntityRows(rows, this.selectedEntity);
        this.entityTableRows = rows;
        this.entityTableColumns = visibleColumns;
        this.state.setRawDataset(parsedDataset);
        this.state.setDataset(this.cloneDataset(parsedDataset));
        this.entityLastExecutedRows = rows.length;
        this.entityLastExecutedColumns = visibleColumns.length;
        this.entityExecuteStatus = 'success';
        this.entityExecuteMessage = `Execution complete. Loaded ${rows.length} rows and ${visibleColumns.length} columns.`;
        setTimeout(() => { if (this.dataStepper) this.dataStepper.selectedIndex = 0; }, 400);
      } else {
        // The computed query returned nothing usable. Do NOT blank the dataset
        // the user already loaded — that would bounce the Computations step back
        // to the "upload a file" empty state even though data is loaded. Keep it
        // and tell the user the formulas/filters produced no rows.
        this.entityDataErrorMessage =
          'The formulas and filters returned no data. Adjust them and execute again — your loaded dataset is unchanged.';
        this.entityExecuteStatus = 'error';
        this.entityExecuteMessage = '';
      }
    } catch (error) {
      // Keep the existing dataset on failure; just report what went wrong.
      this.entityDataErrorMessage = error instanceof Error ? error.message : 'Unable to execute entity query.';
      this.entityExecuteStatus = 'error';
      this.entityExecuteMessage = '';
    } finally {
      this.entityExecuting = false;
      this.entityDataLoading = false;
    }
  }

  /** Compute formulas locally (client-side) and update the preview dataset temporarily. */
  async computeEntityPreview(): Promise<void> {
    if (!this.dataset || !this.dataset.rows.length) return;
    // Ensure optional formulajs is loaded
    await this.formulaService.ensureFormulajs();

    const rows = this.cloneDataset(this.dataset).rows as Record<string, string>[];
    const columnNames = this.dataset.columns.map((c) => c.name);

    const allFormulas = [
      ...this.entityFormulas.map((f) => ({ name: f.name, expression: f.expression })),
      ...this.computedColumns.map((c) => ({ name: c.name, expression: c.expression })),
    ];

    // Build evaluators for each formula
    const evaluators = allFormulas.map((f) => ({ name: f.name, fn: this.formulaService.buildRowEvaluator(f.expression, columnNames) }));

    for (const r of rows) {
      for (const ev of evaluators) {
        try {
          // evaluator expects string values in row
          const res = ev.fn(r as Record<string, string>);
          r[ev.name] = res as unknown as string;
        } catch (e) {
          r[ev.name] = '';
        }
      }
    }

    // Apply to preview dataset without persisting
    const parsedDataset = this.createDatasetFromEntityRows(rows, this.selectedEntity ?? '');
    this.state.setRawDataset(parsedDataset);
    this.state.setDataset(this.cloneDataset(parsedDataset));
    this.computedPreviewAvailable = true;
    this.computedPreviewColumns = evaluators.map((e) => e.name);
    this.entityExecuteStatus = 'success';
    this.entityExecuteMessage = `Computed preview for ${rows.length} rows.`;
  }

  /** Persist the last client-side computed preview values to the entity table. */
  async saveComputedPreview(): Promise<void> {
    if (!this.selectedEntity || !this.entityEditKeyColumn || !this.dataset || !this.dataset.rows.length) return;
    if (!this.computedPreviewAvailable || !this.computedPreviewColumns.length) return;

    const rows = this.dataset.rows as Array<Record<string, unknown>>;
    const rowUpdates: Array<{ keyValue: unknown; changes: Record<string, unknown> }> = [];
    for (const row of rows) {
      const key = row[DataBuilderComponent.ROW_KEY_FIELD];
      if (!key) continue;
      const changes: Record<string, unknown> = {};
      for (const col of this.computedPreviewColumns) {
        if (col in row) changes[col] = row[col] ?? null;
      }
      if (Object.keys(changes).length) rowUpdates.push({ keyValue: key, changes });
    }

    if (!rowUpdates.length) {
      this.entityDataErrorMessage = 'No computed values available to save.';
      return;
    }

    this.entityExecuting = true;
    try {
      const resp = await firstValueFrom(this.datasetService.persistComputedColumnValues({
        entityName: this.selectedEntity,
        keyColumn: this.entityEditKeyColumn,
        rows: rowUpdates,
      }));

      if (resp?.status === 'SUCCESS') {
        this.saveMessage = `Saved ${resp.rowsUpdated ?? rowUpdates.length} computed value(s) to ${this.selectedEntity}.`;
        // Refresh the entity to reflect persisted values
        this.onEntitySelectionChange(this.selectedEntity);
        this.computedPreviewAvailable = false;
      } else {
        this.entityDataErrorMessage = resp?.message || 'Server rejected computed values.';
      }
    } catch (e) {
      this.entityDataErrorMessage = e instanceof Error ? e.message : 'Unable to persist computed values.';
    } finally {
      this.entityExecuting = false;
    }
  }

  /**
   * Merge the backend's computed-only rows back onto the entity's existing
   * rows so the table keeps every original column plus the new computed
   * column(s). The backend returns computed values in the entity's row order;
   * we zip them positionally onto the base rows. If the computed result is a
   * filtered subset (row counts differ) we can't safely align it, so we fall
   * back to the computed rows alone rather than mis-pairing values.
   */
  private mergeComputedColumns(
    computedRows: Array<Record<string, unknown>>,
  ): Array<Record<string, unknown>> {
    const baseRows = this.entityTableRows;

    // No base rows captured, or the formula query was effectively a re-select
    // (already carries the base columns) → nothing to merge onto.
    if (!baseRows.length || !computedRows.length) {
      return computedRows;
    }

    // Counts must line up for a positional merge to be correct.
    if (baseRows.length !== computedRows.length) {
      return computedRows;
    }

    // Only carry over keys the computed row actually adds (the formula
    // aliases) so we don't clobber base columns with stale/empty values.
    return baseRows.map((baseRow, index) => ({
      ...baseRow,
      ...computedRows[index],
    }));
  }

  // ── Inline data editing ─────────────────────────────────────────────────────

  /** Editing is possible only for entity datasets whose rows carry a DB key. */
  get canEditPreviewData(): boolean {
    return this.isEntityDatasetMode && !!this.entityEditKeyColumn;
  }

  get pendingCellEditCount(): number {
    let count = 0;
    this.pendingCellEdits.forEach((changes) => { count += Object.keys(changes).length; });
    return count;
  }

  get hasPendingCellEdits(): boolean {
    return this.pendingCellEdits.size > 0;
  }

  /** Key columns identify the row in the DB — never editable. */
  isEditableColumn(columnName: string): boolean {
    const normalized = columnName.toLowerCase();
    return normalized !== 'id'
      && normalized !== 'uuid'
      && normalized !== DataBuilderComponent.ROW_KEY_FIELD.toLowerCase()
      && normalized !== (this.entityEditKeyColumn ?? '').toLowerCase();
  }

  onPreviewCellEdit(rowIndex: number, columnName: string, value: string): void {
    this.setCellValueDirect(rowIndex, columnName, value);
    this.cellEditStatus = 'idle';
    this.cellEditStatusMessage = '';
  }

  async savePreviewEdits(): Promise<void> {
    if (!this.selectedEntity || !this.entityEditKeyColumn || !this.hasPendingCellEdits || this.savingCellEdits) {
      return;
    }

    const rowUpdates: Array<{ keyValue: unknown; changes: Record<string, unknown> }> = [];
    this.pendingCellEdits.forEach((changes, row) => {
      const keyValue = row[DataBuilderComponent.ROW_KEY_FIELD];
      if (keyValue && Object.keys(changes).length) {
        rowUpdates.push({ keyValue, changes });
      }
    });

    if (!rowUpdates.length) {
      this.cellEditStatus = 'error';
      this.cellEditStatusMessage = 'The edited rows have no database key, so they cannot be saved.';
      return;
    }

    this.savingCellEdits = true;
    this.cellEditStatus = 'idle';
    this.cellEditStatusMessage = 'Saving changes to the database...';

    try {
      const response = await firstValueFrom(this.datasetService.updateEntityRows({
        entityName: this.selectedEntity,
        keyColumn: this.entityEditKeyColumn,
        rows: rowUpdates,
      }));

      if (response?.status === 'SUCCESS') {
        // Mirror the edits onto the raw entity rows so formula execution and
        // chart building see the updated values without a refetch.
        this.pendingCellEdits.forEach((changes, row) => {
          const keyValue = row[DataBuilderComponent.ROW_KEY_FIELD];
          const entityRow = this.entityTableRows.find(
            (candidate) => String(candidate[this.entityEditKeyColumn as string]) === keyValue,
          );
          if (entityRow) {
            Object.entries(changes).forEach(([column, value]) => { entityRow[column] = value; });
          }
        });
        this.pendingCellEdits.clear();
        this.cellEditStatus = 'success';
        this.cellEditStatusMessage = `Saved ${response.rowsUpdated ?? rowUpdates.length} row(s) to ${this.selectedEntity}.`;
      } else {
        this.cellEditStatus = 'error';
        this.cellEditStatusMessage = response?.message || 'The server rejected the update.';
      }
    } catch (error) {
      this.cellEditStatus = 'error';
      this.cellEditStatusMessage = error instanceof Error ? error.message : 'Unable to save changes to the database.';
    } finally {
      this.savingCellEdits = false;
    }
  }

  discardPreviewEdits(): void {
    if (!this.hasPendingCellEdits || this.savingCellEdits) {
      return;
    }
    this.pendingCellEdits.clear();
    this.undoStack = [];
    this.redoStack = [];
    this.activeCell = null;
    this.selectionAnchor = null;
    this.selectionFocus = null;
    this.cellEditStatus = 'idle';
    this.cellEditStatusMessage = '';
    // Reload the entity so the table reverts to the persisted values.
    if (this.selectedEntity) {
      this.onEntitySelectionChange(this.selectedEntity);
    }
  }

  // ── Rich grid: selection, keyboard nav, copy/paste, fill-down, undo/redo ────

  absoluteRowIndex(pageRelativeIndex: number): number {
    return this.previewPage * this.previewPageSize + pageRelativeIndex;
  }

  getColumnEditorKind(columnName: string): 'number' | 'select' | 'text' {
    return this.columnEditorKind.get(columnName)?.kind ?? 'text';
  }

  getColumnEditorOptions(columnName: string): string[] {
    return this.columnEditorKind.get(columnName)?.options ?? [];
  }

  private computeColumnEditorKinds(
    columnNames: string[],
    rows: Record<string, string>[],
  ): Map<string, { kind: 'number' | 'select' | 'text'; options: string[] }> {
    const MAX_DROPDOWN_OPTIONS = 12;
    const result = new Map<string, { kind: 'number' | 'select' | 'text'; options: string[] }>();

    columnNames.forEach((columnName) => {
      const values = rows.map((row) => row[columnName] ?? '').filter((value) => value !== '');
      if (!values.length) {
        result.set(columnName, { kind: 'text', options: [] });
        return;
      }

      const allNumeric = values.every((value) => Number.isFinite(Number(value.replace(/,/g, '').trim())));
      if (allNumeric) {
        result.set(columnName, { kind: 'number', options: [] });
        return;
      }

      const distinct = Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
      if (distinct.length > 1 && distinct.length <= MAX_DROPDOWN_OPTIONS) {
        result.set(columnName, { kind: 'select', options: distinct });
        return;
      }

      result.set(columnName, { kind: 'text', options: [] });
    });

    return result;
  }

  /** Single write primitive — every edit path (typing, paste, fill, find/replace, undo/redo) goes through this. */
  private setCellValueDirect(rowIndex: number, columnName: string, newValue: string): CellChange | null {
    const rows = this.dataset?.rows;
    if (!rows || rowIndex < 0 || rowIndex >= rows.length || !this.isEditableColumn(columnName)) {
      return null;
    }
    const row = rows[rowIndex];
    const oldValue = row[columnName] ?? '';
    if (oldValue === newValue) {
      return null;
    }
    row[columnName] = newValue;
    const changes = this.pendingCellEdits.get(row) ?? {};
    changes[columnName] = newValue;
    this.pendingCellEdits.set(row, changes);
    return { row: rowIndex, column: columnName, oldValue, newValue };
  }

  // ── Selection ──────────────────────────────────────────────────────────────

  private setActiveCell(row: number, col: number, extendSelection = false): void {
    this.activeCell = { row, col };
    if (!extendSelection || !this.selectionAnchor) {
      this.selectionAnchor = { row, col };
    }
    this.selectionFocus = { row, col };
  }

  private getSelectionRect(): { minRow: number; maxRow: number; minCol: number; maxCol: number } | null {
    if (!this.selectionAnchor || !this.selectionFocus) {
      return null;
    }
    return {
      minRow: Math.min(this.selectionAnchor.row, this.selectionFocus.row),
      maxRow: Math.max(this.selectionAnchor.row, this.selectionFocus.row),
      minCol: Math.min(this.selectionAnchor.col, this.selectionFocus.col),
      maxCol: Math.max(this.selectionAnchor.col, this.selectionFocus.col),
    };
  }

  private getSelectionSize(): number {
    const rect = this.getSelectionRect();
    if (!rect) return 1;
    return (rect.maxRow - rect.minRow + 1) * (rect.maxCol - rect.minCol + 1);
  }

  isCellInSelection(rowIndex: number, colIndex: number): boolean {
    const rect = this.getSelectionRect();
    return !!rect && rowIndex >= rect.minRow && rowIndex <= rect.maxRow && colIndex >= rect.minCol && colIndex <= rect.maxCol;
  }

  isCellActive(rowIndex: number, colIndex: number): boolean {
    return this.activeCell?.row === rowIndex && this.activeCell?.col === colIndex;
  }

  /** The fill handle renders only at the bottom-right corner of the current selection. */
  isFillHandleCell(rowIndex: number, colIndex: number): boolean {
    const rect = this.getSelectionRect();
    return !!rect && rowIndex === rect.maxRow && colIndex === rect.maxCol;
  }

  onCellMouseDown(event: MouseEvent, rowIndex: number, colIndex: number): void {
    if (this.isDraggingFillHandle) {
      return;
    }
    this.isDraggingSelection = true;
    this.setActiveCell(rowIndex, colIndex, event.shiftKey);
  }

  onCellMouseEnter(rowIndex: number, colIndex: number): void {
    if (this.isDraggingSelection) {
      this.selectionFocus = { row: rowIndex, col: colIndex };
    } else if (this.isDraggingFillHandle) {
      this.fillDragEndRow = rowIndex;
    }
  }

  onFillHandleMouseDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingFillHandle = true;
    this.fillDragEndRow = this.getSelectionRect()?.maxRow ?? null;
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    if (this.resizingColumnName) {
      this.resizingColumnName = null;
      document.body.classList.remove('preview-column-resizing');
    }
    this.isDraggingSelection = false;
    if (this.isDraggingFillHandle) {
      this.applyFillDown();
      this.isDraggingFillHandle = false;
      this.fillDragEndRow = null;
    }
  }

  /** Fills every row below the current selection (down to the drag-release row) with the top row's value(s), per column. */
  private applyFillDown(): void {
    const rect = this.getSelectionRect();
    const columns = this.dataset?.columns;
    const rows = this.dataset?.rows;
    if (!rect || !columns || !rows || this.fillDragEndRow === null || this.fillDragEndRow <= rect.maxRow) {
      return;
    }

    const changes: CellChange[] = [];
    for (let col = rect.minCol; col <= rect.maxCol; col++) {
      const columnName = columns[col]?.name;
      if (!columnName) continue;
      const sourceValue = rows[rect.minRow]?.[columnName] ?? '';
      for (let row = rect.maxRow + 1; row <= this.fillDragEndRow; row++) {
        const change = this.setCellValueDirect(row, columnName, sourceValue);
        if (change) changes.push(change);
      }
    }

    if (changes.length) {
      this.pushUndo(changes);
      this.cellEditStatus = 'idle';
      this.cellEditStatusMessage = '';
    }
    this.selectionFocus = { row: this.fillDragEndRow, col: rect.maxCol };
  }

  // ── Keyboard navigation ──────────────────────────────────────────────────────

  private editSessionPos: GridCellPos | null = null;
  private editSessionOriginalValue: string | null = null;

  private markActiveCellOnly(row: number, col: number): void {
    this.activeCell = { row, col };
  }

  onCellFocus(rowIndex: number, colIndex: number): void {
    this.markActiveCellOnly(rowIndex, colIndex);
    const columns = this.dataset?.columns;
    const rows = this.dataset?.rows;
    if (!columns?.[colIndex] || !rows?.[rowIndex]) return;
    this.editSessionPos = { row: rowIndex, col: colIndex };
    this.editSessionOriginalValue = rows[rowIndex][columns[colIndex].name] ?? '';
  }

  onCellBlur(): void {
    this.commitEditSession();
  }

  /** Turns a focus→blur edit session into a single undo entry (not one per keystroke). */
  private commitEditSession(): void {
    const pos = this.editSessionPos;
    const originalValue = this.editSessionOriginalValue;
    this.editSessionPos = null;
    this.editSessionOriginalValue = null;
    if (!pos || originalValue === null) return;

    const columns = this.dataset?.columns;
    const rows = this.dataset?.rows;
    if (!columns?.[pos.col] || !rows?.[pos.row]) return;

    const columnName = columns[pos.col].name;
    const currentValue = rows[pos.row][columnName] ?? '';
    if (currentValue !== originalValue) {
      this.pushUndo([{ row: pos.row, column: columnName, oldValue: originalValue, newValue: currentValue }]);
    }
  }

  private moveActiveCell(fromRow: number, fromCol: number, dRow: number, dCol: number, extendSelection: boolean, wrap = false): void {
    const totalRows = this.dataset?.rows.length ?? 0;
    const totalCols = this.dataset?.columns.length ?? 0;
    if (!totalRows || !totalCols) return;

    let row = fromRow + dRow;
    let col = fromCol + dCol;

    if (wrap) {
      if (col >= totalCols) { col = 0; row++; }
      else if (col < 0) { col = totalCols - 1; row--; }
    }

    row = Math.max(0, Math.min(totalRows - 1, row));
    col = Math.max(0, Math.min(totalCols - 1, col));

    this.setActiveCell(row, col, extendSelection);
    this.focusCellAfterRender(row, col);
  }

  private focusCellAfterRender(row: number, col: number): void {
    const targetPage = Math.floor(row / this.previewPageSize);
    if (targetPage !== this.previewPage) {
      this.previewPage = targetPage;
    }
    setTimeout(() => {
      const el = document.getElementById(`grid-cell-${row}-${col}`) as HTMLInputElement | null;
      el?.focus();
      el?.select?.();
    });
  }

  /** Excel's "type once, Enter fills the whole selection" behavior, for a multi-cell range. */
  private fillSelectionWithActiveValue(): void {
    const rect = this.getSelectionRect();
    const active = this.activeCell;
    const columns = this.dataset?.columns;
    const rows = this.dataset?.rows;
    if (!rect || !active || !columns?.[active.col] || !rows) return;

    const columnName = columns[active.col].name;
    const value = rows[active.row]?.[columnName] ?? '';
    const changes: CellChange[] = [];
    for (let row = rect.minRow; row <= rect.maxRow; row++) {
      for (let col = rect.minCol; col <= rect.maxCol; col++) {
        if (row === active.row && col === active.col) continue;
        const targetColumnName = columns[col]?.name;
        if (!targetColumnName) continue;
        const change = this.setCellValueDirect(row, targetColumnName, value);
        if (change) changes.push(change);
      }
    }
    if (changes.length) {
      this.pushUndo(changes);
    }
  }

  onGridKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    const rowAttr = target?.getAttribute?.('data-row');
    const colAttr = target?.getAttribute?.('data-col');
    if (rowAttr == null || colAttr == null) return;
    const rowIndex = Number(rowAttr);
    const colIndex = Number(colAttr);

    const ctrlOrCmd = event.ctrlKey || event.metaKey;
    const key = event.key.toLowerCase();

    if (ctrlOrCmd && key === 'z' && !event.shiftKey) {
      event.preventDefault();
      this.undo();
      return;
    }
    if (ctrlOrCmd && (key === 'y' || (key === 'z' && event.shiftKey))) {
      event.preventDefault();
      this.redo();
      return;
    }

    const isSelect = target.tagName === 'SELECT';

    switch (event.key) {
      case 'ArrowDown':
        if (isSelect) break;
        event.preventDefault();
        this.commitEditSession();
        this.moveActiveCell(rowIndex, colIndex, 1, 0, event.shiftKey);
        break;
      case 'ArrowUp':
        if (isSelect) break;
        event.preventDefault();
        this.commitEditSession();
        this.moveActiveCell(rowIndex, colIndex, -1, 0, event.shiftKey);
        break;
      case 'ArrowLeft': {
        if (isSelect) break;
        const input = target as HTMLInputElement;
        if ((input.selectionStart ?? 0) === 0 && (input.selectionEnd ?? 0) === 0) {
          event.preventDefault();
          this.commitEditSession();
          this.moveActiveCell(rowIndex, colIndex, 0, -1, event.shiftKey);
        }
        break;
      }
      case 'ArrowRight': {
        if (isSelect) break;
        const input = target as HTMLInputElement;
        const len = input.value?.length ?? 0;
        if ((input.selectionStart ?? 0) === len && (input.selectionEnd ?? 0) === len) {
          event.preventDefault();
          this.commitEditSession();
          this.moveActiveCell(rowIndex, colIndex, 0, 1, event.shiftKey);
        }
        break;
      }
      case 'Tab':
        event.preventDefault();
        this.commitEditSession();
        this.moveActiveCell(rowIndex, colIndex, 0, event.shiftKey ? -1 : 1, false, true);
        break;
      case 'Enter':
        event.preventDefault();
        this.commitEditSession();
        if (this.getSelectionSize() > 1) {
          this.fillSelectionWithActiveValue();
        } else {
          this.moveActiveCell(rowIndex, colIndex, 1, 0, false);
        }
        break;
      case 'Escape':
        target.blur();
        break;
    }
  }

  // ── Copy / paste ─────────────────────────────────────────────────────────────

  onGridCopy(event: ClipboardEvent): void {
    const rect = this.getSelectionRect();
    const columns = this.dataset?.columns;
    const rows = this.dataset?.rows;
    if (!rect || !columns || !rows) return;

    const lines: string[] = [];
    for (let row = rect.minRow; row <= rect.maxRow; row++) {
      const cells: string[] = [];
      for (let col = rect.minCol; col <= rect.maxCol; col++) {
        const columnName = columns[col]?.name;
        cells.push(columnName ? (rows[row]?.[columnName] ?? '') : '');
      }
      lines.push(cells.join('\t'));
    }
    event.preventDefault();
    event.clipboardData?.setData('text/plain', lines.join('\n'));
  }

  onGridPaste(event: ClipboardEvent): void {
    if (!this.canEditPreviewData || !this.activeCell || !this.dataset) return;
    const text = event.clipboardData?.getData('text/plain') ?? '';
    if (!text) return;
    event.preventDefault();

    const pastedLines = text.replace(/\r/g, '').split('\n');
    if (pastedLines.length > 1 && pastedLines[pastedLines.length - 1] === '') {
      pastedLines.pop();
    }
    const grid = pastedLines.map((line) => line.split('\t'));

    const rect = this.getSelectionRect();
    const anchor = this.activeCell;
    const columns = this.dataset.columns;
    const totalRows = this.dataset.rows.length;
    const totalCols = columns.length;
    const changes: CellChange[] = [];

    const isSingleValue = grid.length === 1 && grid[0].length === 1;
    if (isSingleValue && rect && this.getSelectionSize() > 1) {
      // Pasting one value onto a multi-cell selection fills the whole selection.
      for (let row = rect.minRow; row <= rect.maxRow; row++) {
        for (let col = rect.minCol; col <= rect.maxCol; col++) {
          const columnName = columns[col]?.name;
          if (!columnName) continue;
          const change = this.setCellValueDirect(row, columnName, grid[0][0]);
          if (change) changes.push(change);
        }
      }
    } else {
      for (let r = 0; r < grid.length; r++) {
        const targetRow = anchor.row + r;
        if (targetRow >= totalRows) break;
        for (let c = 0; c < grid[r].length; c++) {
          const targetCol = anchor.col + c;
          if (targetCol >= totalCols) break;
          const columnName = columns[targetCol]?.name;
          if (!columnName) continue;
          const change = this.setCellValueDirect(targetRow, columnName, grid[r][c]);
          if (change) changes.push(change);
        }
      }
    }

    if (changes.length) {
      this.pushUndo(changes);
      this.cellEditStatus = 'idle';
      this.cellEditStatusMessage = '';
    }
  }

  // ── Undo / redo ──────────────────────────────────────────────────────────────

  private pushUndo(changes: CellChange[]): void {
    if (!changes.length) return;
    this.undoStack.push(changes);
    this.redoStack = [];
    if (this.undoStack.length > 200) {
      this.undoStack.shift();
    }
  }

  undo(): void {
    const batch = this.undoStack.pop();
    if (!batch) return;
    batch.forEach((change) => this.setCellValueDirect(change.row, change.column, change.oldValue));
    this.redoStack.push(batch);
  }

  redo(): void {
    const batch = this.redoStack.pop();
    if (!batch) return;
    batch.forEach((change) => this.setCellValueDirect(change.row, change.column, change.newValue));
    this.undoStack.push(batch);
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  // ── Find & replace ───────────────────────────────────────────────────────────

  toggleFindReplace(): void {
    this.showFindReplace = !this.showFindReplace;
    this.findReplaceMessage = '';
  }

  runFindReplace(): void {
    if (!this.dataset || !this.findReplaceColumn || !this.findReplaceFind) {
      this.findReplaceMessage = 'Choose a column and a value to find.';
      return;
    }
    if (!this.isEditableColumn(this.findReplaceColumn)) {
      this.findReplaceMessage = 'That column cannot be edited.';
      return;
    }

    const changes: CellChange[] = [];
    this.dataset.rows.forEach((row, rowIndex) => {
      if ((row[this.findReplaceColumn] ?? '') === this.findReplaceFind) {
        const change = this.setCellValueDirect(rowIndex, this.findReplaceColumn, this.findReplaceReplace);
        if (change) changes.push(change);
      }
    });

    if (changes.length) {
      this.pushUndo(changes);
      this.findReplaceMessage = `Replaced ${changes.length} cell(s). Click "Save to Database" to persist.`;
    } else {
      this.findReplaceMessage = 'No matching cells were found.';
    }
  }

  // ── Row insert / delete (writes immediately — not part of the pending-edit batch) ──

  async deleteRow(rowIndex: number): Promise<void> {
    if (!this.selectedEntity || !this.entityEditKeyColumn || !this.dataset || this.rowActionBusy) return;
    const row = this.dataset.rows[rowIndex];
    const keyValue = row?.[DataBuilderComponent.ROW_KEY_FIELD];
    if (!keyValue) {
      this.rowActionMessage = 'This row has no database key and cannot be deleted.';
      return;
    }
    if (!window.confirm('Delete this row from the database? This cannot be undone.')) {
      return;
    }

    this.rowActionBusy = true;
    this.rowActionMessage = '';
    try {
      const response = await firstValueFrom(this.datasetService.deleteEntityRow({
        entityName: this.selectedEntity,
        keyColumn: this.entityEditKeyColumn,
        keyValue,
      }));
      if (response?.status === 'SUCCESS') {
        this.dataset.rows.splice(rowIndex, 1);
        const keyColumn = this.entityEditKeyColumn;
        this.entityTableRows = this.entityTableRows.filter(
          (candidate) => String(candidate[keyColumn]) !== keyValue,
        );
        this.pendingCellEdits.delete(row);
        if (this.previewPage >= this.previewTotalPages) {
          this.previewPage = Math.max(0, this.previewTotalPages - 1);
        }
        this.rowActionMessage = 'Row deleted.';
      } else {
        this.rowActionMessage = response?.message || 'The server rejected the delete.';
      }
    } catch (error) {
      this.rowActionMessage = error instanceof Error ? error.message : 'Unable to delete the row.';
    } finally {
      this.rowActionBusy = false;
    }
  }

  async duplicateRow(rowIndex: number): Promise<void> {
    if (!this.selectedEntity || !this.entityEditKeyColumn || !this.dataset || this.rowActionBusy) return;
    const sourceRow = this.dataset.rows[rowIndex];
    if (!sourceRow) return;

    const values: Record<string, unknown> = {};
    this.dataset.columns.forEach((column) => {
      if (this.isEditableColumn(column.name)) {
        values[column.name] = sourceRow[column.name] ?? '';
      }
    });

    this.rowActionBusy = true;
    this.rowActionMessage = '';
    try {
      const response = await firstValueFrom(this.datasetService.insertEntityRow({
        entityName: this.selectedEntity,
        values,
      }));
      if (response?.status === 'SUCCESS' && response.row) {
        const newKeyValue = response.row.keyValue;
        const keyColumn = this.entityEditKeyColumn;
        const newRow: Record<string, string> = { ...sourceRow };
        newRow[DataBuilderComponent.ROW_KEY_FIELD] = newKeyValue == null ? '' : String(newKeyValue);
        this.dataset.rows.splice(rowIndex + 1, 0, newRow);

        const rawRow: Record<string, unknown> = { ...sourceRow, [keyColumn]: newKeyValue };
        this.entityTableRows.splice(rowIndex + 1, 0, rawRow);

        this.rowActionMessage = 'Row duplicated and saved to the database.';
      } else {
        this.rowActionMessage = response?.message || 'The server rejected the insert.';
      }
    } catch (error) {
      this.rowActionMessage = error instanceof Error ? error.message : 'Unable to duplicate the row.';
    } finally {
      this.rowActionBusy = false;
    }
  }

  // ── Full dataset download ───────────────────────────────────────────────────

  downloadingPreview = false;

  /** Downloads ALL dataset rows (not just the visible page) as CSV or Excel. */
  async downloadPreviewData(format: 'csv' | 'xlsx'): Promise<void> {
    const dataset = this.dataset;
    if (!dataset?.rows.length || this.downloadingPreview) {
      return;
    }

    this.downloadingPreview = true;
    try {
      const columns = dataset.columns.map((column) => column.name);
      const baseName = (this.selectedEntity || dataset.fileName || 'data-export')
        .replace(/[^A-Za-z0-9._-]+/g, '_')
        .replace(/^_+|_+$/g, '') || 'data-export';

      if (format === 'csv') {
        const escapeCsv = (value: string): string => (
          /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
        );
        const lines: string[] = [columns.map(escapeCsv).join(',')];
        for (const row of dataset.rows) {
          lines.push(columns.map((column) => escapeCsv(row[column] ?? '')).join(','));
        }
        // BOM so Excel opens UTF-8 CSVs with correct characters.
        const blob = new Blob(['\u{FEFF}' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
        this.triggerBlobDownload(blob, `${baseName}.csv`);
      } else {
        // Lazy-load SheetJS so the export library never weighs down initial page load.
        const XLSX = await import('xlsx');
        const sheetRows = dataset.rows.map((row) => {
          const record: Record<string, unknown> = {};
          columns.forEach((column) => { record[column] = row[column] ?? ''; });
          return record;
        });
        const worksheet = XLSX.utils.json_to_sheet(sheetRows, { header: columns });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
        XLSX.writeFile(workbook, `${baseName}.xlsx`);
      }
    } finally {
      this.downloadingPreview = false;
    }
  }

  private triggerBlobDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private extractVisibleEntityColumns(rows: Array<Record<string, unknown>>): string[] {
    const hiddenColumns = new Set(this.hiddenEntityColumns);
    // Always hidden regardless of layout.options.hiddenColumns overrides —
    // this is an internal bookkeeping field, never real entity data.
    hiddenColumns.add(DataBuilderComponent.ROW_KEY_FIELD);
    const normalizedHiddenColumns = new Set(Array.from(hiddenColumns, (column) => column.toLowerCase()));
    const columns: string[] = [];
    const seen = new Set<string>();

    rows.forEach((row) => {
      Object.keys(row).forEach((key) => {
        const normalizedKey = key.toLowerCase();
        const isSourceRowHash = normalizedKey.replace(/[^a-z0-9]/g, '') === 'sourcerowhash'
          || normalizedKey.replace(/[^a-z0-9]/g, '') === 'sourcedrowhash'
          || normalizedKey.replace(/[^a-z0-9]/g, '') === 'spricedrowhash';
        const isHiddenColumn = normalizedHiddenColumns.has(normalizedKey);
        if (!isHiddenColumn && !isSourceRowHash && !seen.has(key)) {
          seen.add(key);
          columns.push(key);
        }
      });
    });

    return columns;
  }

  private resetEntityComputationState(): void {
    this.entityFormulas = [];
    this.entityFilters = [];
    this.cancelEntityFormulaEdit();
    this.cancelEntityFilterEdit();
  }

  openEntityPreview(): void {
    if (!this.entityTableRows.length || !this.entityTableColumns.length) {
      return;
    }
    this.isEntityPreviewOpen = true;
  }

  closeEntityPreview(): void {
    this.isEntityPreviewOpen = false;
  }

  onFileSelected(event: Event, appendToPipelineSources = false): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    const existingPipelineFiles = appendToPipelineSources ? [...this.uploadedFiles] : [];
    this.errorMessage = '';
    this.validationMessage = '';
    this.saveMessage = '';
    this.computedColumns = [];
    this.rowFilter = '';
    this.state.setCustomAggregations([]);
    this.newColumnName = '';
    this.newColumnExpression = '';
    this.newColumnError = '';
    this.rowFilterError = '';
    this.newAggregationLabel = '';
    this.newAggregationExpression = '';
    this.newAggregationError = '';
    this.state.setRawDataset(undefined);
    this.currentFileName = '';
    this.uploadedFileName = '';
    if (!appendToPipelineSources) {
      this.uploadedFiles = [];
      this.datasetService.clearLatestWorkflow();
      this.clearWorkbookSelection();
    }

    if (!files.length) {
      return;
    }

    const unsupportedFiles = files.filter((file) => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      return !this.getSupportedFileType(extension);
    });
    if (unsupportedFiles.length) {
      this.state.setDataset(undefined);
      this.errorMessage = `Upload only CSV or Excel files (.csv, .xlsx, .xls). Unsupported: ${unsupportedFiles.map((file) => file.name).join(', ')}`;
      input.value = '';
      return;
    }

    const file = files[0];
    const extension = file.name.split('.').pop()?.toLowerCase();
    const fileType = this.getSupportedFileType(extension);
    if (!fileType) {
      this.state.setDataset(undefined);
      this.errorMessage = 'Upload a CSV or Excel file (.csv, .xlsx, .xls).';
      input.value = '';
      return;
    }

    const label = files.length > 1 ? `${file.name} (+${files.length - 1} more)` : file.name;
    this.setFileUploadStatus(`Reading ${label}…`);

    let routeAsWorkbook = false;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        if (fileType !== 'csv' && files.length === 1 && (extension === 'xlsx' || extension === 'xlsm')) {
          const XLSX = await import('xlsx');
          routeAsWorkbook = this.countSheetsWithData(XLSX, reader.result as ArrayBuffer) > 1;
        }
        if (appendToPipelineSources && !routeAsWorkbook) {
          this.setFileUploadStatus(`Uploading ${label}…`);
          await this.replacePipelineWorkflowSourceFiles(files);
        }

        this.setFileUploadStatus(`Parsing ${label}…`);
        let parsedDataset: UploadedDataset;
        if (fileType === 'csv') {
          const text = String(reader.result || '');
          parsedDataset = this.createDatasetFromRows(this.parseCsvRows(text), file.name, 'csv');
        } else {
          const XLSX = await import('xlsx');
          parsedDataset = this.parseExcelDataset(XLSX, reader.result as ArrayBuffer, file.name);
        }

        // Show the parsed data IMMEDIATELY — the preview should not wait for the
        // backend upload / saved-formulas round-trips. Computed columns + row
        // filter are applied once (if any) come back from the backend below.
        this.state.setRawDataset(parsedDataset);
        this.currentFileName = file.name;
        const selectedUploads = files.map(f => ({ name: f.name, file: f }));
        const mergedUploads = appendToPipelineSources
          ? [
              ...existingPipelineFiles.filter((existing) => !selectedUploads.some((next) => next.name === existing.name)),
              ...selectedUploads,
            ]
          : selectedUploads;
        this.uploadedFileName = mergedUploads.length > 1 ? `${mergedUploads[0].name} (+${mergedUploads.length - 1} more)` : mergedUploads[0].name;
        this.uploadedFiles = mergedUploads;
        if (appendToPipelineSources) {
          const uploadedNames = new Set(selectedUploads.map((upload) => upload.name));
          this.hiddenPipelineSourceNames = new Set(
            [...this.hiddenPipelineSourceNames].filter((name) => !uploadedNames.has(name)),
          );
          this.selectedPipelineSourceName = file.name;
          this.resetPipelinePreview();
          selectedUploads.forEach((upload) => {
            this.ensurePipelineSourceNode(upload.name, upload.name === file.name ? parsedDataset.rows.length : 0);
          });
        }
        this.applyDatasetFormulas();
      } catch (error) {
        this.state.setRawDataset(undefined);
        this.state.setDataset(undefined);
        this.errorMessage = error instanceof Error ? error.message : 'Unable to parse the uploaded file.';
        this.clearFileUploadStatus();
        input.value = '';
        return;
      }

      // The preview is on screen now, so the loader ends here — enrichment below
      // runs in the background and must not hold it open.
      this.clearFileUploadStatus();

      // Backend enrichment (upload + saved formulas) runs in the background so a
      // slow/unreachable backend never blocks the preview that's already showing.
      if (routeAsWorkbook) {
        this.workbookFile = file;
        this.workbookFromPipeline = appendToPipelineSources;
        void this.provisionWorkbook();
      } else if (!appendToPipelineSources) {
        void this.enrichUploadedDatasetInBackground(file, files);
      }
      input.value = '';
    };
    reader.onerror = () => {
      this.errorMessage = 'Unable to read the uploaded file.';
      this.clearFileUploadStatus();
      input.value = '';
    };

    if (fileType === 'csv') {
      reader.readAsText(file);
      return;
    }

    reader.readAsArrayBuffer(file);
  }

  private async replacePipelineWorkflowSourceFiles(files: File[]): Promise<void> {
    try {
      await firstValueFrom(this.datasetService.replaceWorkflowSourceFiles(files, this.effectiveSchemaName));
    } catch (error) {
      throw new Error(
        error instanceof Error && error.message
          ? `Unable to replace workflow source files: ${error.message}`
          : 'Unable to replace workflow source files.',
      );
    }
  }

  private countSheetsWithData(XLSX: typeof import('xlsx'), buffer: ArrayBuffer): number {
    const workbook = XLSX.read(buffer, { type: 'array', sheetRows: 2 });
    return workbook.SheetNames.filter((name) => !!workbook.Sheets[name]?.['!ref']).length;
  }

  clearWorkbookSelection(): void {
    this.workbookFile = null;
    this.workbookFromPipeline = false;
    this.workbookReport = null;
    this.workbookIngestion = null;
    this.workbookError = '';
    this.workbookStatusText = '';
  }

  get workbookSheetCount(): number {
    return this.workbookReport?.sheets?.length ?? 0;
  }

  get workbookVirtualCount(): number {
    return (this.workbookReport?.sheets ?? [])
      .reduce((total, sheet) => total + (sheet.virtualAttributes?.length ?? 0), 0);
  }

  get workbookNeedsConfigCount(): number {
    return (this.workbookReport?.sheets ?? [])
      .reduce((total, sheet) => total + (sheet.lookupsNeedingConfig?.length ?? 0), 0);
  }

  workbookKeptColumns(sheet: WorkbookSheetOutcome): Array<{ name: string; reason: string }> {
    const kept = sheet?.keptAsStoredColumns ?? {};
    return Object.keys(kept).map((name) => ({ name, reason: String(kept[name]) }));
  }

  workbookRunSucceeded(run: WorkbookRun): boolean {
    const sheet = this.workbookReport?.sheets?.find((candidate) => candidate.workflowName === run.workflowName);
    if (run.statusCode < 400 && sheet?.rowsInSheet === 0 && (run.rowsIngested ?? 0) === 0) {
      return true;
    }
    return run.ingested ?? run.statusCode < 400;
  }

  workbookRunStatus(workflowName: string): WorkbookRun | null {
    const runs = this.workbookIngestion?.runs ?? [];
    return runs.find((run) => run.workflowName === workflowName) ?? null;
  }

  trackWorkbookSheet(_index: number, sheet: WorkbookSheetOutcome): string {
    return sheet?.entityName ?? String(_index);
  }

  async provisionWorkbook(): Promise<void> {
    if (!this.workbookFile || this.workbookBusy) {
      return;
    }
    this.workbookBusy = true;
    this.workbookError = '';
    this.workbookReport = null;
    this.workbookIngestion = null;
    this.workbookStatusText = this.workbookAutoRun
      ? 'Creating an entity and workflow for every sheet, then ingesting…'
      : 'Creating an entity and workflow for every sheet…';
    try {
      const response = await firstValueFrom(this.datasetService.uploadWorkbook(this.workbookFile, {
        entityPrefix: this.workbookEntityPrefix,
        autoRun: this.workbookAutoRun,
      }));
      this.workbookReport = response ?? null;
      this.workbookIngestion = response?.ingestion ?? null;
      const failedSheets = (response?.sheets ?? []).filter((sheet) => !!sheet.error);
      if (failedSheets.length) {
        this.workbookError = `${failedSheets.length} sheet(s) could not be provisioned: `
          + failedSheets.map((sheet) => `${sheet.sheetName} (${sheet.error})`).join('; ');
      }
      this.scheduleEntityOptionsLoad();
    } catch (error) {
      this.workbookError = this.describeWorkbookError(error);
    } finally {
      this.workbookBusy = false;
      this.workbookStatusText = '';
    }
  }

  async ingestWorkbook(): Promise<void> {
    const order = this.workbookReport?.provisioningOrder ?? [];
    if (!order.length || this.workbookBusy) {
      return;
    }
    this.workbookBusy = true;
    this.workbookError = '';
    this.workbookStatusText = `Ingesting ${order.length} sheet(s) in dependency order…`;
    try {
      this.workbookIngestion = await firstValueFrom(this.datasetService.runWorkbook(order));
      if ((this.workbookIngestion?.failedCount ?? 0) > 0) {
        this.workbookError = `${this.workbookIngestion?.failedCount} sheet(s) failed to ingest.`
          + ' Check each sheet below for its result.';
      }
    } catch (error) {
      this.workbookError = this.describeWorkbookError(error);
    } finally {
      this.workbookBusy = false;
      this.workbookStatusText = '';
    }
  }

  private describeWorkbookError(error: unknown): string {
    const record = error as { error?: { message?: string }; message?: string } | null;
    return record?.error?.message
      || record?.message
      || 'The workbook could not be processed.';
  }

  removeUploadedFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    if (this.uploadedFiles.length === 0) {
      this.clearWorkbookSelection();
      this.datasetService.clearLatestWorkflow();
      this.currentFileName = '';
      this.uploadedFileName = '';
      this.state.setRawDataset(undefined);
      this.state.setDataset(undefined);
      this.errorMessage = '';
      this.validationMessage = '';
    } else {
      this.uploadedFileName = this.uploadedFiles.map(f => f.name).join(', ');
    }
  }

  /**
   * Backend enrichment after the preview is already visible: upload the file to
   * get a datasetId, then load any saved formulas and re-apply them. Runs detached
   * from the preview render so backend latency never delays the data preview.
   */
  private async enrichUploadedDatasetInBackground(file: File, files: File[]): Promise<void> {
    try {
      const uploadedDataset = await firstValueFrom(this.datasetService.uploadDataset(files));
      if (uploadedDataset) {
        this.scheduleEntityOptionsLoad();
      }

      // Attach the backend datasetId to the already-shown dataset.
      if (uploadedDataset?.datasetId) {
        const current = this.rawDataset;
        if (current) {
          this.state.setRawDataset({ ...current, datasetId: uploadedDataset.datasetId });
        }
      }

      const datasetId = this.rawDataset?.datasetId || null;
      const savedFormulas = await firstValueFrom(
        this.datasetService.getFormulas(datasetId, file.name),
      );
      const hasSavedFormulas =
        savedFormulas.computedColumns.length > 0 ||
        !!savedFormulas.rowFilter ||
        savedFormulas.customAggregations.length > 0;

      this.computedColumns = savedFormulas.computedColumns.map((c) => ({ ...c }));
      this.rowFilter = savedFormulas.rowFilter;
      this.state.setCustomAggregations(savedFormulas.customAggregations.map((a) => ({ ...a })));

      // Only re-derive the preview if saved formulas actually changed it.
      if (hasSavedFormulas) {
        this.applyDatasetFormulas();
      }

      if (datasetId) {
        this.saveMessage = `${files.length} file${files.length > 1 ? 's' : ''} uploaded. Backend dataset id: ${datasetId}`;
      }
    } catch {
      // Non-fatal: the parsed preview is already showing. The dataset just won't
      // have a backend id / saved formulas until the next successful upload.
    }
  }

  addComputedColumn(): void {
    const name = this.newColumnName.trim();
    const expression = this.newColumnExpression.trim();
    this.newColumnError = '';

    if (!name) { this.newColumnError = 'Column name is required.'; return; }
    if (!expression) { this.newColumnError = 'Expression is required.'; return; }
    if (!/^[A-Za-z_][A-Za-z0-9_ ]*$/.test(name)) {
      this.newColumnError = 'Name must start with a letter or underscore (letters, digits, spaces, underscores only).';
      return;
    }
    const existingColumnNames = (this.rawDataset?.columns || []).map((c) => c.name);
    if (existingColumnNames.includes(name) || this.computedColumns.some((c) => c.name === name)) {
      this.newColumnError = `A column named "${name}" already exists.`;
      return;
    }

    this.computedColumns = [...this.computedColumns, { name, expression }];
    this.newColumnName = '';
    this.newColumnExpression = '';
    this.applyDatasetFormulas();
    this.persistDatasetFormulas();
  }

  removeComputedColumn(name: string): void {
    this.computedColumns = this.computedColumns.filter((c) => c.name !== name);
    this.applyDatasetFormulas();
    this.persistDatasetFormulas();
  }

  onRowFilterChange(): void {
    this.applyDatasetFormulas();
    this.persistDatasetFormulas();
  }

  addCustomAggregation(): void {
    const label = this.newAggregationLabel.trim();
    const expression = this.newAggregationExpression.trim();
    this.newAggregationError = '';

    if (!label) { this.newAggregationError = 'Label is required.'; return; }
    if (!expression) { this.newAggregationError = 'Expression is required.'; return; }
    const key = `custom:${label.replace(/[^A-Za-z0-9_]/g, '_')}`;
    if (this.customAggregations.some((a) => a.key === key)) {
      this.newAggregationError = `An aggregation called "${label}" already exists.`;
      return;
    }
    try {
      this.formulaService.buildAggregationEvaluator(expression, (this.dataset?.columns || []).map((c) => c.name));
    } catch (error) {
      this.newAggregationError = `Invalid expression: ${error instanceof Error ? error.message : 'unknown error'}`;
      return;
    }

    this.state.setCustomAggregations([...this.customAggregations, { key, label, expression }]);
    this.newAggregationLabel = '';
    this.newAggregationExpression = '';
    this.persistDatasetFormulas();
  }

  removeCustomAggregation(key: string): void {
    this.state.setCustomAggregations(this.customAggregations.filter((a) => a.key !== key));
    if (this.selectedAggregation === key) {
      this.selectedAggregation = 'sum';
    }
    this.persistDatasetFormulas();
  }

  beginEditComputedColumn(column: ComputedColumn): void {
    this.editingComputedColumn = column.name;
    this.editingComputedColumnName = column.name;
    this.editingComputedColumnExpression = column.expression;
    this.editingComputedColumnError = '';
  }

  cancelEditComputedColumn(): void {
    this.editingComputedColumn = null;
    this.editingComputedColumnError = '';
  }

  saveEditComputedColumn(originalName: string): void {
    const name = this.editingComputedColumnName.trim();
    const expression = this.editingComputedColumnExpression.trim();
    this.editingComputedColumnError = '';

    if (!name) { this.editingComputedColumnError = 'Column name is required.'; return; }
    if (!expression) { this.editingComputedColumnError = 'Expression is required.'; return; }
    if (!/^[A-Za-z_][A-Za-z0-9_ ]*$/.test(name)) {
      this.editingComputedColumnError = 'Name must start with a letter or underscore.';
      return;
    }
    const baseColumnNames = (this.rawDataset?.columns || []).map((c) => c.name);
    if (name !== originalName) {
      if (baseColumnNames.includes(name)
        || this.computedColumns.some((c) => c.name === name)) {
        this.editingComputedColumnError = `A column named "${name}" already exists.`;
        return;
      }
    }

    this.computedColumns = this.computedColumns.map((c) =>
      c.name === originalName ? { name, expression } : c,
    );

    this.editingComputedColumn = null;
    this.applyDatasetFormulas();
    this.persistDatasetFormulas();
    this.syncLibraryEntryIfLinked('column', originalName, { name, expression });
  }

  beginEditAggregation(agg: CustomAggregation): void {
    this.editingAggregation = agg.key;
    this.editingAggregationLabel = agg.label;
    this.editingAggregationExpression = agg.expression;
    this.editingAggregationError = '';
  }

  cancelEditAggregation(): void {
    this.editingAggregation = null;
    this.editingAggregationError = '';
  }

  saveEditAggregation(originalKey: string): void {
    const label = this.editingAggregationLabel.trim();
    const expression = this.editingAggregationExpression.trim();
    this.editingAggregationError = '';

    if (!label) { this.editingAggregationError = 'Label is required.'; return; }
    if (!expression) { this.editingAggregationError = 'Expression is required.'; return; }

    try {
      this.formulaService.buildAggregationEvaluator(expression, (this.dataset?.columns || []).map((c) => c.name));
    } catch (error) {
      this.editingAggregationError = `Invalid expression: ${error instanceof Error ? error.message : 'unknown error'}`;
      return;
    }

    this.state.setCustomAggregations(this.customAggregations.map((a) =>
      a.key === originalKey ? { ...a, label, expression } : a,
    ));

    this.editingAggregation = null;
    this.persistDatasetFormulas();
    this.syncLibraryEntryIfLinked('aggregation', originalKey, { label, expression });
  }

  applyTemplate(template: FormulaTemplate): void {
    if (template.kind === 'column') {
      this.newColumnName = template.defaultName;
      this.newColumnExpression = template.expressionTemplate;
      this.newColumnError = '';
    } else {
      this.newAggregationLabel = template.defaultName;
      this.newAggregationExpression = template.expressionTemplate;
      this.newAggregationError = '';
    }
  }

  toggleLibraryPanel(): void {
    this.showLibraryPanel = !this.showLibraryPanel;
    if (this.showLibraryPanel && this.computationLibrary.length === 0) {
      this.loadComputationLibrary();
    }
  }

  saveColumnToLibrary(column: ComputedColumn): void {
    const existing = this.computationLibrary.find(
      (entry) => entry.kind === 'column' && entry.name === column.name,
    );
    const now = new Date().toISOString();
    if (existing) {
      if (existing.expression === column.expression) {
        this.saveMessage = `"${column.name}" is already in the library and unchanged.`;
        return;
      }
      existing.versions = [
        ...(existing.versions || []),
        { expression: existing.expression, savedAt: existing.updatedAt },
      ];
      existing.expression = column.expression;
      existing.updatedAt = now;
      this.persistComputationLibrary();
      this.computationLibraryService.update(existing as unknown as ComputationLibraryEntry).subscribe();
      this.propagateLibraryColumnUpdate(existing);
      this.saveMessage = `Library updated: "${column.name}" → v${existing.versions.length + 1}.`;
    } else {
      const entry: LibraryComputation = {
        id: this.createId(),
        kind: 'column',
        name: column.name,
        expression: column.expression,
        createdAt: now,
        updatedAt: now,
        versions: [],
      };
      this.computationLibrary = [entry, ...this.computationLibrary];
      this.persistComputationLibrary();
      this.computationLibraryService.create(entry as unknown as ComputationLibraryEntry).subscribe();
      this.saveMessage = `Saved "${column.name}" to library.`;
    }
  }

  saveAggregationToLibrary(agg: CustomAggregation): void {
    const existing = this.computationLibrary.find(
      (entry) => entry.kind === 'aggregation' && entry.name === agg.key,
    );
    const now = new Date().toISOString();
    if (existing) {
      if (existing.expression === agg.expression && existing.label === agg.label) {
        this.saveMessage = `"${agg.label}" is already in the library and unchanged.`;
        return;
      }
      existing.versions = [
        ...(existing.versions || []),
        { expression: existing.expression, label: existing.label, savedAt: existing.updatedAt },
      ];
      existing.expression = agg.expression;
      existing.label = agg.label;
      existing.updatedAt = now;
      this.persistComputationLibrary();
      this.computationLibraryService.update(existing as unknown as ComputationLibraryEntry).subscribe();
      this.saveMessage = `Library updated: "${agg.label}" → v${existing.versions.length + 1}.`;
    } else {
      const entry: LibraryComputation = {
        id: this.createId(),
        kind: 'aggregation',
        name: agg.key,
        label: agg.label,
        expression: agg.expression,
        createdAt: now,
        updatedAt: now,
        versions: [],
      };
      this.computationLibrary = [entry, ...this.computationLibrary];
      this.persistComputationLibrary();
      this.computationLibraryService.create(entry as unknown as ComputationLibraryEntry).subscribe();
      this.saveMessage = `Saved "${agg.label}" to library.`;
    }
  }

  applyLibraryEntry(entry: LibraryComputation): void {
    if (!this.dataset && entry.kind === 'column') {
      this.saveMessage = 'Upload a dataset before applying library computations.';
      return;
    }
    if (entry.kind === 'column') {
      const existing = this.computedColumns.find((c) => c.name === entry.name);
      if (existing) {
        this.computedColumns = this.computedColumns.map((c) =>
          c.name === entry.name ? { name: entry.name, expression: entry.expression } : c,
        );
        this.saveMessage = `Re-applied "${entry.name}" from library.`;
      } else {
        const baseColumnNames = (this.rawDataset?.columns || []).map((c) => c.name);
        if (baseColumnNames.includes(entry.name)) {
          this.saveMessage = `Column "${entry.name}" already exists in the dataset.`;
          return;
        }
        this.computedColumns = [...this.computedColumns, { name: entry.name, expression: entry.expression }];
        this.saveMessage = `Applied "${entry.name}" from library.`;
      }
      this.applyDatasetFormulas();
      this.persistDatasetFormulas();
    } else {
      const existing = this.customAggregations.find((a) => a.key === entry.name);
      if (existing) {
        this.state.setCustomAggregations(this.customAggregations.map((a) =>
          a.key === entry.name ? { ...a, label: entry.label || a.label, expression: entry.expression } : a,
        ));
      } else {
        this.state.setCustomAggregations([
          ...this.customAggregations,
          { key: entry.name, label: entry.label || entry.name, expression: entry.expression },
        ]);
      }
      this.persistDatasetFormulas();
      this.saveMessage = `Applied aggregation "${entry.label || entry.name}" from library.`;
    }
  }

  removeLibraryEntry(entry: LibraryComputation, event?: Event): void {
    event?.stopPropagation();
    if (!window.confirm(`Remove "${entry.label || entry.name}" from the computation library?`)) {
      return;
    }
    this.computationLibrary = this.computationLibrary.filter((e) => e.id !== entry.id);
    if (this.viewingVersionsId === entry.id) {
      this.viewingVersionsId = null;
    }
    this.persistComputationLibrary();
    this.computationLibraryService.delete(entry.id).subscribe();
    this.saveMessage = `Removed "${entry.label || entry.name}" from library.`;
  }

  toggleVersions(id: string): void {
    this.viewingVersionsId = this.viewingVersionsId === id ? null : id;
  }

  restoreLibraryVersion(entry: LibraryComputation, index: number): void {
    const version = entry.versions[index];
    if (!version) return;
    const now = new Date().toISOString();
    entry.versions = [
      ...entry.versions.slice(0, index),
      ...entry.versions.slice(index + 1),
      { expression: entry.expression, label: entry.label, savedAt: entry.updatedAt },
    ];
    entry.expression = version.expression;
    if (version.label !== undefined) entry.label = version.label;
    entry.updatedAt = now;
    this.persistComputationLibrary();
    this.computationLibraryService.restoreVersion(entry.id, index).subscribe();
    if (entry.kind === 'column') {
      this.propagateLibraryColumnUpdate(entry);
    }
    this.saveMessage = `Restored "${entry.label || entry.name}" from history.`;
  }

  async syncToDeam(): Promise<void> {
    this.deamSyncMessage = '';
    if (!this.dataset) {
      this.deamSyncMessage = 'Upload a dataset before syncing to DEAM.';
      return;
    }
    const payload = {
      fileName: this.dataset.fileName,
      columns: this.dataset.columns,
      rowCount: this.dataset.rows.length,
      computedColumns: this.computedColumns,
      customAggregations: this.customAggregations,
      rowFilter: this.rowFilter,
      timestamp: new Date().toISOString(),
    };
    try {
      await firstValueFrom(this.datasetService.syncToDeam(payload));
      this.deamSyncMessage = `Synced ${this.dataset.rows.length} rows and ${this.computedColumns.length} computed columns to DEAM.`;
    } catch (error) {
      this.deamSyncMessage = error instanceof Error
        ? error.message
        : 'Unable to sync this dataset to DEAM.';
    }
  }

  private syncLibraryEntryIfLinked(
    kind: 'column' | 'aggregation',
    originalIdentifier: string,
    next: { name?: string; label?: string; expression: string },
  ): void {
    const entry = this.computationLibrary.find(
      (e) => e.kind === kind && e.name === originalIdentifier,
    );
    if (!entry) return;
    const newName = kind === 'column' ? (next.name || entry.name) : entry.name;
    const sameExpression = entry.expression === next.expression;
    const sameName = entry.name === newName;
    const sameLabel = kind === 'aggregation' ? entry.label === next.label : true;
    if (sameExpression && sameName && sameLabel) {
      return;
    }
    entry.versions = [
      ...(entry.versions || []),
      { expression: entry.expression, label: entry.label, savedAt: entry.updatedAt },
    ];
    entry.expression = next.expression;
    if (kind === 'column' && next.name) entry.name = next.name;
    if (kind === 'aggregation' && next.label) entry.label = next.label;
    entry.updatedAt = new Date().toISOString();
    this.persistComputationLibrary();
    this.computationLibraryService.update(entry as unknown as ComputationLibraryEntry).subscribe();
    if (entry.kind === 'column') {
      this.propagateLibraryColumnUpdate(entry);
    }
  }

  private propagateLibraryColumnUpdate(entry: LibraryComputation): void {
    if (entry.kind !== 'column') return;
    let touched = 0;
    const next = this.state.savedCharts().map((chart) => {
      if (!chart.dataset.columns.some((c) => c.name === entry.name)) return chart;
      const columnNames = chart.dataset.columns.map((c) => c.name);
      try {
        const evaluator = this.formulaService.buildRowEvaluator(entry.expression, columnNames);
        const updatedRows = chart.dataset.rows.map((row) => {
          const value = evaluator(row);
          let stringified = '';
          if (typeof value === 'number') {
            stringified = Number.isFinite(value) ? String(value) : '';
          } else if (value === null || value === undefined) {
            stringified = '';
          } else {
            stringified = String(value);
          }
          return { ...row, [entry.name]: stringified };
        });
        touched += 1;
        return {
          ...chart,
          dataset: { ...chart.dataset, rows: updatedRows },
          updatedAt: new Date().toISOString(),
        };
      } catch {
        return chart;
      }
    });
    if (touched > 0) {
      this.state.setSavedCharts(next);
      this.persistence.persistSavedCharts(next);
      this.dashboardNeedsRefresh.emit();
    }
  }

  loadComputationLibrary(): void {
    this.computationLibraryService.list().subscribe({
      next: (entries) => {
        this.computationLibrary = entries as LibraryComputation[];
      },
      error: () => {
        this.computationLibrary = [];
      },
    });
  }

  private persistComputationLibrary(): void {
    try {
      window.localStorage.setItem(
        DataBuilderComponent.COMPUTATION_LIBRARY_STORAGE_KEY,
        JSON.stringify(this.computationLibrary),
      );
    } catch {
      this.errorMessage = 'Unable to save computation library locally.';
    }
  }

  private getSupportedFileType(extension: string | undefined): SupportedFileType | null {
    if (extension === 'csv') {
      return 'csv';
    }

    if (extension === 'xlsx' || extension === 'xls') {
      return 'excel';
    }

    return null;
  }

  private createDatasetFromRows(rows: string[][], fileName: string, sourceType: SupportedFileType): UploadedDataset {
    if (rows.length < 2) {
      throw new Error('The uploaded file must include a header row and at least one data row.');
    }

    const headers = rows[0].map((header) => header.trim()).filter((header) => !!header);
    if (!headers.length) {
      throw new Error('The header row is empty.');
    }

    const uniqueHeaders = new Set(headers);
    if (uniqueHeaders.size !== headers.length) {
      throw new Error('The header row contains duplicate column names.');
    }

    const dataRows = rows.slice(1, this.maxRowsForPoc + 1).map((row) => {
      const record: Record<string, string> = {};
      headers.forEach((header, index) => {
        record[header] = (row[index] || '').trim();
      });
      return record;
    }).filter((row) => Object.values(row).some((value) => value !== ''));

    if (!dataRows.length) {
      throw new Error('The uploaded file does not contain any usable data rows.');
    }

    return {
      fileName,
      sourceType,
      columns: headers.map((name) => ({
        name,
        type: this.formulaService.inferColumnType(dataRows.map((row) => row[name])),
      })),
      rows: dataRows,
    };
  }

  private parseExcelDataset(XLSX: typeof import('xlsx'), buffer: ArrayBuffer, fileName: string): UploadedDataset {
    const workbook = XLSX.read(buffer, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) {
      throw new Error('The Excel file does not contain any sheets.');
    }

    const worksheet = workbook.Sheets[firstSheetName];
    const rows = XLSX.utils.sheet_to_json<string[]>(worksheet, {
      header: 1,
      raw: false,
      defval: '',
      blankrows: false,
    });

    return this.createDatasetFromRows(
      rows.map((row: string[]) => row.map((cell: string) => String(cell ?? '').trim())),
      fileName,
      'excel',
    );
  }

  private parseCsvRows(text: string): string[][] {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentValue = '';
    let insideQuotes = false;

    for (let index = 0; index < text.length; index += 1) {
      const character = text[index];
      const nextCharacter = text[index + 1];

      if (character === '"' && insideQuotes && nextCharacter === '"') {
        currentValue += '"';
        index += 1;
        continue;
      }

      if (character === '"') {
        insideQuotes = !insideQuotes;
        continue;
      }

      if (character === ',' && !insideQuotes) {
        currentRow.push(currentValue);
        currentValue = '';
        continue;
      }

      if ((character === '\n' || character === '\r') && !insideQuotes) {
        if (character === '\r' && nextCharacter === '\n') {
          index += 1;
        }
        currentRow.push(currentValue);
        rows.push(currentRow);
        currentRow = [];
        currentValue = '';
        continue;
      }

      currentValue += character;
    }

    currentRow.push(currentValue);
    rows.push(currentRow);

    return rows.filter((row) => row.some((value) => value.trim() !== ''));
  }

  private createDatasetFromEntityRows(rows: Array<Record<string, unknown>>, entityName: string): UploadedDataset {
    const columns = this.extractVisibleEntityColumns(rows);

    // Detect the DB key column from genuinely raw rows (uuid/id present
    // verbatim). Only ever SET entityEditKeyColumn here when positively
    // detected — a later call on already-projected rows (uuid/id stripped
    // for display) must never downgrade or null out a key column that an
    // earlier call already resolved correctly. This was the root cause of
    // rows persisting against the wrong key value: computeEntityPreview()
    // re-runs this method on a clone of the *visible* dataset, which never
    // carries the hidden 'uuid' field, so re-detecting from scratch every
    // time silently fell back to 'id' (or null) and corrupted the key.
    // Detect the key column ONLY the first time it's unknown for this entity
    // (onEntitySelectionChange clears it to null before this runs on fresh
    // raw rows). Once resolved, NEVER re-detect on a later call — a
    // known-uuid entity's already-projected rows (uuid hidden for display)
    // still carry an unrelated 'id' column, and re-running detection on that
    // shape would silently swap the resolved key from 'uuid' to 'id', which
    // is exactly what produced updates against the wrong value previously.
    if (!this.entityEditKeyColumn) {
      this.entityEditKeyColumn = rows.some((row) => row['uuid'] != null)
        ? 'uuid'
        : rows.some((row) => row['id'] != null)
          ? 'id'
          : null;
    }
    const keyColumnToRead = this.entityEditKeyColumn;

    const dataRows: Record<string, string>[] = [];
    rows.forEach((row) => {
      const record: Record<string, string> = {};
      columns.forEach((column) => {
        const value = row[column];
        record[column] = value == null ? '' : String(value).trim();
      });
      if (!Object.values(record).some((value) => value !== '')) {
        return;
      }
      // Embed the row's DB key directly on the row object rather than a
      // parallel positional array, so it survives cloning/re-projection
      // (computeEntityPreview) with zero index-alignment risk. Reads ONLY the
      // resolved key column; falls back to a previously-embedded key when
      // `row` is itself already-projected and lacks that raw field.
      const rawKeyValue = keyColumnToRead ? row[keyColumnToRead] : null;
      const carriedKey = rawKeyValue ?? (row as Record<string, unknown>)[DataBuilderComponent.ROW_KEY_FIELD];
      record[DataBuilderComponent.ROW_KEY_FIELD] = carriedKey == null ? '' : String(carriedKey);
      dataRows.push(record);
    });

    this.pendingCellEdits.clear();
    this.undoStack = [];
    this.redoStack = [];
    this.activeCell = null;
    this.selectionAnchor = null;
    this.selectionFocus = null;
    this.cellEditStatusMessage = '';
    this.cellEditStatus = 'idle';
    this.previewPage = 0;

    if (!dataRows.length) {
      throw new Error('The selected entity does not contain any usable rows.');
    }

    this.columnEditorKind = this.computeColumnEditorKinds(columns, dataRows);

    return {
      fileName: entityName,
      sourceType: 'entity',
      columns: columns.map((name) => ({
        name,
        type: this.formulaService.inferColumnType(dataRows.map((row) => row[name])),
      })),
      rows: dataRows,
    };
  }

  private validateEntityFormula(
    name: string,
    expression: string,
    editingIndex: number | null,
  ): string {
    if (!name) {
      return 'Formula name is required.';
    }
    if (!expression) {
      return 'Formula expression is required.';
    }
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) {
      return 'Formula name must start with a letter or underscore and contain only letters, digits, and underscores.';
    }
    if (/\bas\s+[A-Za-z_][A-Za-z0-9_]*$/i.test(expression)) {
      return 'Enter the formula alias in the Formula name field only.';
    }

    const duplicate = this.entityFormulas.some((formula, index) => (
      index !== editingIndex && formula.name.toLowerCase() === name.toLowerCase()
    ));
    if (duplicate) {
      return `A formula named "${name}" already exists.`;
    }

    const columns = (this.rawDataset?.columns || this.dataset?.columns || []).map((column) => column.name);
    const unknownBracketReference = Array.from(expression.matchAll(/\[([^\]]+)\]/g))
      .map((match) => match[1].trim())
      .find((columnName) => !columns.includes(columnName));
    if (unknownBracketReference) {
      return `Unknown column "${unknownBracketReference}" in the formula expression.`;
    }

    try {
      const replaced = this.formulaService.substituteColumnRefs(expression, columns, () => '1');
      const normalized = replaced.replace(/\d+(\.\d+)?/g, '1').replace(/\s+/g, ' ').trim();
      if (!/^[0-9A-Za-z_+\-*/().\s,'"]+$/.test(normalized)) {
        return 'Only columns, numbers, spaces, parentheses, + - * /, commas, quotes, and supported functions are allowed.';
      }

      const allowedFns = new Set([
        'SUM','AVERAGE','COUNT','COUNTIF','weightedAverage','IF','IFERROR','concat','CONCATENATE',
        'upper','lower','trim','substr','replace','contains','startsWith','endsWith','length',
        'year','month','day','dayOfWeek','abs','round','ceil','floor','sqrt','log','exp','pow',
        'min','max','VARP','VARS','STDEVP','STDEVS','median','percentile','first','last','range'
      ]);

      const ids = normalized.match(/[A-Za-z_][A-Za-z0-9_]*/g) || [];
      for (const id of ids) {
        if (id === '1') continue;
        if (!allowedFns.has(id)) {
          return `Expression contains unknown function or unsupported token: ${id}`;
        }
      }

      if (!this.formulaService.hasBalancedParentheses(normalized)) {
        return 'Formula syntax is invalid. Check the parentheses in the expression.';
      }
      // eslint-disable-next-line no-new-func
      new Function(`return (${normalized});`);
    } catch {
      return 'Formula syntax is invalid.';
    }

    return '';
  }

  private validateEntityFilter(
    attributeName: string,
    operator: string,
    attributeValue: string[],
  ): string {
    if (!attributeName) {
      return 'Filter column is required.';
    }
    const availableColumns = new Set((this.dataset?.columns || []).map((column) => column.name));
    if (availableColumns.size > 0 && !availableColumns.has(attributeName)) {
      return `Unknown filter column "${attributeName}".`;
    }
    if (!operator) {
      return 'Filter operator is required.';
    }
    if (!this.entityFilterOperators.includes(operator)) {
      return `Unsupported filter operator "${operator}".`;
    }
    if ((operator === 'IS NULL' || operator === 'IS NOT NULL') && attributeValue.length === 0) {
      return '';
    }
    if (attributeValue.length === 0) {
      return 'Filter value is required.';
    }
    if (operator === 'BETWEEN' && attributeValue.length !== 2) {
      return 'BETWEEN requires exactly two comma-separated values.';
    }
    return '';
  }

  private buildEntityAttributesList(): string[] {
    return this.entityFormulas.map((formula) => `${formula.expression} as ${formula.name}`);
  }

  private buildEntityFilterPayload(): EntityFilterDefinition[] {
    if (this.entityFilters.length > 0) {
      return this.entityFilters.map((filter, index) => ({
        ...filter,
        logicalOperator: index === this.entityFilters.length - 1 ? '' : (filter.logicalOperator || 'AND'),
      }));
    }
    return [{
      operator: 'IS NOT',
      attributeName: 'uuid',
      attributeValue: null,
      logicalOperator: '',
    }];
  }

  private cloneDataset(dataset: UploadedDataset): UploadedDataset {
    return {
      fileName: dataset.fileName,
      sourceType: dataset.sourceType,
      columns: dataset.columns.map((column) => ({ ...column })),
      rows: dataset.rows.map((row) => ({ ...row })),
    };
  }

  private loadDatasetFormulas(fileName: string): DatasetFormulas {
    try {
      const raw = window.localStorage.getItem(DataBuilderComponent.DATASET_FORMULAS_STORAGE_KEY);
      const all = raw ? JSON.parse(raw) : {};
      const entry = all?.[fileName];
      return {
        computedColumns: Array.isArray(entry?.computedColumns) ? entry.computedColumns : [],
        rowFilter: typeof entry?.rowFilter === 'string' ? entry.rowFilter : '',
        customAggregations: Array.isArray(entry?.customAggregations) ? entry.customAggregations : [],
      };
    } catch {
      return { computedColumns: [], rowFilter: '', customAggregations: [] };
    }
  }

  private persistDatasetFormulas(): void {
    if (!this.currentFileName) return;
    try {
      const raw = window.localStorage.getItem(DataBuilderComponent.DATASET_FORMULAS_STORAGE_KEY);
      const all = raw ? JSON.parse(raw) : {};
      all[this.currentFileName] = {
        computedColumns: this.computedColumns,
        rowFilter: this.rowFilter,
        customAggregations: this.customAggregations,
      };
      window.localStorage.setItem(
        DataBuilderComponent.DATASET_FORMULAS_STORAGE_KEY,
        JSON.stringify(all),
      );
    } catch {
      this.errorMessage = 'Unable to save formulas locally.';
    }
  }

  private applyDatasetFormulas(): void {
    this.newColumnError = '';
    this.rowFilterError = '';

    if (!this.rawDataset) {
      this.state.setDataset(undefined);
      return;
    }

    const baseColumns = this.rawDataset.columns.map((column) => ({ ...column }));
    let workingRows: Record<string, string>[] = this.rawDataset.rows.map((row) => ({ ...row }));
    const allColumns = [...baseColumns];

    for (const computed of this.computedColumns) {
      try {
        const evaluator = this.formulaService.buildRowEvaluator(computed.expression, allColumns.map((c) => c.name));
        let inferredType: ColumnType = 'number';
        workingRows = workingRows.map((row) => {
          const value = evaluator(row);
          if (typeof value === 'number' && !Number.isFinite(value)) {
            return { ...row, [computed.name]: '' };
          }
          if (typeof value !== 'number') {
            inferredType = 'string';
          }
          return { ...row, [computed.name]: value === null || value === undefined ? '' : String(value) };
        });
        allColumns.push({ name: computed.name, type: inferredType });
      } catch (error) {
        this.newColumnError = `Failed to evaluate "${computed.name}": ${error instanceof Error ? error.message : 'unknown error'}`;
      }
    }

    if (this.rowFilter.trim()) {
      try {
        const filterFn = this.formulaService.buildRowEvaluator(this.rowFilter, allColumns.map((c) => c.name));
        workingRows = workingRows.filter((row) => !!filterFn(row));
      } catch (error) {
        this.rowFilterError = `Row filter error: ${error instanceof Error ? error.message : 'unknown error'}`;
      }
    }

    this.state.setDataset({
      fileName: this.rawDataset.fileName,
      sourceType: this.rawDataset.sourceType,
      columns: allColumns,
      rows: workingRows,
    });
  }

  private createId(): string {
    return `chart-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
}
